import React, { ReactElement, useState, useEffect } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { Filter, ViGenericPartName, ViSide } from "../models";
import { groupBy, isMatchingDamage } from "../services/Utility";
import { ViClaimDamagedPart } from "../models/";

const useStyles = makeStyles((theme: Theme) => ({
  svg: { isolation: "isolate", minWidth: `${theme.goldenRatio(5)}rem` },
  part: {
    "& .highlight": {
      strokeWidth: 0,
      fillOpacity: 0,
    },
  },
  damagedPart: {
    cursor: "pointer",
    "& .highlight": {
      transition: "all 0.1s ease",
      fill: "red",
      fillOpacity: theme.goldenRatio(-1),
      stroke: "red",
    },
  },
  fadedDamagedPart: {
    cursor: "pointer",
    "& .highlight": {
      fill: "red",
      stroke: "red",
      fillOpacity: theme.goldenRatio(-2),
      strokeOpacity: theme.goldenRatio(-2),
    },
  },
}));

function CarMap({
  claimDamagedParts,
  setHoverFilter,
  hoverFilter,
}: {
  claimDamagedParts: ViClaimDamagedPart[];
  setHoverFilter: (filters: Filter[]) => void;
  hoverFilter: Filter[];
}): ReactElement {
  const classes = useStyles();
  const [damagedParts, setDamagedParts] = useState<{
    [key: string]: { [key: string]: any };
  }>({});

  function handleHover(filter: Filter = {}) {
    if ("genericPartName" in filter || hoverFilter.length > 0) {
      setHoverFilter(
        filter.genericPartName ? [{ ...filter, side: filter.side || ViSide.NONE }] : []
      );
    }
  }

  function isFaded(damages: Filter[] | Record<string, Filter[]>) {
    const damagesArr: Filter[] = Array.isArray(damages)
      ? damages
      : Object.values(damages)[0] || [];
    return (
      !hoverFilter.length ||
      !hoverFilter.some((filter) =>
        damagesArr.some((damage) => isMatchingDamage(filter, damage))
      )
    );
  }

  useEffect(() => {
    if (!claimDamagedParts?.length) return;
    setDamagedParts(
      Object.fromEntries(
        Object.entries(
          groupBy(
            claimDamagedParts
              .filter(({ objectDetectionLabels }) => Array.isArray(objectDetectionLabels))
              .flatMap(
                ({ objectDetectionLabels, sideResult }) =>
                  (objectDetectionLabels as string[]).map((label) => ({
                    genericPartName: label,
                    ...(sideResult ? { side: sideResult.side } : {}),
                  })) /* ({
                ...cDP,
                // physicalParts: cDP.physicalParts.map((pP) => ({
                //   ...pP,
                //   name: pP.objectDetectionLabels,
                //   sideResult: cDP.sideResult,
                // })),
                name: cDP.objectDetectionLabels,
              }) */
              ),
            ["genericPartName"]
          )
        ).map(([key, arr]: any /* [string, any[]] */) => [
          key,
          groupBy(arr, ["side"], "N/A"),
        ])
      )
    );
  }, [claimDamagedParts]);

  return (
    <svg className={classes.svg} viewBox="-5 -5 390 270">
      <g
        id="front_bumper"
        className={
          damagedParts[ViGenericPartName.FRONT_BUMPER]
            ? isFaded(damagedParts[ViGenericPartName.FRONT_BUMPER])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (damagedParts && damagedParts[ViGenericPartName.FRONT_BUMPER])
            handleHover({ genericPartName: ViGenericPartName.FRONT_BUMPER });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <linearGradient
          id="_lgradient_328"
          x1="-3.581742813452138e-7"
          x2="0.9999997608607669"
          y1="0.5000026402995099"
          y2="0.5000026402995099"
          gradientTransform="matrix(0,-98.763,23.73,0,0,181.099)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
          <stop offset="50%" stopOpacity="1" style={{ stopColor: "rgb(210,211,215)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(191,192,197)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_328)"
          d=" M 2.6 86.5 C 1.5 88.9 1.1 95.6 0.8 98 C 0.5 100.4 0 130.3 0 130.3 L 0 131.8 C 0 131.8 0.6 162.5 0.8 164.9 C 1 167.3 1 175.9 2.2 177.7 C 3.1 180.8 19.2 181.9 23.5 180.5 C 23.9 180.4 18.1 179.4 18.1 177.8 C 18.4 169.8 15.6 145.9 15.5 130 C 15.5 115.5 18.2 95.2 18.3 86.8 C 18.3 84.5 24.2 83 23.7 82.9 C 19.6 81.3 4.1 83.3 2.6 86.5 Z  M 13.8 168.5 C 15.9 173.9 15.5 177.5 15.1 179 C 14.1 179.5 8.9 180.4 5.9 176.2 C 4.1 173.7 3.8 166.4 4.7 163.9 C 5.3 162 5.9 161.5 6.4 161.5 C 9.8 162.6 12.5 165.2 13.8 168.5 Z  M 7.9 109.6 L 7.3 131.7 L 7.9 153.8 C 7.4 155.6 3.2 157.4 3.2 157.4 C 3.2 157.4 2.2 135.7 2.3 131.7 C 2.1 127.7 3.2 106 3.2 106 C 3.2 106 7.4 107.8 7.9 109.6 L 7.9 109.6 Z  M 15.1 84.5 C 15.6 85.8 15.9 89.6 13.8 95 C 12.5 98.3 9.8 100.9 6.4 102 C 5.9 102 5.3 101.5 4.7 99.6 C 3.8 97 4.1 89.8 5.9 87.3 C 8.9 83.1 14.2 84 15.1 84.5 Z "
        />
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_hhKRGuNaj7pqJUr2o2EGlPAtnXUgcX4h">
                  <path
                    fill="rgb(255,255,255)"
                    fillRule="evenodd"
                    d=" M 2.6 86.5 C 1.5 88.9 1.1 95.6 0.8 98 C 0.5 100.4 0 130.2 0 130.2 L 0 131.7 C 0 131.7 0.6 162.4 0.8 164.8 C 1 167.2 1 175.8 2.2 177.6 C 3.1 180.7 19.2 181.8 23.5 180.4 C 23.9 180.3 18.1 179.2 18.1 177.3 C 18.3 168.9 15.5 145.6 15.5 129.9 C 15.5 115.4 18.2 95.2 18.3 86.8 C 18.3 84.4 24.2 83 23.7 82.8 C 19.6 81.3 4.1 83.3 2.6 86.5 Z  M 13.8 168.5 C 15.9 173.9 15.5 177.5 15.1 179 C 14.1 179.5 8.9 180.4 5.9 176.2 C 4.1 173.7 3.8 166.4 4.7 163.9 C 5.3 162 5.9 161.5 6.4 161.5 C 9.8 162.6 12.5 165.2 13.8 168.5 Z  M 15.1 84.5 C 15.6 85.8 15.9 89.6 13.8 95 C 12.5 98.3 9.8 100.9 6.4 102 C 5.9 102 5.3 101.5 4.7 99.6 C 3.8 97 4.1 89.8 5.9 87.3 C 8.9 83.1 14.2 84 15.1 84.5 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_hhKRGuNaj7pqJUr2o2EGlPAtnXUgcX4h)">
                  <g>
                    <g>
                      <linearGradient
                        id="_lgradient_329"
                        x1="0.8019791666666478"
                        x2="0.0073027714942577315"
                        y1="0.5119932432432452"
                        y2="0.41470493537831427"
                        gradientTransform="matrix(0,-4.8,14.8,0,3.2,107.7)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(220,227,233)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(191,192,197)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_329)"
                        d=" M 3.2 102.9 C 3.6 103.9 4 104.9 4.2 106 C 5.2 106.5 17.1 107 18 107.7 C 17.5 107.1 16.9 106.6 16.3 106.2 C 15.4 105.7 14.4 105.3 13.4 105 C 10.1 104 6.7 103.3 3.2 102.9 Z "
                      />
                      <linearGradient
                        id="_lgradient_330"
                        x1="0.158083333333348"
                        x2="0.9506860728911298"
                        y1="0.5136081081081081"
                        y2="0.4163435421571574"
                        gradientTransform="matrix(0,-4.8,14.8,0,3.2,160.6)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(220,227,233)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(191,192,197)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_330)"
                        d=" M 3.2 160.6 C 3.6 159.6 4 158.6 4.2 157.5 C 5.2 157 17.1 156.5 18 155.8 C 17.5 156.4 16.9 156.9 16.3 157.3 C 15.4 157.8 14.4 158.2 13.4 158.5 C 10.1 159.7 6.7 160.4 3.2 160.6 Z "
                      />
                      <linearGradient
                        id="_lgradient_331"
                        x1="0.998029126213587"
                        x2="-0.0008038834951396012"
                        y1="0.48583155392987326"
                        y2="0.4908800976191934"
                        gradientTransform="matrix(0,-51.5,13.911,0,4.2,157.5)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(191,192,197)" }}
                        />
                        <stop
                          offset="50%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(210,211,215)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(191,192,197)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_331)"
                        d=" M 4.2 157.5 C 4.2 157.5 9.8 156.9 13.9 156.4 C 14.7 156.3 15.4 156.1 16.1 155.8 C 15.7 153.2 18.3 143.1 18.1 131.6 C 18.1 119.9 16 108.9 16.1 107.7 C 15.4 107.4 14.7 107.2 14 107.1 C 9.9 106.7 4.3 106 4.3 106 C 4.3 106 7.8 108.4 8 109.6 L 7.4 131.2 L 7.4 132 L 8 153.9 C 7.5 155.5 4.2 157.5 4.2 157.5 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 2.6 86.5 C 1.5 88.9 1.1 95.6 0.8 98 C 0.5 100.4 0 130.3 0 130.3 L 0 131.8 C 0 131.8 0.6 162.5 0.8 164.9 C 1 167.3 1 175.9 2.2 177.7 C 3.1 180.8 19.2 181.9 23.5 180.5 C 23.9 180.4 18.1 179.4 18.1 177.8 C 18.4 169.8 15.6 145.9 15.5 130 C 15.5 115.5 18.2 95.2 18.3 86.8 C 18.3 84.5 24.2 83 23.7 82.9 C 19.6 81.3 4.1 83.3 2.6 86.5 Z"
        />
      </g>
      <g
        id="rear_bumper"
        className={
          damagedParts && damagedParts[ViGenericPartName.REAR_BUMPER]
            ? isFaded(damagedParts[ViGenericPartName.REAR_BUMPER])
              ? classes.fadedDamagedPart
              : isFaded(damagedParts[ViGenericPartName.REAR_BUMPER])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (damagedParts && damagedParts[ViGenericPartName.REAR_BUMPER])
            handleHover({ genericPartName: ViGenericPartName.REAR_BUMPER });
        }}
        onMouseLeave={() => handleHover()}
      >
        <linearGradient
          id="_lgradient_332"
          x1="0"
          x2="0.9999999999999998"
          y1="0.5000000000000036"
          y2="0.5000000000000036"
          gradientTransform="matrix(0,-94.3,20.7,0,360.6,178.9)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
          <stop offset="50%" stopOpacity="1" style={{ stopColor: "rgb(210,211,215)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(191,192,197)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_332)"
          d=" M 369.2 130.6 L 369.2 171.8 C 369.2 174.7 363 176.7 360.6 177.8 L 360.6 178.9 C 372.1 177.9 381.3 179.3 381.3 166.9 L 381.3 96.6 C 381.3 83.8 372 85.6 360.6 84.6 L 360.6 85.4 C 363.2 86.6 369.2 88.7 369.2 91 L 369.2 130.6 Z "
        />
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_dsC7qiLvhMNEQfwWNfCQ74eWBjQL6nT3">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 369.2 130.6 L 369.2 178.2 C 380.7 177.2 381.3 173.3 381.3 166.8 L 381.3 96.5 C 381.3 88.2 380.7 86 369.2 85 L 369.2 130.6 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_dsC7qiLvhMNEQfwWNfCQ74eWBjQL6nT3)">
                  <g>
                    <linearGradient
                      id="_lgradient_333"
                      x1="-0.002477508650518434"
                      x2="0.9985605536332183"
                      y1="0.49999999999999645"
                      y2="0.49999999999999645"
                      gradientTransform="matrix(0,-28.9,13.4,0,372.5,178.6)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(220,227,233)" }}
                      />
                      <stop
                        offset="50%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(210,211,215)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(191,192,197)" }}
                      />
                    </linearGradient>
                    <path
                      fill="url(#_lgradient_333)"
                      d=" M 372.5 177.8 L 372.5 153.1 C 372.5 152.5 372.7 151.9 373.1 151.5 C 373.5 151 374.1 150.8 374.7 150.8 C 378.5 150.8 382.2 150.4 385.9 149.7 L 385 178 L 377.7 178.6 L 372.5 177.8 Z "
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_yeaYg9iw6FfeZRe4Doh3GGNaXq3muuIz">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 369.2 130.6 L 369.2 178.2 C 380.7 177.2 381.3 173.3 381.3 166.8 L 381.3 96.5 C 381.3 88.2 380.7 86 369.2 85 L 369.2 130.6 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_yeaYg9iw6FfeZRe4Doh3GGNaXq3muuIz)">
                  <g>
                    <linearGradient
                      id="_lgradient_334"
                      x1="0"
                      x2="1.0013840830449823"
                      y1="0.49999999999999645"
                      y2="0.49999999999999645"
                      gradientTransform="matrix(0,-28.9,13.4,0,372.5,113.8)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(220,227,233)" }}
                      />
                      <stop
                        offset="50%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(210,211,215)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(191,192,197)" }}
                      />
                    </linearGradient>
                    <path
                      fill="url(#_lgradient_334)"
                      d=" M 372.5 85.4 L 372.5 110.4 C 372.5 111 372.7 111.6 373.1 112 C 373.5 112.5 374.1 112.7 374.7 112.7 C 378.5 112.7 382.2 113.1 385.9 113.8 L 385 85.5 L 377.7 84.9 L 372.5 85.4 Z "
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_HSiDeZVJFlor4dNjGPjli1iiZSUX88ZQ">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 369.2 130.6 L 369.2 178.2 C 380.7 177.2 381.3 173.3 381.3 166.8 L 381.3 96.5 C 381.3 88.2 380.7 86 369.2 85 L 369.2 130.6 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_HSiDeZVJFlor4dNjGPjli1iiZSUX88ZQ)">
                  <g>
                    <linearGradient
                      id="_lgradient_335"
                      x1="-1.0343715277777719"
                      x2="2.027430555555563"
                      y1="-19.270072992700747"
                      y2="-19.270072992700747"
                      gradientTransform="matrix(0,-28.8,13.7,0,364.5,146)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(220,227,233)" }}
                      />
                      <stop
                        offset="50%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(210,211,215)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(191,192,197)" }}
                      />
                    </linearGradient>
                    <path
                      fill="url(#_lgradient_335)"
                      d=" M 364.5 117.7 L 364.5 145.5 C 364.5 145.8 364.7 146 364.9 146 C 364.9 146 364.9 146 365 146 L 377.7 146 C 378 146 378.2 145.8 378.2 145.6 C 378.2 145.6 378.2 145.6 378.2 145.5 L 378.2 117.7 C 378.2 117.4 378 117.2 377.8 117.2 C 377.8 117.2 377.8 117.2 377.7 117.2 L 365 117.2 C 364.7 117.2 364.5 117.4 364.5 117.7 Z  M 375.9 122.3 C 376.1 122.3 376.3 122.4 376.3 122.6 L 376.3 140.5 C 376.3 140.7 376.1 140.8 375.9 140.8 L 367 141.2 C 366.8 141.2 366.6 141.1 366.6 140.9 L 366.6 122.3 C 366.6 122.1 366.8 122 367 122 L 375.9 122.3 Z "
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 369.2 130.6 L 369.2 171.8 C 369.2 174.7 363 176.7 360.6 177.8 L 360.6 178.9 C 372.1 177.9 381.3 179.3 381.3 166.9 L 381.3 96.6 C 381.3 83.8 372 85.6 360.6 84.6 L 360.6 85.4 C 363.2 86.6 369.2 88.7 369.2 91 L 369.2 130.6 Z "
        />
      </g>
      <g
        id="hood"
        className={
          damagedParts && damagedParts[ViGenericPartName.HOOD]
            ? isFaded(damagedParts[ViGenericPartName.HOOD])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (damagedParts && damagedParts[ViGenericPartName.HOOD])
            handleHover({ genericPartName: ViGenericPartName.HOOD });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <linearGradient
          id="_lgradient_336"
          x1="0.000001046179206598552"
          x2="1.0000000878649973"
          y1="0.4999999999999999"
          y2="0.4999999999999999"
          gradientTransform="matrix(0,-88.18,75,0,63,175.79)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
          <stop offset="50%" stopOpacity="1" style={{ stopColor: "rgb(210,211,215)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(191,192,197)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_336)"
          d=" M 63 131.7 C 63.2 123.7 64.3 115.7 66.3 107.9 C 67.1 105.2 66.1 101.4 81.1 93.8 C 100.2 84.4 138 88.5 138 88.5 C 138 88.5 123.3 89.6 123.7 131.7 C 123.3 173.8 138 174.9 138 174.9 C 138 174.9 100.2 179 81.1 169.6 C 66.6 162.1 67 158.2 66.3 155.5 C 64.3 147.7 63.2 139.8 63 131.7 Z "
        />
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_Rc5hAuZMXW2zFYRo1JNOc32ssRdcC3cb">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 63 131.7 C 63.2 123.7 64.3 115.7 66.3 107.9 C 67.1 105.2 66.2 101.3 81.1 93.8 C 100.2 84.4 138 88.5 138 88.5 C 138 88.5 123.3 89.6 123.7 131.7 C 123.3 173.8 138 174.9 138 174.9 C 138 174.9 100.2 179 81.1 169.6 C 66.8 162.6 67 158.2 66.3 155.5 C 64.3 147.7 63.2 139.8 63 131.7 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_Rc5hAuZMXW2zFYRo1JNOc32ssRdcC3cb)">
                  <g>
                    <g opacity="0.9">
                      <linearGradient
                        id="_lgradient_337"
                        x1="0.08330805687204457"
                        x2="0.5865801288687449"
                        y1="0.46055598455598445"
                        y2="0.6488401867427267"
                        gradientTransform="matrix(0,-21.1,77.7,0,60.3,174.9)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(218,218,220)" }}
                        />
                        <stop
                          offset="50%"
                          stopOpacity="0.6"
                          style={{ stopColor: "rgb(255,255,255)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(218,218,220)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_337)"
                        d=" M 63.8 153.8 C 63.8 153.8 117.9 173.2 138 174.9 C 129.2 174.8 89.4 176.2 60.3 155 L 63.8 153.8 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_E5bqGO6dqC3dWXHR3MAxx8rzU1mIsodD">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 63 131.7 C 63.2 123.7 64.3 115.7 66.3 107.9 C 67.1 105.2 66.2 101.3 81.1 93.8 C 100.2 84.4 138 88.5 138 88.5 C 138 88.5 123.3 89.6 123.7 131.7 C 123.3 173.8 138 174.9 138 174.9 C 138 174.9 100.2 179 81.1 169.6 C 66.8 162.6 67 158.2 66.3 155.5 C 64.3 147.7 63.2 139.8 63 131.7 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_E5bqGO6dqC3dWXHR3MAxx8rzU1mIsodD)">
                  <g>
                    <g opacity="0.9">
                      <linearGradient
                        id="_lgradient_338"
                        x1="0.7879558359621548"
                        x2="0.4751848709991236"
                        y1="0.4528374655647376"
                        y2="0.5701617447799754"
                        gradientTransform="matrix(0,-31.7,72.6,0,63.3,120.9)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(218,218,220)" }}
                        />
                        <stop
                          offset="50%"
                          stopOpacity="0.6"
                          style={{ stopColor: "rgb(255,255,255)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(218,218,220)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_338)"
                        d=" M 63.3 120.9 C 63.3 120.9 88.1 107.6 135.9 89.2 C 99.8 95.8 83 101.3 65.5 108.8 C 63.7 118.1 63.3 120.9 63.3 120.9 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_yEBCamDBiFtIRVd5rcVod53t7ZJLI2a7">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 63 131.7 C 63.2 123.7 64.3 115.7 66.3 107.9 C 67.1 105.2 66.2 101.3 81.1 93.8 C 100.2 84.4 138 88.5 138 88.5 C 138 88.5 123.3 89.6 123.7 131.7 C 123.3 173.8 138 174.9 138 174.9 C 138 174.9 100.2 179 81.1 169.6 C 66.8 162.6 67 158.2 66.3 155.5 C 64.3 147.7 63.2 139.8 63 131.7 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_yEBCamDBiFtIRVd5rcVod53t7ZJLI2a7)">
                  <g>
                    <g opacity="0.6">
                      <linearGradient
                        id="_lgradient_339"
                        x1="1.0329028036455608"
                        x2="0.5592481286637084"
                        y1="0.41800404312668304"
                        y2="0.5953709828755631"
                        gradientTransform="matrix(0,-27.832,74.2,0,62.5,111.6)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(218,218,220)" }}
                        />
                        <stop
                          offset="50%"
                          stopOpacity="0.7"
                          style={{ stopColor: "rgb(255,255,255)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(218,218,220)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_339)"
                        d=" M 62.5 111.6 C 62.5 111.6 88.9 105 136.7 86.6 C 122.9 82.1 101.4 80.8 64.7 99.5 C 62.9 108.7 62.5 111.6 62.5 111.6 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 63 131.7 C 63.2 123.7 64.3 115.7 66.3 107.9 C 67.1 105.2 66.1 101.4 81.1 93.8 C 100.2 84.4 138 88.5 138 88.5 C 138 88.5 123.3 89.6 123.7 131.7 C 123.3 173.8 138 174.9 138 174.9 C 138 174.9 100.2 179 81.1 169.6 C 66.6 162.1 67 158.2 66.3 155.5 C 64.3 147.7 63.2 139.8 63 131.7 Z "
        />
      </g>
      <g
        id="leftside_mirror"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.SIDE_MIRROR] &&
          damagedParts[ViGenericPartName.SIDE_MIRROR][ViSide.LEFT]
            ? isFaded(damagedParts[ViGenericPartName.SIDE_MIRROR][ViSide.LEFT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.SIDE_MIRROR] &&
            damagedParts[ViGenericPartName.SIDE_MIRROR][ViSide.LEFT]
          )
            handleHover({
              genericPartName: ViGenericPartName.SIDE_MIRROR,
              side: ViSide.LEFT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <linearGradient
          id="_lgradient_340"
          x1="0.993365591397847"
          x2="-0.00015053763441841284"
          y1="0.4999999999999991"
          y2="0.499999999999999"
          gradientTransform="matrix(4.65,-8.054,16.998,9.814,97.907,189.555)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_340)"
          d=" M 101.13 184.373 C 102.276 186.189 106.02 186.503 105.615 194.005 C 107.574 194.213 109.609 193.887 111.289 192.778 C 111.289 192.778 110.39 191.335 110.853 190.332 C 111.567 188.897 117.542 192.347 119.281 191.734 C 119.468 191.611 119.445 191.251 119.445 191.251 C 119.445 191.251 102.346 184.266 101.107 184.013 C 101.13 184.373 101.094 184.236 101.13 184.373 Z "
        />
        <linearGradient
          id="_lgradient_341"
          x1="0.997019442179444"
          x2="-0.005294492407149853"
          y1="0.5000022124779671"
          y2="0.500002212477967"
          gradientTransform="matrix(7.029,-12.174,11.932,6.889,100.222,202.39)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(151,151,154)" }} />
          <stop offset="50%" stopOpacity="1" style={{ stopColor: "rgb(182,182,185)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(155,155,158)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_341)"
          d=" M 111.339 192.691 C 112.082 193.005 112.688 193.355 113.381 193.755 C 114.599 195.844 113.56 206.444 112.383 208.883 C 107.746 208.515 105.123 201.458 105.665 193.919 C 105.668 193.113 105.945 192.233 106.518 191.64 C 107.948 190.964 111.339 192.691 111.339 192.691 Z "
        />
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_RswkGPJ3XmCd6bDQdXQh8appQMxOarfn">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 111.339 192.691 C 112.082 193.005 112.688 193.355 113.381 193.755 C 114.599 195.844 113.56 206.444 112.383 208.883 C 107.746 208.515 105.123 201.458 105.665 193.919 C 105.668 193.113 105.945 192.233 106.518 191.64 C 107.948 190.964 111.339 192.691 111.339 192.691 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_RswkGPJ3XmCd6bDQdXQh8appQMxOarfn)">
                  <g>
                    <linearGradient
                      id="_lgradient_342"
                      x1="0.994999048716747"
                      x2="0.0005840338440314952"
                      y1="0.4999985973409746"
                      y2="0.4999985973409745"
                      gradientTransform="matrix(6.833,-11.835,13.785,7.959,98.256,203.867)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="0"
                        style={{ stopColor: "rgb(218,218,220)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(255,255,255)" }}
                      />
                    </linearGradient>
                    <path
                      fill="url(#_lgradient_342)"
                      d=" M 112.886 210.212 C 112.886 210.212 111.142 203.432 110.963 201.943 C 110.843 200.95 110.45 200.03 109.871 199.234 C 109.474 199.12 108.559 195.705 108.24 195.059 C 107.92 194.413 106.425 193.203 106.315 192.793 C 106.205 192.383 104.393 192.722 104.102 193.825 C 103.812 194.928 103.025 199.092 103.683 201.551 C 104.342 204.009 105.244 207.648 107.482 209.171 C 109.721 210.694 113.105 211.032 112.886 210.212 Z "
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_ubNTslHEfPOHXM7a4wmv6NBhro7BLpIA">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 118.354 196.741 C 118.487 197.511 118.483 198.317 118.393 199.073 C 117.193 201.152 107.437 205.45 104.695 205.599 C 102.754 201.36 107.667 195.652 114.49 192.432 C 115 192.149 116.28 191.733 116.886 192.083 C 118.222 192.97 118.354 196.741 118.354 196.741 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_ubNTslHEfPOHXM7a4wmv6NBhro7BLpIA)">
                  <g>
                    <g opacity="0.8">
                      <path
                        fill="rgb(255,255,255)"
                        d=" M 111.361 207.804 C 111.251 207.803 111.2 204.621 111.248 200.702 C 111.296 196.784 111.424 193.603 111.535 193.605 C 111.645 193.606 111.696 196.789 111.648 200.707 C 111.6 204.625 111.472 207.806 111.361 207.804 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 111.339 192.691 C 112.082 193.005 112.688 193.355 113.381 193.755 C 114.599 195.844 113.56 206.444 112.383 208.883 C 107.746 208.515 105.123 201.458 105.665 193.919 C 105.668 193.113 105.945 192.233 106.518 191.64 C 107.948 190.964 111.339 192.691 111.339 192.691 Z "
        />
      </g>
      <g
        id="rightside_mirror"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.SIDE_MIRROR] &&
          damagedParts[ViGenericPartName.SIDE_MIRROR][ViSide.RIGHT]
            ? isFaded(damagedParts[ViGenericPartName.SIDE_MIRROR][ViSide.RIGHT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.SIDE_MIRROR] &&
            damagedParts[ViGenericPartName.SIDE_MIRROR][ViSide.RIGHT]
          )
            handleHover({
              genericPartName: ViGenericPartName.SIDE_MIRROR,
              side: ViSide.RIGHT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <linearGradient
          id="_lgradient_343"
          x1="-0.005548387096773411"
          x2="0.9879999999999995"
          y1="0.4999999999999991"
          y2="0.4999999999999991"
          gradientTransform="matrix(-4.65,-8.054,16.998,-9.814,103.253,84.696)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_343)"
          d=" M 101.826 81.824 C 102.972 80.008 106.716 79.694 106.311 72.192 C 108.27 71.984 110.305 72.31 111.985 73.419 C 111.985 73.419 111.086 74.862 111.549 75.864 C 112.262 77.3 118.238 73.85 119.977 74.463 C 120.164 74.586 120.141 74.946 120.141 74.946 C 120.141 74.946 103.042 81.931 101.803 82.184 C 101.826 81.824 101.79 81.961 101.826 81.824 Z "
        />
        <linearGradient
          id="_lgradient_344"
          x1="-0.002959217311117257"
          x2="0.9993333767659696"
          y1="0.5000022124779671"
          y2="0.5000022124779671"
          gradientTransform="matrix(-7.029,-12.174,11.932,-6.889,107.946,75.981)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(151,151,154)" }} />
          <stop offset="50%" stopOpacity="1" style={{ stopColor: "rgb(182,182,185)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(155,155,158)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_344)"
          d=" M 112.035 73.506 C 112.777 73.192 113.384 72.842 114.076 72.442 C 115.295 70.353 114.256 59.753 113.079 57.314 C 108.442 57.682 105.819 64.739 106.361 72.278 C 106.364 73.084 106.641 73.964 107.214 74.557 C 108.644 75.232 112.035 73.506 112.035 73.506 Z "
        />
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_09DEWOhQmtCCPtA6qcPuLIzFGKURTnsW">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 112.035 73.506 C 112.777 73.192 113.384 72.842 114.076 72.442 C 115.295 70.353 114.256 59.753 113.079 57.314 C 108.442 57.682 105.819 64.739 106.361 72.278 C 106.364 73.084 106.641 73.964 107.214 74.557 C 108.644 75.232 112.035 73.506 112.035 73.506 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_09DEWOhQmtCCPtA6qcPuLIzFGKURTnsW)">
                  <g>
                    <linearGradient
                      id="_lgradient_345"
                      x1="-0.00304814816165333"
                      x2="0.9913815014373455"
                      y1="0.4999985973409746"
                      y2="0.4999985973409746"
                      gradientTransform="matrix(-6.833,-11.835,13.785,-7.959,105.785,74.165)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="0"
                        style={{ stopColor: "rgb(218,218,220)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(255,255,255)" }}
                      />
                    </linearGradient>
                    <path
                      fill="url(#_lgradient_345)"
                      d=" M 113.581 55.985 C 113.581 55.985 111.838 62.765 111.659 64.254 C 111.539 65.247 111.146 66.167 110.567 66.963 C 110.17 67.076 109.255 70.492 108.936 71.138 C 108.616 71.784 107.12 72.994 107.011 73.404 C 106.901 73.814 105.088 73.475 104.798 72.372 C 104.508 71.269 103.72 67.105 104.379 64.646 C 105.038 62.188 105.94 58.549 108.178 57.026 C 110.416 55.503 113.801 55.165 113.581 55.985 Z "
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_8qBxw6lCrucguTe94MM7Hr1wD7cZ1UNH">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 105.085 61.468 C 104.952 62.238 104.955 63.044 105.045 63.8 C 106.245 65.878 116.001 70.176 118.743 70.325 C 120.684 66.087 115.772 60.378 108.948 57.159 C 108.438 56.876 107.159 56.46 106.552 56.81 C 105.303 57.647 105.085 61.468 105.085 61.468 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_8qBxw6lCrucguTe94MM7Hr1wD7cZ1UNH)">
                  <g>
                    <g opacity="0.8">
                      <path
                        fill="rgb(255,255,255)"
                        d=" M 112.419 65.391 C 112.467 69.31 112.416 72.492 112.306 72.494 C 112.195 72.495 112.067 69.315 112.019 65.396 C 111.971 61.478 112.022 58.296 112.132 58.294 C 112.243 58.293 112.371 61.473 112.419 65.391 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 112.035 73.506 C 112.777 73.192 113.384 72.842 114.076 72.442 C 115.295 70.353 114.256 59.753 113.079 57.314 C 108.442 57.682 105.819 64.739 106.361 72.278 C 106.364 73.084 106.641 73.964 107.214 74.557 C 108.644 75.232 112.035 73.506 112.035 73.506 Z "
        />
      </g>
      <g
        id="front_windscreen"
        className={
          damagedParts && damagedParts[ViGenericPartName.WINDSCREEN]
            ? isFaded(damagedParts[ViGenericPartName.WINDSCREEN])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (damagedParts && damagedParts[ViGenericPartName.WINDSCREEN])
            handleHover({
              genericPartName: ViGenericPartName.WINDSCREEN,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <path
          fill="none"
          d=" M 184.6 166.3 C 184.6 166.3 173.5 132.3 185.2 96.2 C 185.2 96.2 168 92.9 164.7 92.3 C 161.4 91.7 151.9 89.7 147.9 90.6 C 145.1 94.6 137.9 114 138 129.5 C 138 143.4 137 153 148.3 173.1 C 155.6 172.5 162.8 171.3 169.8 169.5 C 181.9 166.5 184 166.4 184.6 166.3 Z "
        />
        <g opacity="0.7">
          <linearGradient
            id="_lgradient_346"
            x1="0.9999999356829437"
            x2="4.440892098500626e-16"
            y1="0.49999801920412024"
            y2="0.4999980192041201"
            gradientTransform="matrix(0,-82.729,47.223,0,137.977,173.1)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(10,10,10)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(64,64,64)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_346)"
            d=" M 184.6 166.3 C 184.6 166.3 173.5 132.3 185.2 96.2 C 185.2 96.2 168 92.9 164.7 92.3 C 161.4 91.7 151.9 89.7 147.9 90.6 C 145.1 94.6 137.9 114 138 129.5 C 138 143.4 137 153 148.3 173.1 C 155.6 172.5 162.8 171.3 169.8 169.5 C 181.9 166.5 184 166.4 184.6 166.3 Z "
          />
        </g>
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_r6kb1Hqug4buVuvgCwhS8O4DB5lqB54Y">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 184.6 166.3 C 184.6 166.3 173.5 132.3 185.2 96.2 C 185.2 96.2 168 92.9 164.7 92.3 C 161.4 91.7 151.9 89.7 147.9 90.6 C 145.1 94.6 137.9 114 138 129.5 C 138 143.4 137 153 148.3 173.1 C 155.6 172.5 162.8 171.3 169.8 169.5 C 181.9 166.5 184 166.4 184.6 166.3 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_r6kb1Hqug4buVuvgCwhS8O4DB5lqB54Y)">
                  <g>
                    <g opacity="0.5">
                      <linearGradient
                        id="_lgradient_347"
                        x1="1.0000029239765986"
                        x2="-0.0000029239766110578103"
                        y1="0.5000017262204479"
                        y2="0.5000017262204478"
                        gradientTransform="matrix(0,-34.2,51.027,0,136.693,173.7)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(226,226,218)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(255,255,255)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_347)"
                        d=" M 181.6 146.7 C 181.6 146.7 164.2 142.3 155.7 141.1 C 149.4 140.2 143.1 139.6 136.7 139.5 C 136.5 152 140.7 164.1 148.6 173.7 C 161 172.1 173.3 169.8 185.5 166.8 C 192.2 160.4 181.6 146.7 181.6 146.7 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>

        <path
          className={"highlight"}
          d=" M 184.6 166.3 C 184.6 166.3 173.5 132.3 185.2 96.2 C 185.2 96.2 168 92.9 164.7 92.3 C 161.4 91.7 151.9 89.7 147.9 90.6 C 145.1 94.6 137.9 114 138 129.5 C 138 143.4 137 153 148.3 173.1 C 155.6 172.5 162.8 171.3 169.8 169.5 C 181.9 166.5 184 166.4 184.6 166.3 Z "
        />
      </g>
      <g
        id="leftfront_door"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.FRONT_DOOR] &&
          damagedParts[ViGenericPartName.FRONT_DOOR][ViSide.LEFT]
            ? isFaded(damagedParts[ViGenericPartName.FRONT_DOOR][ViSide.LEFT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.FRONT_DOOR] &&
            damagedParts[ViGenericPartName.FRONT_DOOR][ViSide.LEFT]
          )
            handleHover({
              genericPartName: ViGenericPartName.FRONT_DOOR,
              side: ViSide.LEFT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g>
          <linearGradient
            id="_lgradient_348"
            x1="-0.001127855415202994"
            x2="0.9988754143151526"
            y1="0.5000012744910225"
            y2="0.5000012744910225"
            gradientTransform="matrix(0,-36.902,58.017,0,128.883,236.002)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
            <stop
              offset="50%"
              stopOpacity="1"
              style={{ stopColor: "rgb(210,211,215)" }}
            />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(191,192,197)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_348)"
            d=" M 130.2 204.4 C 127.6 212.7 129.5 224.6 130.4 232.2 C 130.7 234.4 131.1 236 133.2 235.9 C 156.6 236.3 184.6 235.4 184.6 235.4 C 184.6 235.4 183.8 224.5 186.1 204.3 C 186.3 202.6 186.6 200.8 186.9 199.1 C 187.1 199.1 141.4 202.2 130.2 204.4 Z "
          />
          <g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_0MtPMB6HQOSsju8waFs63HYELJixiK6A">
                    <path
                      fill="rgb(255,255,255)"
                      d=" M 130.2 204.4 C 127.6 212.7 129.5 224.6 130.4 232.2 C 130.7 234.4 131.1 236 133.2 235.9 C 156.6 236.3 184.6 235.4 184.6 235.4 C 184.6 235.4 183.8 224.5 186.1 204.3 C 186.3 202.6 186.6 200.8 186.9 199.1 C 187.1 199.1 141.4 202.2 130.2 204.4 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_0MtPMB6HQOSsju8waFs63HYELJixiK6A)">
                    <g>
                      <g opacity="0.2">
                        <linearGradient
                          id="_lgradient_349"
                          x1="-0.039654166666658774"
                          x2="1.0276227477625122"
                          y1="0.4967743806646552"
                          y2="0.5181199189532236"
                          gradientTransform="matrix(0,-12,66.2,0,128,242)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(86,86,89)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(98,98,100)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_349)"
                          d=" M 128 233.6 C 128 233.6 166.3 232.9 194.2 230 L 193 239.4 L 152.3 242 L 130 238 L 128 233.6 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_u9jaNRGylHS2p53Vn364MVUunz0j6yxQ">
                    <path
                      fill="rgb(255,255,255)"
                      d=" M 130.2 204.4 C 127.6 212.7 129.5 224.6 130.4 232.2 C 130.7 234.4 131.1 236 133.2 235.9 C 156.6 236.3 184.6 235.4 184.6 235.4 C 184.6 235.4 183.8 224.5 186.1 204.3 C 186.3 202.6 186.6 200.8 186.9 199.1 C 187.1 199.1 141.4 202.2 130.2 204.4 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_u9jaNRGylHS2p53Vn364MVUunz0j6yxQ)">
                    <g>
                      <g opacity="0.7">
                        <linearGradient
                          id="_lgradient_350"
                          x1="0.15386131386864488"
                          x2="0.8342325872315874"
                          y1="0.5062161057061467"
                          y2="0.46177391409378166"
                          gradientTransform="matrix(0,-13.7,66.302,0,127.808,218.1)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="0"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_350)"
                          d=" M 129.1 218.1 C 129.1 218.1 132.2 216.4 134.5 216.4 C 134.5 216.4 179.3 212.3 187.4 212.3 C 195.5 212.3 194 204.4 194 204.4 C 194 204.4 145 208.9 127.9 212.7 C 127.4 215.2 129.1 218.1 129.1 218.1 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_Z8jTmASKrbLj4bI8fVWyMRzDBH003JB6">
                    <path
                      fill="rgb(255,255,255)"
                      d=" M 130.2 204.4 C 127.6 212.7 129.5 224.6 130.4 232.2 C 130.7 234.4 131.1 236 133.2 235.9 C 156.6 236.3 184.6 235.4 184.6 235.4 C 184.6 235.4 183.8 224.5 186.1 204.3 C 186.3 202.6 186.6 200.8 186.9 199.1 C 187.1 199.1 141.4 202.2 130.2 204.4 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_Z8jTmASKrbLj4bI8fVWyMRzDBH003JB6)">
                    <g>
                      <g opacity="0.4">
                        <linearGradient
                          id="_lgradient_351"
                          x1="0.029386150184809168"
                          x2="0.7936110804669746"
                          y1="0.5029091121408591"
                          y2="0.45113235534667523"
                          gradientTransform="matrix(0,-4.817,48.018,0,146.497,232.213)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_351)"
                          d=" M 186.3 227.4 C 186.3 227.4 146.8 229.5 146.5 231 C 146.2 232.5 169.6 232.8 186.8 230.8 C 204 228.8 187.2 227.3 186.3 227.4 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g>
          <linearGradient
            id="_lgradient_354"
            x1="0.5000036983908256"
            x2="0.5000036983908254"
            y1="-0.001012290509297742"
            y2="0.9989875805003194"
            gradientTransform="matrix(0,-5.342,9.78,0,169.8,209.526)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(132,132,134)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(93,93,95)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_354)"
            d=" M 169.8 207 C 169.8 205.7 171.1 204 176 204.2 C 180.9 204.4 179.9 208 178.1 209 C 176.4 210 170 209.7 169.8 207 Z "
          />
          <linearGradient
            id="_lgradient_355"
            x1="0.5000034691064208"
            x2="0.5000034691064206"
            y1="-0.0015935568943650225"
            y2="1.0029092999316784"
            gradientTransform="matrix(0,-2.708,14.057,0,168.625,207.905)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(63,66,67)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(13,14,17)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_355)"
            d=" M 169 207.9 C 177.7 207 179.3 207.6 181.6 207.5 C 182.5 207.4 183.5 205.1 181.6 205.2 C 177.4 205.2 173.1 205.6 169 206.5 C 168.5 206.5 168.5 208 169 207.9 Z "
          />
          <linearGradient
            id="_lgradient_356"
            x1="0.003434782608543685"
            x2="0.992929837353684"
            y1="0.5048677997052593"
            y2="0.44415273033728975"
            gradientTransform="matrix(0,-2.3,13.82,0,168.761,207.4)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(240,240,242)" }} />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(174,174,176)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_356)"
            d=" M 169.2 207.4 C 173.3 207 177.4 206.8 181.5 207 C 182.4 206.9 183.4 205.1 181.5 205.1 C 177.3 205.1 173.1 205.4 169 206.2 C 168.7 206.3 168.6 207.4 169.2 207.4 Z "
          />
        </g>
        <path
          className={"highlight"}
          d=" M 130.2 204.4 C 127.6 212.7 129.5 224.6 130.4 232.2 C 130.7 234.4 131.1 236 133.2 235.9 C 156.6 236.3 184.6 235.4 184.6 235.4 C 184.6 235.4 183.8 224.5 186.1 204.3 C 186.3 202.6 186.6 200.8 186.9 199.1 C 187.1 199.1 141.4 202.2 130.2 204.4 Z "
        />
      </g>
      <g
        id="leftfront_door_window"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.FRONT_DOOR_WINDOW] &&
          damagedParts[ViGenericPartName.FRONT_DOOR_WINDOW][ViSide.LEFT]
            ? isFaded(damagedParts[ViGenericPartName.FRONT_DOOR_WINDOW][ViSide.LEFT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.FRONT_DOOR_WINDOW] &&
            damagedParts[ViGenericPartName.FRONT_DOOR_WINDOW][ViSide.LEFT]
          )
            handleHover({
              genericPartName: ViGenericPartName.FRONT_DOOR_WINDOW,
              side: ViSide.LEFT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g opacity="0.7">
          <linearGradient
            id="_lgradient_352"
            x1="0.04415289256197008"
            x2="0.9387768595041233"
            y1="0.500000000000004"
            y2="0.500000000000004"
            gradientTransform="matrix(0,-24.2,59.5,0,130.2,204.4)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(10,10,10)" }} />
            <stop
              offset="100%"
              stopOpacity="0.5"
              style={{ stopColor: "rgb(64,64,64)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_352)"
            d=" M 167.8 183.2 C 152.5 187 132.1 200.5 130.2 204.4 C 144.5 202.3 157.5 201.4 187.1 199.2 C 188.3 192.9 189.2 186.6 189.7 180.2 C 182.3 180.4 174.9 181.4 167.8 183.2 Z "
          />
        </g>
        <linearGradient
          id="_lgradient_353"
          x1="0.4999999999999889"
          x2="0.49999999999998873"
          y1="3.9968028886505635e-15"
          y2="1.000000000000004"
          gradientTransform="matrix(0,-24.2,59.5,0,130.2,204.4)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_353)"
          d=" M 167.8 183.2 C 152.5 187 132.1 200.5 130.2 204.4 C 144.5 202.3 157.5 201.4 187.1 199.2 C 188.3 192.9 189.2 186.6 189.7 180.2 C 182.3 180.4 174.9 181.4 167.8 183.2 Z  M 185.3 197.6 C 163.3 199 155.7 199.6 145 200.6 C 143.7 200.7 142.4 200.8 141 201 C 141.5 200.1 142.2 199.4 142.9 198.7 C 147.3 194.4 158.2 186.9 168.9 184.4 C 175.1 182.9 181.3 182 187.6 181.6 C 187.1 186.9 186.3 192.3 185.3 197.6 Z "
        />
        <path
          className={"highlight"}
          d=" M 167.8 183.2 C 152.5 187 132.1 200.5 130.2 204.4 C 144.5 202.3 157.5 201.4 187.1 199.2 C 188.3 192.9 189.2 186.6 189.7 180.2 C 182.3 180.4 174.9 181.4 167.8 183.2 Z "
        />
      </g>
      <g
        id="rightfront_door"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.FRONT_DOOR] &&
          damagedParts[ViGenericPartName.FRONT_DOOR][ViSide.RIGHT]
            ? isFaded(damagedParts[ViGenericPartName.FRONT_DOOR][ViSide.RIGHT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.FRONT_DOOR] &&
            damagedParts[ViGenericPartName.FRONT_DOOR][ViSide.RIGHT]
          )
            handleHover({
              genericPartName: ViGenericPartName.FRONT_DOOR,
              side: ViSide.RIGHT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g>
          <linearGradient
            id="_lgradient_357"
            x1="0.9988732643554687"
            x2="-0.001130005374900378"
            y1="0.5000012744910225"
            y2="0.5000012744910224"
            gradientTransform="matrix(0,-36.902,58.017,0,128.883,64.3)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
            <stop
              offset="50%"
              stopOpacity="1"
              style={{ stopColor: "rgb(210,211,215)" }}
            />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(191,192,197)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_357)"
            d=" M 130.2 59 C 127.6 50.7 129.5 38.8 130.4 31.2 C 130.7 29 131.1 27.4 133.2 27.5 C 156.6 27.1 184.6 28 184.6 28 C 184.6 28 183.8 38.9 186.1 59.1 C 186.3 60.8 186.6 62.6 186.9 64.3 C 187.1 64.2 141.4 61.1 130.2 59 Z "
          />
          <g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_sRKLHdfdUtxCuSOzBVv8A9GGdlui80PR">
                    <path
                      fill="rgb(255,255,255)"
                      d=" M 130.2 59 C 127.6 50.7 129.5 38.8 130.4 31.2 C 130.7 29 131.1 27.4 133.2 27.5 C 156.6 27.1 184.6 28 184.6 28 C 184.6 28 183.8 38.9 186.1 59.1 C 186.3 60.8 186.6 62.6 186.9 64.3 C 187.1 64.2 141.4 61.1 130.2 59 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_sRKLHdfdUtxCuSOzBVv8A9GGdlui80PR)">
                    <g>
                      <g opacity="0.2">
                        <linearGradient
                          id="_lgradient_358"
                          x1="1.0245080785791565"
                          x2="-0.043399882247946664"
                          y1="0.4967894729675222"
                          y2="0.5184130603979783"
                          gradientTransform="matrix(0,-12,66.2,0,128,33.4)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(86,86,89)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(98,98,100)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_358)"
                          d=" M 128 29.8 C 128 29.8 166.3 30.5 194.2 33.4 L 193 24 L 152.3 21.4 L 130 25.4 L 128 29.8 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_F0OWUKlf05GbzXomzCxgYwMOQfcd7J2l">
                    <path
                      fill="rgb(255,255,255)"
                      d=" M 130.2 59 C 127.6 50.7 129.5 38.8 130.4 31.2 C 130.7 29 131.1 27.4 133.2 27.5 C 156.6 27.1 184.6 28 184.6 28 C 184.6 28 183.8 38.9 186.1 59.1 C 186.3 60.8 186.6 62.6 186.9 64.3 C 187.1 64.2 141.4 61.1 130.2 59 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_F0OWUKlf05GbzXomzCxgYwMOQfcd7J2l)">
                    <g>
                      <g opacity="0.7">
                        <linearGradient
                          id="_lgradient_359"
                          x1="0.8385693430657"
                          x2="0.15733177522335307"
                          y1="0.5061904653045941"
                          y2="0.46098634724083576"
                          gradientTransform="matrix(0,-13.7,66.302,0,127.808,59)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="0"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_359)"
                          d=" M 129.1 45.3 C 129.1 45.3 132.2 47 134.5 47 C 134.5 47 179.3 51.1 187.4 51.1 C 195.5 51.1 194 59 194 59 C 194 59 145 54.5 127.9 50.7 C 127.4 48.1 129.1 45.3 129.1 45.3 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_xKu7N4N9NhBCEW0VJGpvcl0iC7svg4RN">
                    <path
                      fill="rgb(255,255,255)"
                      d=" M 130.2 59 C 127.6 50.7 129.5 38.8 130.4 31.2 C 130.7 29 131.1 27.4 133.2 27.5 C 156.6 27.1 184.6 28 184.6 28 C 184.6 28 183.8 38.9 186.1 59.1 C 186.3 60.8 186.6 62.6 186.9 64.3 C 187.1 64.2 141.4 61.1 130.2 59 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_xKu7N4N9NhBCEW0VJGpvcl0iC7svg4RN)">
                    <g>
                      <g opacity="0.4">
                        <linearGradient
                          id="_lgradient_360"
                          x1="0.947161029875609"
                          x2="0.18287023252078782"
                          y1="0.5026692053235027"
                          y2="0.45295788757361183"
                          gradientTransform="matrix(0,-4.818,47.929,0,146.497,36.005)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_360)"
                          d=" M 186.3 36 C 186.3 36 146.8 33.9 146.5 32.4 C 146.2 30.9 169.6 30.6 186.8 32.6 C 203.8 34.7 187.2 36.1 186.3 36 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g>
          <linearGradient
            id="_lgradient_361"
            x1="0.4999950029717226"
            x2="0.49999500297172245"
            y1="-0.003057321841243521"
            y2="0.9969323240117411"
            gradientTransform="matrix(0,-5.335,9.78,0,169.8,59.135)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(132,132,134)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(93,93,95)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_361)"
            d=" M 169.8 56.4 C 169.8 57.7 171.1 59.4 176 59.1 C 180.9 58.8 179.9 55.3 178.1 54.3 C 176.3 53.3 170 53.7 169.8 56.4 Z "
          />
          <linearGradient
            id="_lgradient_362"
            x1="0.49995960265214023"
            x2="0.49995960265214007"
            y1="0.000003461045956498765"
            y2="1.0017733211819309"
            gradientTransform="matrix(0,-2.708,14.095,0,168.587,58.203)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(63,66,67)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(13,14,17)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_362)"
            d=" M 169 55.5 C 177.7 56.4 179.3 55.8 181.6 55.9 C 182.5 56 183.5 58.3 181.6 58.2 C 176.8 58.1 171.8 57.7 169 56.9 C 168.5 56.9 168.4 55.4 169 55.5 Z "
          />
          <linearGradient
            id="_lgradient_363"
            x1="0.9432854247395"
            x2="-0.03516210233607531"
            y1="0.5046579612902065"
            y2="0.4400459417710341"
            gradientTransform="matrix(0,-2.315,13.82,0,168.761,58.315)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(240,240,242)" }} />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(174,174,176)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_363)"
            d=" M 169.2 56 C 173.3 56.4 177.4 56.6 181.5 56.4 C 182.4 56.5 183.4 58.3 181.5 58.3 C 177.3 58.4 173.1 58 169 57.2 C 168.7 57.1 168.6 56 169.2 56 Z "
          />
        </g>
        <path
          className={"highlight"}
          d=" M 130.2 59 C 127.6 50.7 129.5 38.8 130.4 31.2 C 130.7 29 131.1 27.4 133.2 27.5 C 156.6 27.1 184.6 28 184.6 28 C 184.6 28 183.8 38.9 186.1 59.1 C 186.3 60.8 186.6 62.6 186.9 64.3 C 187.1 64.2 141.4 61.1 130.2 59 Z "
        />
      </g>
      <g
        id="rightfront_door_window"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.FRONT_DOOR_WINDOW] &&
          damagedParts[ViGenericPartName.FRONT_DOOR_WINDOW][ViSide.RIGHT]
            ? isFaded(damagedParts[ViGenericPartName.FRONT_DOOR_WINDOW][ViSide.RIGHT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.FRONT_DOOR_WINDOW] &&
            damagedParts[ViGenericPartName.FRONT_DOOR_WINDOW][ViSide.RIGHT]
          )
            handleHover({
              genericPartName: ViGenericPartName.FRONT_DOOR_WINDOW,
              side: ViSide.RIGHT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g opacity="0.7">
          <linearGradient
            id="_lgradient_364"
            x1="0.9511652892562026"
            x2="0.056541322314057396"
            y1="0.5000000000000044"
            y2="0.5000000000000043"
            gradientTransform="matrix(0,-24.2,59.5,0,130.2,83.2)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(10,10,10)" }} />
            <stop
              offset="100%"
              stopOpacity="0.5"
              style={{ stopColor: "rgb(64,64,64)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_364)"
            d=" M 189.7 83.2 C 189.2 76.8 188.3 70.5 187.1 64.2 C 157.5 62 144.5 61.1 130.2 59 C 132.1 63 152.5 76.5 167.8 80.2 C 175 82 182.3 83 189.7 83.2 Z "
          />
        </g>
        <linearGradient
          id="_lgradient_365"
          x1="0.5"
          x2="0.49999999999999983"
          y1="4.440892098500626e-15"
          y2="1.0000000000000044"
          gradientTransform="matrix(0,-24.2,59.5,0,130.2,83.2)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_365)"
          d=" M 189.7 83.2 C 189.2 76.8 188.3 70.5 187.1 64.2 C 157.5 62 144.5 61.1 130.2 59 C 132.1 63 152.5 76.5 167.8 80.2 C 175 82 182.3 83 189.7 83.2 Z  M 187.5 81.8 C 181.2 81.4 174.9 80.4 168.8 79 C 158 76.5 147.2 69.1 142.8 64.7 C 142 64.1 141.4 63.3 140.9 62.4 C 142.3 62.5 143.6 62.7 144.9 62.8 C 155.7 63.7 163.3 64.3 185.2 65.7 C 186.3 71 187.1 76.4 187.5 81.8 Z "
        />
        <path
          className={"highlight"}
          d=" M 189.7 83.2 C 189.2 76.8 188.3 70.5 187.1 64.2 C 157.5 62 144.5 61.1 130.2 59 C 132.1 63 152.5 76.5 167.8 80.2 C 175 82 182.3 83 189.7 83.2 Z "
        />
      </g>
      <g
        id="leftrear_door"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.REAR_DOOR] &&
          damagedParts[ViGenericPartName.REAR_DOOR][ViSide.LEFT]
            ? isFaded(damagedParts[ViGenericPartName.REAR_DOOR][ViSide.LEFT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.REAR_DOOR] &&
            damagedParts[ViGenericPartName.REAR_DOOR][ViSide.LEFT]
          )
            handleHover({
              genericPartName: ViGenericPartName.REAR_DOOR,
              side: ViSide.LEFT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g>
          <linearGradient
            id="_lgradient_437"
            x1="1.0130352921767907"
            x2="-0.011588091572634074"
            y1="0.5288272625339063"
            y2="0.46291162848679257"
            gradientTransform="matrix(0,59.62,42.326,0,196.2,176.24)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
            <stop
              offset="50%"
              stopOpacity="1"
              style={{ stopColor: "rgb(210,211,215)" }}
            />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(191,192,197)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_437)"
            d=" M 237.792 197.557 C 237.792 197.557 238.392 207.257 238.492 218.257 C 238.592 223.857 238.492 229.557 237.992 235.157 C 235.392 236.457 196.792 235.557 196.792 235.557 C 196.792 235.557 195.692 204.757 196.492 198.457 C 204.592 197.857 237.792 197.557 237.792 197.557 Z "
          />
          <g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_XGtZL9FPVWQqChWQ18MUdR4aLoHa93Tp">
                    <path
                      fill="rgb(255,255,255)"
                      fillRule="evenodd"
                      d=" M 235.092 179.457 C 235.092 179.457 234.792 178.257 234.492 177.357 C 221.492 175.457 198.992 176.557 198.992 176.557 L 198.692 178.857 L 235.092 179.457 Z  M 237.792 197.557 C 237.792 197.557 238.392 207.257 238.492 218.257 C 238.592 223.857 238.492 229.557 237.992 235.157 C 235.392 236.457 196.792 235.557 196.792 235.557 C 196.792 235.557 195.692 204.757 196.492 198.457 C 204.592 197.857 237.792 197.557 237.792 197.557 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_XGtZL9FPVWQqChWQ18MUdR4aLoHa93Tp)">
                    <g>
                      <g opacity="0.4">
                        <linearGradient
                          id="_lgradient_438"
                          x1="0.7395604003228016"
                          x2="-0.034515061465158814"
                          y1="0.5015393343153765"
                          y2="0.4512720826537337"
                          gradientTransform="matrix(0,4.963,37.488,0,196.692,226.193)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_438)"
                          d=" M 196.692 227.757 C 196.692 227.757 236.292 224.857 234.092 226.957 C 232.692 228.157 196.692 231.157 196.692 231.157 L 196.692 227.757 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_PdCA8CsPuo52ANuy9pyfHu5jfNGjQpHB">
                    <path
                      fill="rgb(255,255,255)"
                      fillRule="evenodd"
                      d=" M 235.092 179.457 C 235.092 179.457 234.792 178.257 234.492 177.357 C 221.492 175.457 198.992 176.557 198.992 176.557 L 198.692 178.857 L 235.092 179.457 Z  M 237.792 197.557 C 237.792 197.557 238.392 207.257 238.492 218.257 C 238.592 223.857 238.492 229.557 237.992 235.157 C 235.392 236.457 196.792 235.557 196.792 235.557 C 196.792 235.557 195.692 204.757 196.492 198.457 C 204.592 197.857 237.792 197.557 237.792 197.557 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_PdCA8CsPuo52ANuy9pyfHu5jfNGjQpHB)">
                    <g>
                      <g opacity="0.7">
                        <linearGradient
                          id="_lgradient_439"
                          x1="0.6652682008489013"
                          x2="0.05170150979638455"
                          y1="0.5032603441047758"
                          y2="0.48842555950486055"
                          gradientTransform="matrix(0,16.848,49.021,0,193.223,201.457)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="0"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_439)"
                          d=" M 242.192 201.457 C 242.192 201.457 201.892 205.057 195.292 205.957 C 191.792 206.357 193.792 213.057 193.792 213.057 C 193.792 213.057 232.892 210.457 232.892 212.057 C 232.892 213.657 235.092 218.857 241.392 218.257 C 241.392 218.157 242.492 204.957 242.192 201.457 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_2R3hZttxKNJMbVNZ4rY5LPP0lJSOeUdj">
                    <path
                      fill="rgb(255,255,255)"
                      fillRule="evenodd"
                      d=" M 235.092 179.457 C 235.092 179.457 234.792 178.257 234.492 177.357 C 221.492 175.457 198.992 176.557 198.992 176.557 L 198.692 178.857 L 235.092 179.457 Z  M 237.792 197.557 C 237.792 197.557 238.392 207.257 238.492 218.257 C 238.592 223.857 238.492 229.557 237.992 235.157 C 235.392 236.457 196.792 235.557 196.792 235.557 C 196.792 235.557 195.692 204.757 196.492 198.457 C 204.592 197.857 237.792 197.557 237.792 197.557 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_2R3hZttxKNJMbVNZ4rY5LPP0lJSOeUdj)">
                    <g>
                      <g opacity="0.2">
                        <linearGradient
                          id="_lgradient_440"
                          x1="0.49999282217660124"
                          x2="0.49999282217660107"
                          y1="0"
                          y2="1.0000001090914021"
                          gradientTransform="matrix(0,16.125,49.886,0,193.292,226.632)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(86,86,89)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(98,98,100)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_440)"
                          d=" M 196.792 231.057 C 196.792 231.057 231.892 228.757 242.192 226.657 C 245.192 226.057 240.392 236.557 240.392 236.557 L 199.492 242.757 L 193.292 240.757 L 196.792 231.057 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g>
          <linearGradient
            id="_lgradient_448"
            x1="0.49998940446148055"
            x2="0.4999894044614804"
            y1="-0.004659574493089735"
            y2="0.9928054688149182"
            gradientTransform="matrix(0,-4.663,8.542,0,224.5,205.945)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(132,132,134)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(93,93,95)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_448)"
            d=" M 224.5 203.7 C 224.5 202.6 225.6 201.1 229.9 201.3 C 234.2 201.5 233.3 204.6 231.8 205.5 C 230.3 206.4 224.7 206 224.5 203.7 Z "
          />
          <g>
            <linearGradient
              id="_lgradient_449"
              x1="0.4999789800310275"
              x2="0.4999789800310273"
              y1="0.000008102973890089515"
              y2="0.9958635905642161"
              gradientTransform="matrix(0,-2.415,12.341,0,223.425,204.5)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(63,66,67)" }} />
              <stop
                offset="100%"
                stopOpacity="1"
                style={{ stopColor: "rgb(13,14,17)" }}
              />
            </linearGradient>
            <path
              fill="url(#_lgradient_449)"
              d=" M 223.8 204.5 C 227.5 204.1 231.1 203.9 234.8 204.1 C 235.6 204 236.5 202 234.8 202.1 C 231.1 202 227.4 202.4 223.8 203.2 C 223.3 203.3 223.3 204.5 223.8 204.5 Z "
            />
            <linearGradient
              id="_lgradient_450"
              x1="-0.03711040681047706"
              x2="0.947366280606041"
              y1="0.5055517522390183"
              y2="0.4409143939742437"
              gradientTransform="matrix(0,-2.007,12.105,0,223.561,204.007)"
              gradientUnits="userSpaceOnUse"
            >
              <stop
                offset="0%"
                stopOpacity="1"
                style={{ stopColor: "rgb(240,240,242)" }}
              />
              <stop
                offset="100%"
                stopOpacity="1"
                style={{ stopColor: "rgb(174,174,176)" }}
              />
            </linearGradient>
            <path
              fill="url(#_lgradient_450)"
              d=" M 224 204 C 227.6 203.6 231.1 203.5 234.7 203.6 C 235.5 203.5 236.4 202 234.7 202 C 231 202 227.4 202.3 223.8 203 C 223.5 203.1 223.4 204.1 224 204 Z "
            />
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 237.792 197.557 C 237.792 197.557 238.392 207.257 238.492 218.257 C 238.592 223.857 238.492 229.557 237.992 235.157 C 235.392 236.457 196.792 235.557 196.792 235.557 C 196.792 235.557 195.692 204.757 196.492 198.457 C 204.592 197.857 237.792 197.557 237.792 197.557 Z "
        />
      </g>
      <g
        id="leftrear_door_window"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.REAR_DOOR_WINDOW] &&
          damagedParts[ViGenericPartName.REAR_DOOR_WINDOW][ViSide.LEFT]
            ? isFaded(damagedParts[ViGenericPartName.REAR_DOOR_WINDOW][ViSide.LEFT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.REAR_DOOR_WINDOW] &&
            damagedParts[ViGenericPartName.REAR_DOOR_WINDOW][ViSide.LEFT]
          )
            handleHover({
              genericPartName: ViGenericPartName.REAR_DOOR_WINDOW,
              side: ViSide.LEFT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g opacity="0.7">
          <linearGradient
            id="_lgradient_446"
            x1="-0.02299911612440919"
            x2="1.0475089018898158"
            y1="0.4999974293059104"
            y2="0.4999974293059104"
            gradientTransform="matrix(0,-19.636,38.9,0,197.7,198.1)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(10,10,10)" }} />
            <stop
              offset="100%"
              stopOpacity="0.5"
              style={{ stopColor: "rgb(64,64,64)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_446)"
            d=" M 199.5 179 C 197.9 182.2 198.1 194.5 197.7 198.1 C 199.1 198 236.6 196.8 236.6 196.8 C 235.2 191.2 234.9 180.2 234.9 180.2 C 231.2 177.2 199.5 179 199.5 179 Z "
          />
        </g>
        <linearGradient
          id="_lgradient_447"
          x1="0.5000032531120255"
          x2="0.5000032531120253"
          y1="-0.000002415458935089987"
          y2="1.0000000000000036"
          gradientTransform="matrix(0,-20.662,41.4,0,196.2,198.9)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_447)"
          d=" M 196.2 198.9 C 197.6 198.8 230.4 198.8 237.6 197.6 C 237.1 193.8 236.8 184.5 234.9 179.4 C 221.3 177.2 198.5 178.8 198.5 178.8 C 197.2 184.3 196.6 195.3 196.2 198.9 Z  M 235.2 196.2 C 228.7 197.3 199.6 197.7 198.4 197.8 C 198.4 194.7 199.3 181 200.1 179.9 C 200.1 179.9 221.4 179 233.6 180.9 C 234.9 184.5 235.6 194.1 235.2 196.2 L 235.2 196.2 Z "
        />
        <path
          className={"highlight"}
          d=" M 196.2 198.9 C 197.6 198.8 230.4 198.8 237.6 197.6 C 237.1 193.8 236.8 184.5 234.9 179.4 C 221.3 177.2 198.5 178.8 198.5 178.8 C 197.2 184.3 196.6 195.3 196.2 198.9 Z "
        />
      </g>
      <g
        id="rightrear_door"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.REAR_DOOR] &&
          damagedParts[ViGenericPartName.REAR_DOOR][ViSide.RIGHT]
            ? isFaded(damagedParts[ViGenericPartName.REAR_DOOR][ViSide.RIGHT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.REAR_DOOR] &&
            damagedParts[ViGenericPartName.REAR_DOOR][ViSide.RIGHT]
          )
            handleHover({
              genericPartName: ViGenericPartName.REAR_DOOR,
              side: ViSide.RIGHT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g>
          <linearGradient
            id="_lgradient_433"
            x1="1.0130352921767907"
            x2="-0.011588091572634074"
            y1="0.5288272625339063"
            y2="0.46291162848679257"
            gradientTransform="matrix(0,-59.62,42.326,0,195.908,87.317)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
            <stop
              offset="50%"
              stopOpacity="1"
              style={{ stopColor: "rgb(210,211,215)" }}
            />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(191,192,197)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_433)"
            d=" M 237.5 66 C 237.5 66 238.1 56.3 238.2 45.3 C 238.3 39.7 238.2 34 237.7 28.4 C 235.1 27.1 196.5 28 196.5 28 C 196.5 28 195.4 58.8 196.2 65.1 C 204.3 65.7 237.5 66 237.5 66 Z "
          />
          <g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_PxQYoZNNWFbN7pLKPJfEaFDghUyauLfS">
                    <path
                      fill="rgb(255,255,255)"
                      fillRule="evenodd"
                      d=" M 234.8 84.1 C 234.8 84.1 234.5 85.3 234.2 86.2 C 221.2 88.1 198.7 87 198.7 87 L 198.4 84.7 L 234.8 84.1 Z  M 237.5 66 C 237.5 66 238.1 56.3 238.2 45.3 C 238.3 39.7 238.2 34 237.7 28.4 C 235.1 27.1 196.5 28 196.5 28 C 196.5 28 195.4 58.8 196.2 65.1 C 204.3 65.7 237.5 66 237.5 66 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_PxQYoZNNWFbN7pLKPJfEaFDghUyauLfS)">
                    <g>
                      <g opacity="0.4">
                        <linearGradient
                          id="_lgradient_434"
                          x1="0.7395604003228016"
                          x2="-0.034515061465158814"
                          y1="0.5015393343153765"
                          y2="0.4512720826537337"
                          gradientTransform="matrix(0,-4.963,37.488,0,196.4,37.363)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_434)"
                          d=" M 196.4 35.8 C 196.4 35.8 236 38.7 233.8 36.6 C 232.4 35.4 196.4 32.4 196.4 32.4 L 196.4 35.8 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_Zg9EGAjOLJAYjAfuvyLBOlhmuPFRPbGv">
                    <path
                      fill="rgb(255,255,255)"
                      fillRule="evenodd"
                      d=" M 234.8 84.1 C 234.8 84.1 234.5 85.3 234.2 86.2 C 221.2 88.1 198.7 87 198.7 87 L 198.4 84.7 L 234.8 84.1 Z  M 237.5 66 C 237.5 66 238.1 56.3 238.2 45.3 C 238.3 39.7 238.2 34 237.7 28.4 C 235.1 27.1 196.5 28 196.5 28 C 196.5 28 195.4 58.8 196.2 65.1 C 204.3 65.7 237.5 66 237.5 66 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_Zg9EGAjOLJAYjAfuvyLBOlhmuPFRPbGv)">
                    <g>
                      <g opacity="0.7">
                        <linearGradient
                          id="_lgradient_435"
                          x1="0.6652682008489013"
                          x2="0.05170150979638455"
                          y1="0.5032603441047758"
                          y2="0.48842555950486055"
                          gradientTransform="matrix(0,-16.848,49.021,0,192.931,62.1)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="0"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_435)"
                          d=" M 241.9 62.1 C 241.9 62.1 201.6 58.5 195 57.6 C 191.5 57.2 193.5 50.5 193.5 50.5 C 193.5 50.5 232.6 53.1 232.6 51.5 C 232.6 49.9 234.8 44.7 241.1 45.3 C 241.1 45.4 242.2 58.6 241.9 62.1 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_ZFB4zYC5RHDA4dGNN3pKqhhyzHCCJOHS">
                    <path
                      fill="rgb(255,255,255)"
                      fillRule="evenodd"
                      d=" M 234.8 84.1 C 234.8 84.1 234.5 85.3 234.2 86.2 C 221.2 88.1 198.7 87 198.7 87 L 198.4 84.7 L 234.8 84.1 Z  M 237.5 66 C 237.5 66 238.1 56.3 238.2 45.3 C 238.3 39.7 238.2 34 237.7 28.4 C 235.1 27.1 196.5 28 196.5 28 C 196.5 28 195.4 58.8 196.2 65.1 C 204.3 65.7 237.5 66 237.5 66 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_ZFB4zYC5RHDA4dGNN3pKqhhyzHCCJOHS)">
                    <g>
                      <g opacity="0.2">
                        <linearGradient
                          id="_lgradient_436"
                          x1="0.49999282217660124"
                          x2="0.49999282217660107"
                          y1="0"
                          y2="1.0000001090914021"
                          gradientTransform="matrix(0,-16.125,49.886,0,193,36.925)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(86,86,89)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(98,98,100)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_436)"
                          d=" M 196.5 32.5 C 196.5 32.5 231.6 34.8 241.9 36.9 C 244.9 37.5 240.1 27 240.1 27 L 199.2 20.8 L 193 22.8 L 196.5 32.5 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g>
          <linearGradient
            id="_lgradient_443"
            x1="0.49996770561985215"
            x2="0.499967705619852"
            y1="-0.005842029326746712"
            y2="0.9916347214548047"
            gradientTransform="matrix(0,-4.663,8.542,0,224.4,62.218)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(132,132,134)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(93,93,95)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_443)"
            d=" M 224.4 59.8 C 224.4 60.9 225.5 62.4 229.8 62.2 C 234.1 62 233.2 58.9 231.7 58 C 230.2 57.1 224.6 57.5 224.4 59.8 Z "
          />
          <g>
            <linearGradient
              id="_lgradient_444"
              x1="0.5000210199690542"
              x2="0.500021019969054"
              y1="2.4868995751603507e-14"
              y2="0.996665784976507"
              gradientTransform="matrix(0,-2.415,12.341,0,223.325,61.415)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(63,66,67)" }} />
              <stop
                offset="100%"
                stopOpacity="1"
                style={{ stopColor: "rgb(13,14,17)" }}
              />
            </linearGradient>
            <path
              fill="url(#_lgradient_444)"
              d=" M 223.7 59 C 227.4 59.4 231 59.6 234.7 59.4 C 235.5 59.5 236.4 61.5 234.7 61.4 C 231 61.5 227.3 61.1 223.7 60.3 C 223.2 60.2 223.2 59 223.7 59 Z "
            />
            <linearGradient
              id="_lgradient_445"
              x1="0.956044894829688"
              x2="-0.028838604242540478"
              y1="0.5043125642247084"
              y2="0.44467842043171274"
              gradientTransform="matrix(0,-2.007,12.105,0,223.461,61.5)"
              gradientUnits="userSpaceOnUse"
            >
              <stop
                offset="0%"
                stopOpacity="1"
                style={{ stopColor: "rgb(240,240,242)" }}
              />
              <stop
                offset="100%"
                stopOpacity="1"
                style={{ stopColor: "rgb(174,174,176)" }}
              />
            </linearGradient>
            <path
              fill="url(#_lgradient_445)"
              d=" M 223.9 59.5 C 227.5 59.9 231 60 234.6 59.9 C 235.4 60 236.3 61.5 234.6 61.5 C 230.9 61.5 227.3 61.2 223.7 60.5 C 223.4 60.4 223.3 59.4 223.9 59.5 Z "
            />
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 237.5 66 C 237.5 66 238.1 56.3 238.2 45.3 C 238.3 39.7 238.2 34 237.7 28.4 C 235.1 27.1 196.5 28 196.5 28 C 196.5 28 195.4 58.8 196.2 65.1 C 204.3 65.7 237.5 66 237.5 66 Z "
        />
      </g>
      <g
        id="rightrear_door_window"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.REAR_DOOR_WINDOW] &&
          damagedParts[ViGenericPartName.REAR_DOOR_WINDOW][ViSide.RIGHT]
            ? isFaded(damagedParts[ViGenericPartName.REAR_DOOR_WINDOW][ViSide.RIGHT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.REAR_DOOR_WINDOW] &&
            damagedParts[ViGenericPartName.REAR_DOOR_WINDOW][ViSide.RIGHT]
          )
            handleHover({
              genericPartName: ViGenericPartName.REAR_DOOR_WINDOW,
              side: ViSide.RIGHT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g opacity="0.7">
          <linearGradient
            id="_lgradient_441"
            x1="1.0095535503306188"
            x2="-0.05691287531804434"
            y1="0.5000025706940852"
            y2="0.500002570694085"
            gradientTransform="matrix(0,-19.71,38.9,0,197.6,85.01)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(10,10,10)" }} />
            <stop
              offset="100%"
              stopOpacity="0.5"
              style={{ stopColor: "rgb(64,64,64)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_441)"
            d=" M 234.8 83.2 C 234.8 83.2 235.1 72.2 236.5 66.6 C 236.5 66.6 199 65.4 197.6 65.3 C 198 69 197.8 81.3 199.4 84.4 C 199.4 84.5 231.1 86.3 234.8 83.2 Z "
          />
        </g>
        <linearGradient
          id="_lgradient_442"
          x1="0.5000015222216128"
          x2="0.5000015222216125"
          y1="-0.0028961352656970973"
          y2="0.9971038647342993"
          gradientTransform="matrix(0,-20.705,41.4,0,196.2,85.305)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
        </linearGradient>
        <path
          className={"highlight"}
          d=" M 198.5 84.7 C 198.5 84.7 221.3 86.4 234.9 84.1 C 236.7 79 237.1 69.7 237.6 65.9 C 230.4 64.7 197.6 64.7 196.2 64.6 C 196.5 68.2 197.1 79.2 198.5 84.7 Z "
        />
        <path
          fill="url(#_lgradient_442)"
          d=" M 198.5 84.7 C 198.5 84.7 221.3 86.4 234.9 84.1 C 236.7 79 237.1 69.7 237.6 65.9 C 230.4 64.7 197.6 64.7 196.2 64.6 C 196.5 68.2 197.1 79.2 198.5 84.7 Z  M 233.4 82.6 C 221.2 84.6 199.9 83.6 199.9 83.6 C 199.1 82.5 198.2 68.8 198.2 65.7 C 199.5 65.8 228.5 66.2 235 67.3 C 235.5 69.4 234.8 79.1 233.4 82.6 L 233.4 82.6 Z "
        />
      </g>
      <g
        id="leftfront_wheel"
        className={
          damagedParts &&
          ((damagedParts[ViGenericPartName.WHEEL] &&
            damagedParts[ViGenericPartName.WHEEL]["N/A"]) ||
            (damagedParts[ViGenericPartName.WHEEL] &&
              damagedParts[ViGenericPartName.WHEEL][ViSide.LEFT]) ||
            (damagedParts[ViGenericPartName.FRONT_WHEEL] &&
              damagedParts[ViGenericPartName.FRONT_WHEEL][ViSide.LEFT]))
            ? isFaded([
                ...(damagedParts[ViGenericPartName.WHEEL] &&
                damagedParts[ViGenericPartName.WHEEL]["N/A"] &&
                damagedParts[ViGenericPartName.WHEEL]["N/A"].length
                  ? damagedParts[ViGenericPartName.WHEEL]["N/A"]
                  : []),
                ...(damagedParts[ViGenericPartName.WHEEL] &&
                damagedParts[ViGenericPartName.WHEEL][ViSide.LEFT] &&
                damagedParts[ViGenericPartName.WHEEL][ViSide.LEFT].length
                  ? damagedParts[ViGenericPartName.WHEEL][ViSide.LEFT]
                  : []),
                ...(damagedParts[ViGenericPartName.FRONT_WHEEL] &&
                damagedParts[ViGenericPartName.FRONT_WHEEL][ViSide.LEFT] &&
                damagedParts[ViGenericPartName.FRONT_WHEEL][ViSide.LEFT].length
                  ? damagedParts[ViGenericPartName.FRONT_WHEEL][ViSide.LEFT]
                  : []),
              ])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            (damagedParts && damagedParts[ViGenericPartName.WHEEL]) ||
            (damagedParts &&
              damagedParts[ViGenericPartName.WHEEL] &&
              damagedParts[ViGenericPartName.WHEEL][ViSide.LEFT]) ||
            (damagedParts &&
              damagedParts[ViGenericPartName.FRONT_WHEEL] &&
              damagedParts[ViGenericPartName.FRONT_WHEEL][ViSide.LEFT]) ||
            (damagedParts && damagedParts[ViGenericPartName.FRONT_WHEEL])
          )
            handleHover({
              genericPartName: ViGenericPartName.WHEEL,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <path
          fill="rgb(0,0,0)"
          d=" M 75 232.2 C 66.4 232.2 59.3 239.2 59.3 247.8 C 59.3 256.4 66.3 263.5 75 263.5 L 76.6 263.5 C 85.3 263.5 92.3 256.5 92.3 247.8 C 92.3 239.1 85.3 232.1 76.6 232.1 L 75.8 232.1 C 75.5 232.2 75.3 232.2 75 232.2 Z "
        />
        <g opacity="0.5">
          <linearGradient
            id="_lgradient_418"
            x1="0.4999931972789349"
            x2="0.49999319727893493"
            y1="0.9989795918367359"
            y2="0.0010204081632672057"
            gradientTransform="matrix(0,-29.4,29.4,0,60.3,262.5)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="0" style={{ stopColor: "rgb(219,220,224)" }} />
            <stop
              offset="100%"
              stopOpacity="0.6"
              style={{ stopColor: "rgb(255,255,255)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_418)"
            d=" M 75 262.5 C 66.887 262.5 60.3 255.913 60.3 247.8 C 60.3 239.687 66.887 233.1 75 233.1 C 83.113 233.1 89.7 239.687 89.7 247.8 C 89.7 255.913 83.113 262.5 75 262.5 Z "
          />
        </g>
        <g>
          <linearGradient
            id="_lgradient_419"
            x1="0.9961095238095456"
            x2="-0.0038904761904545726"
            y1="0.5"
            y2="0.4999999999999999"
            gradientTransform="matrix(0,-21,21,0,64.5,258.3)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_419)"
            d=" M 75 258.3 C 69.2 258.3 64.5 253.6 64.5 247.8 C 64.5 242 69.2 237.3 75 237.3 C 80.8 237.3 85.5 242 85.5 247.8 C 85.5 253.6 80.8 258.3 75 258.3 L 75 258.3 Z "
          />
          <g>
            <g>
              <g>
                <g>
                  <g>
                    <clipPath id="_clipPath_EJCO7aUhzmb0d1eU3fHWdvOEIRtVShe1">
                      <path
                        fill="rgb(255,255,255)"
                        d=" M 75 258.5 C 69.095 258.5 64.3 253.705 64.3 247.8 C 64.3 241.895 69.095 237.1 75 237.1 C 80.905 237.1 85.7 241.895 85.7 247.8 C 85.7 253.705 80.905 258.5 75 258.5 Z "
                      />
                    </clipPath>
                    <g clipPath="url(#_clipPath_EJCO7aUhzmb0d1eU3fHWdvOEIRtVShe1)">
                      <g>
                        <g>
                          <linearGradient
                            id="_lgradient_420"
                            x1="0.8645909090910671"
                            x2="0.04420140190721622"
                            y1="0.993859154929579"
                            y2="0.00834184847506092"
                            gradientTransform="matrix(0,-5.133,7.1,0,78.8,247.533)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_420)"
                            d=" M 78.8 247.4 C 78.8 247.4 79.8 247.7 80.1 247.4 C 80.4 247.1 82.7 245.4 82.7 245.4 C 83.5 245.2 84.3 245.1 85.1 245.1 C 85.5 245.3 85.6 245.1 85.6 245.1 L 85.9 243.9 L 85.5 242.4 L 84.5 242.9 C 84 243 83.5 243.2 83.1 243.6 C 81.6 244.8 80.2 246.1 78.8 247.4 Z "
                          />
                          <linearGradient
                            id="_lgradient_421"
                            x1="1.0052783902976827"
                            x2="0.0033489525910219697"
                            y1="0.4999974318410665"
                            y2="0.4999974318410664"
                            gradientTransform="matrix(0,-7.256,7.788,0,76.594,247.656)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_421)"
                            d=" M 76.6 247.4 C 76.6 247.4 77.8 247.8 78.1 247.6 C 78.4 247.4 83.4 242.6 84.2 242.2 C 85 241.8 82.9 240.4 82.9 240.4 C 82.9 240.4 80.8 242.5 80.8 243.5 C 80.8 243.8 80.7 244 80.5 244.2 C 79.9 244.9 77.7 246.8 77.7 246.8 C 77.7 246.8 76.5 247.1 76.6 247.4 Z "
                          />
                          <linearGradient
                            id="_lgradient_422"
                            x1="0.07946048032784692"
                            x2="0.8494174116509421"
                            y1="0.8539385642606803"
                            y2="-0.12601021189823614"
                            gradientTransform="matrix(0,-6.1,6.311,0,76.489,257.5)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_422)"
                            d=" M 76.5 251.4 C 76.5 251.4 76.4 252.4 76.8 252.7 C 77.2 253 79.3 254.8 79.3 254.8 C 79.7 255.5 79.9 256.2 80.1 257 C 80 257.4 80.2 257.5 80.2 257.5 L 81.4 257.5 L 82.8 256.8 L 82.1 255.9 C 81.9 255.4 81.5 254.9 81.1 254.6 C 80.5 254.2 76.8 251.5 76.5 251.4 Z "
                          />
                          <linearGradient
                            id="_lgradient_423"
                            x1="0.6515169554453588"
                            x2="0.4608553043502712"
                            y1="0.9786758941538238"
                            y2="0.09100972141249963"
                            gradientTransform="matrix(0,-6.407,8.125,0,75.975,255.6)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_423)"
                            d=" M 76 249.2 C 76 249.2 75.9 250.5 76.1 250.7 C 76.3 250.9 82.2 254.9 82.6 255.5 C 83 256.1 84.1 253.8 84.1 253.8 C 84.1 253.8 81.6 252.2 80.6 252.4 C 80.3 252.5 80 252.4 79.8 252.3 C 79 251.8 76.6 250.1 76.6 250.1 C 76.6 250.1 76.3 249.1 76 249.2 Z "
                          />
                          <linearGradient
                            id="_lgradient_424"
                            x1="0.06220284556971656"
                            x2="1.0086799810125764"
                            y1="0.23550905581396364"
                            y2="0.49827576340889035"
                            gradientTransform="matrix(0,-7.9,4.3,0,67.8,258.9)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_424)"
                            d=" M 72.1 251 C 72.1 251 71.1 251.3 71 251.7 C 70.9 252.1 70 254.8 70 254.8 C 69.5 255.4 68.9 255.9 68.2 256.4 C 67.8 256.5 67.8 256.7 67.8 256.7 L 68.2 257.9 L 69.4 258.9 L 70 258 C 70.4 257.6 70.7 257.1 70.8 256.6 C 70.9 255.9 72.1 251.4 72.1 251 Z "
                          />
                          <linearGradient
                            id="_lgradient_425"
                            x1="0.17691425319149978"
                            x2="0.8130830031915388"
                            y1="1.197925959075448"
                            y2="0.7580607090753718"
                            gradientTransform="matrix(0,-9.4,3.275,0,70.657,259.2)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_425)"
                            d=" M 73.9 249.8 C 73.9 249.8 72.7 250.2 72.6 250.5 C 72.5 250.8 71.1 257.7 70.7 258.4 C 70.3 259.1 72.8 259.2 72.8 259.2 C 72.8 259.2 73.3 256.3 72.7 255.4 C 72.6 255.2 72.5 254.9 72.5 254.6 C 72.6 253.6 73.3 250.8 73.3 250.8 C 73.3 250.8 74.1 250 73.9 249.8 Z "
                          />
                          <linearGradient
                            id="_lgradient_426"
                            x1="0.10331141221347373"
                            x2="0.07819838441264723"
                            y1="5.207358235294025"
                            y2="6.1225766658725345"
                            gradientTransform="matrix(0,-2.911,8.5,0,62.9,247)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_426)"
                            d=" M 71.4 246.8 C 71.4 246.8 70.8 245.9 70.4 245.9 C 70 245.9 67.1 245.7 67.1 245.7 C 66.4 245.4 65.7 244.9 65.1 244.4 C 64.9 244 64.7 244.1 64.7 244.1 L 63.6 244.8 L 62.9 246.2 L 63.9 246.5 C 64.4 246.8 64.9 246.9 65.4 247 C 66.2 246.9 71.1 246.9 71.4 246.8 Z "
                          />
                          <linearGradient
                            id="_lgradient_427"
                            x1="5.702314362702477"
                            x2="7.593921843274848"
                            y1="1.5245329970575998"
                            y2="3.189147579961489"
                            gradientTransform="matrix(0,-2.212,9.516,0,63.584,249.4)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_427)"
                            d=" M 73.1 248.3 C 73.1 248.3 72.4 247.2 72.1 247.2 C 71.8 247.2 64.8 247.4 64 247.2 C 63.2 247 63.8 249.4 63.8 249.4 C 63.8 249.4 66.7 249.2 67.4 248.4 C 67.6 248.2 67.8 248.1 68.1 248 C 69.1 247.9 71.9 247.9 71.9 247.9 C 71.9 247.9 72.9 248.5 73.1 248.3 Z "
                          />
                          <linearGradient
                            id="_lgradient_428"
                            x1="1.021667158749989"
                            x2="0.10364064124995132"
                            y1="0.2416212350871696"
                            y2="0.5579758500873057"
                            gradientTransform="matrix(0,-8,2.811,0,73,244.1)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_428)"
                            d=" M 75.3 244.1 C 75.3 244.1 75.9 243.3 75.8 242.9 C 75.7 242.5 74.9 239.8 74.9 239.8 C 75 239 75.1 238.2 75.4 237.5 C 75.7 237.2 75.5 237 75.5 237 L 74.5 236.3 L 73 236.1 L 73.1 237.2 C 73 237.7 73 238.3 73.2 238.8 C 73.5 239.4 75.1 243.8 75.3 244.1 Z "
                          />
                          <linearGradient
                            id="_lgradient_429"
                            x1="0.6765041448913225"
                            x2="0.3943664514103995"
                            y1="-0.2098459908359711"
                            y2="0.5343767374405407"
                            gradientTransform="matrix(0,-9.147,5.109,0,70.1,246.2)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_429)"
                            d=" M 74.5 246.2 C 74.5 246.2 75.3 245.2 75.2 244.9 C 75.1 244.6 72.4 238.1 72.3 237.3 C 72.2 236.5 70.1 237.9 70.1 237.9 C 70.1 237.9 71.3 240.6 72.3 241 C 72.5 241.1 72.7 241.3 72.9 241.5 C 73.5 242.6 74 243.8 74.4 245 C 74.4 245 74.2 246.1 74.5 246.2 Z "
                          />
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_70"
                cx="0.4480000000000004"
                cy="0.4999999999999716"
                r="1"
                fx="0.4480000000000004"
                fy="0.4999999999999716"
                gradientTransform="matrix(0,-0.8,0.8,0,77,249.2)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_70)"
                d=" M 77.4 249.2 C 77.179 249.2 77 249.021 77 248.8 C 77 248.579 77.179 248.4 77.4 248.4 C 77.621 248.4 77.8 248.579 77.8 248.8 C 77.8 249.021 77.621 249.2 77.4 249.2 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_71"
                cx="0.4480000000000004"
                cy="0.5000000000000142"
                r="1"
                fx="0.4480000000000004"
                fy="0.5000000000000142"
                gradientTransform="matrix(0,-0.8,0.8,0,74.6,251.1)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_71)"
                d=" M 75 251.1 C 74.779 251.1 74.6 250.921 74.6 250.7 C 74.6 250.479 74.779 250.3 75 250.3 C 75.221 250.3 75.4 250.479 75.4 250.7 C 75.4 250.921 75.221 251.1 75 251.1 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_72"
                cx="0.4480000000000004"
                cy="0.5000000000000426"
                r="1"
                fx="0.4480000000000004"
                fy="0.5000000000000426"
                gradientTransform="matrix(0,-0.8,0.8,0,71.7,249.6)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_72)"
                d=" M 72.1 249.6 C 71.879 249.6 71.7 249.421 71.7 249.2 C 71.7 248.979 71.879 248.8 72.1 248.8 C 72.321 248.8 72.5 248.979 72.5 249.2 C 72.5 249.421 72.321 249.6 72.1 249.6 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_73"
                cx="0.4480000000000075"
                cy="0.4999999999999716"
                r="1"
                fx="0.4480000000000075"
                fy="0.4999999999999716"
                gradientTransform="matrix(0,-0.8,0.8,0,72.5,246.2)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_73)"
                d=" M 72.9 246.2 C 72.679 246.2 72.5 246.021 72.5 245.8 C 72.5 245.579 72.679 245.4 72.9 245.4 C 73.121 245.4 73.3 245.579 73.3 245.8 C 73.3 246.021 73.121 246.2 72.9 246.2 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_74"
                cx="0.4480000000000075"
                cy="0.4999999999999858"
                r="1"
                fx="0.4480000000000075"
                fy="0.4999999999999858"
                gradientTransform="matrix(0,-0.8,0.8,0,75.8,246.2)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_74)"
                d=" M 76.2 246.2 C 75.979 246.2 75.8 246.021 75.8 245.8 C 75.8 245.579 75.979 245.4 76.2 245.4 C 76.421 245.4 76.6 245.579 76.6 245.8 C 76.6 246.021 76.421 246.2 76.2 246.2 Z "
              />
            </g>
          </g>
          <linearGradient
            id="_lgradient_430"
            x1="0.6854485981308567"
            x2="0.31456074766357167"
            y1="0.010448598130843312"
            y2="0.9895560747663568"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(190,209,221)" }} />
            <stop
              offset="50%"
              stopOpacity="1"
              style={{ stopColor: "rgb(247,247,247)" }}
            />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(190,209,221)" }}
            />
          </linearGradient>
          <path
            fill="none"
            stroke="url(#_lgradient_430)"
            strokeLinecap="butt"
            strokeLinejoin="miter"
            strokeMiterlimit="10"
            strokeWidth="1"
            d=" M 75 258.5 C 69.095 258.5 64.3 253.705 64.3 247.8 C 64.3 241.895 69.095 237.1 75 237.1 C 80.905 237.1 85.7 241.895 85.7 247.8 C 85.7 253.705 80.905 258.5 75 258.5 Z "
            vectorEffect="non-scaling-stroke"
          />
        </g>
        <path
          className={"highlight"}
          d=" M 75 232.2 C 66.4 232.2 59.3 239.2 59.3 247.8 C 59.3 256.4 66.3 263.5 75 263.5 L 76.6 263.5 C 85.3 263.5 92.3 256.5 92.3 247.8 C 92.3 239.1 85.3 232.1 76.6 232.1 L 75.8 232.1 C 75.5 232.2 75.3 232.2 75 232.2 Z "
        />
      </g>
      <g
        id="rightfront_wheel"
        className={
          damagedParts &&
          ((damagedParts[ViGenericPartName.WHEEL] &&
            damagedParts[ViGenericPartName.WHEEL]["N/A"]) ||
            (damagedParts[ViGenericPartName.WHEEL] &&
              damagedParts[ViGenericPartName.WHEEL][ViSide.RIGHT]) ||
            (damagedParts[ViGenericPartName.FRONT_WHEEL] &&
              damagedParts[ViGenericPartName.FRONT_WHEEL][ViSide.RIGHT]))
            ? isFaded([
                ...(damagedParts[ViGenericPartName.WHEEL] &&
                damagedParts[ViGenericPartName.WHEEL]["N/A"] &&
                damagedParts[ViGenericPartName.WHEEL]["N/A"].length
                  ? damagedParts[ViGenericPartName.WHEEL]["N/A"]
                  : []),
                ...(damagedParts[ViGenericPartName.WHEEL] &&
                damagedParts[ViGenericPartName.WHEEL][ViSide.RIGHT] &&
                damagedParts[ViGenericPartName.WHEEL][ViSide.RIGHT].length
                  ? damagedParts[ViGenericPartName.WHEEL][ViSide.RIGHT]
                  : []),
                ...(damagedParts[ViGenericPartName.FRONT_WHEEL] &&
                damagedParts[ViGenericPartName.FRONT_WHEEL][ViSide.RIGHT] &&
                damagedParts[ViGenericPartName.FRONT_WHEEL][ViSide.RIGHT].length
                  ? damagedParts[ViGenericPartName.FRONT_WHEEL][ViSide.RIGHT]
                  : []),
              ])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            (damagedParts && damagedParts[ViGenericPartName.WHEEL]) ||
            (damagedParts &&
              damagedParts[ViGenericPartName.WHEEL] &&
              damagedParts[ViGenericPartName.WHEEL][ViSide.RIGHT]) ||
            (damagedParts &&
              damagedParts[ViGenericPartName.FRONT_WHEEL] &&
              damagedParts[ViGenericPartName.FRONT_WHEEL][ViSide.RIGHT]) ||
            (damagedParts && damagedParts[ViGenericPartName.FRONT_WHEEL])
          )
            handleHover({
              genericPartName: ViGenericPartName.WHEEL,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <path
          fill="rgb(0,0,0)"
          d=" M 74.6 0 C 65.9 0 58.9 7 58.9 15.7 C 58.9 24.4 65.9 31.4 74.6 31.4 L 76.2 31.4 C 84.9 31.4 91.9 24.4 91.9 15.7 C 91.9 7 84.9 0 76.2 0 L 76.2 0 L 74.6 0 Z "
        />
        <g opacity="0.5">
          <linearGradient
            id="_lgradient_405"
            x1="0.5"
            x2="0.5000000000000001"
            y1="0.9986394557823153"
            y2="0.0006802721088443597"
            gradientTransform="matrix(0,-29.4,29.4,0,59.9,30.3)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="0" style={{ stopColor: "rgb(219,220,224)" }} />
            <stop
              offset="100%"
              stopOpacity="0.6"
              style={{ stopColor: "rgb(255,255,255)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_405)"
            d=" M 74.6 30.3 C 66.487 30.3 59.9 23.713 59.9 15.6 C 59.9 7.487 66.487 0.9 74.6 0.9 C 82.713 0.9 89.3 7.487 89.3 15.6 C 89.3 23.713 82.713 30.3 74.6 30.3 Z "
          />
        </g>
        <g>
          <linearGradient
            id="_lgradient_406"
            x1="1.0028774509803924"
            x2="0.00041666666669826213"
            y1="0.5000000000000013"
            y2="0.5000000000000012"
            gradientTransform="matrix(0,-20.4,20.4,0,64.4,25.9)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_406)"
            d=" M 74.6 25.9 C 69 25.9 64.4 21.3 64.4 15.7 C 64.4 10.1 69 5.5 74.6 5.5 C 80.2 5.5 84.8 10.1 84.8 15.7 L 84.8 15.7 C 84.8 21.3 80.2 25.9 74.6 25.9 Z "
          />
          <g>
            <g>
              <g>
                <g>
                  <g>
                    <clipPath id="_clipPath_O8luHBhsouuJDEIu5WXxZeIGsGMSwouT">
                      <path
                        fill="rgb(255,255,255)"
                        d=" M 74.6 26.3 C 68.695 26.3 63.9 21.505 63.9 15.6 C 63.9 9.695 68.695 4.9 74.6 4.9 C 80.505 4.9 85.3 9.695 85.3 15.6 C 85.3 21.505 80.505 26.3 74.6 26.3 Z "
                      />
                    </clipPath>
                    <g clipPath="url(#_clipPath_O8luHBhsouuJDEIu5WXxZeIGsGMSwouT)">
                      <g>
                        <g>
                          <linearGradient
                            id="_lgradient_407"
                            x1="0.8645324675324773"
                            x2="0.04404715699184636"
                            y1="0.9938591549295825"
                            y2="0.008325916040234915"
                            gradientTransform="matrix(0,-5.133,7.1,0,78.4,15.333)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_407)"
                            d=" M 78.4 15.2 C 78.4 15.2 79.4 15.5 79.7 15.2 C 80 14.9 82.3 13.2 82.3 13.2 C 83.1 13 83.9 12.9 84.7 12.9 C 85.1 13.1 85.2 12.9 85.2 12.9 L 85.5 11.7 L 85.1 10.2 L 84.1 10.7 C 83.6 10.8 83.1 11 82.7 11.4 C 81.2 12.6 79.8 13.9 78.4 15.2 Z "
                          />
                          <linearGradient
                            id="_lgradient_408"
                            x1="1.00665656008821"
                            x2="0.003376515986872164"
                            y1="0.5000025701126756"
                            y2="0.5000025701126755"
                            gradientTransform="matrix(0,-7.256,7.782,0,76.1,15.456)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_408)"
                            d=" M 76.1 15.2 C 76.1 15.2 77.3 15.6 77.6 15.4 C 77.9 15.2 82.9 10.4 83.7 10 C 84.5 9.6 82.4 8.2 82.4 8.2 C 82.4 8.2 80.3 10.3 80.3 11.3 C 80.3 11.6 80.2 11.8 80 12 C 79.4 12.7 77.2 14.6 77.2 14.6 C 77.2 14.6 76.1 14.9 76.1 15.2 Z "
                          />
                          <linearGradient
                            id="_lgradient_409"
                            x1="0.09632743870968596"
                            x2="0.8525308450106293"
                            y1="0.8362347506066072"
                            y2="-0.12786033335120717"
                            gradientTransform="matrix(0,-6.2,6.411,0,76.089,25.4)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_409)"
                            d=" M 76.1 19.2 C 76.1 19.2 76 20.2 76.4 20.5 C 76.8 20.8 78.9 22.6 78.9 22.6 C 79.3 23.3 79.5 24 79.7 24.8 C 79.6 25.2 79.8 25.3 79.8 25.3 L 81.1 25.4 L 82.5 24.7 L 81.8 23.8 C 81.6 23.3 81.2 22.8 80.8 22.5 C 80.3 22.2 76.4 19.3 76.1 19.2 Z "
                          />
                          <linearGradient
                            id="_lgradient_410"
                            x1="0.6508370334011673"
                            x2="0.4601753823060799"
                            y1="0.9761536504615265"
                            y2="0.08848747772020304"
                            gradientTransform="matrix(0,-6.407,8.125,0,75.575,23.4)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_410)"
                            d=" M 75.6 17 C 75.6 17 75.5 18.3 75.7 18.5 C 75.9 18.7 81.8 22.7 82.2 23.3 C 82.6 23.9 83.7 21.6 83.7 21.6 C 83.7 21.6 81.2 20 80.2 20.2 C 79.9 20.3 79.6 20.2 79.4 20.1 C 78.6 19.6 76.2 17.9 76.2 17.9 C 76.2 17.9 75.9 16.9 75.6 17 Z "
                          />
                          <linearGradient
                            id="_lgradient_411"
                            x1="0.06143108227852423"
                            x2="1.0079875227849087"
                            y1="0.23527480232557707"
                            y2="0.4979558985281214"
                            gradientTransform="matrix(0,-7.9,4.3,0,67.4,26.7)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_411)"
                            d=" M 71.7 18.8 C 71.7 18.8 70.7 19.1 70.6 19.5 C 70.5 19.9 69.6 22.6 69.6 22.6 C 69.1 23.2 68.5 23.7 67.8 24.2 C 67.4 24.3 67.4 24.5 67.4 24.5 L 67.8 25.7 L 69 26.7 L 69.6 25.8 C 70 25.4 70.3 24.9 70.4 24.4 C 70.5 23.7 71.7 19.2 71.7 18.8 Z "
                          />
                          <linearGradient
                            id="_lgradient_412"
                            x1="0.1764382936170037"
                            x2="0.8133770436170558"
                            y1="1.1988718333696902"
                            y2="0.7584741833697028"
                            gradientTransform="matrix(0,-9.4,3.275,0,70.257,27)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_412)"
                            d=" M 73.5 17.6 C 73.5 17.6 72.3 18 72.2 18.3 C 72.1 18.6 70.7 25.5 70.3 26.2 C 69.9 26.9 72.4 27 72.4 27 C 72.4 27 72.9 24.1 72.3 23.2 C 72.2 23 72.1 22.7 72.1 22.4 C 72.2 21.4 72.9 18.6 72.9 18.6 C 72.9 18.6 73.7 17.8 73.5 17.6 Z "
                          />
                          <linearGradient
                            id="_lgradient_413"
                            x1="0.11319732824432549"
                            x2="0.08805298375475334"
                            y1="5.07959117647057"
                            y2="5.994851191310773"
                            gradientTransform="matrix(0,-2.911,8.5,0,62.5,14.8)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_413)"
                            d=" M 71 14.6 C 71 14.6 70.4 13.7 70 13.7 C 69.6 13.7 66.7 13.5 66.7 13.5 C 66 13.2 65.3 12.7 64.7 12.2 C 64.5 11.8 64.3 11.9 64.3 11.9 L 63.2 12.6 L 62.5 14 L 63.5 14.3 C 64 14.6 64.5 14.7 65 14.8 C 65.8 14.7 70.7 14.7 71 14.6 Z "
                          />
                          <linearGradient
                            id="_lgradient_414"
                            x1="5.401545319733572"
                            x2="7.293152800306544"
                            y1="1.4630277427489355"
                            y2="3.1276423256534276"
                            gradientTransform="matrix(0,-2.212,9.516,0,63.184,17.2)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_414)"
                            d=" M 72.7 16.1 C 72.7 16.1 72 15 71.7 15 C 71.4 15 64.4 15.2 63.6 15 C 62.8 14.8 63.4 17.2 63.4 17.2 C 63.4 17.2 66.3 17 67 16.2 C 67.2 16 67.4 15.9 67.7 15.8 C 68.7 15.7 71.5 15.7 71.5 15.7 C 71.5 15.7 72.5 16.3 72.7 16.1 Z "
                          />
                          <linearGradient
                            id="_lgradient_415"
                            x1="1.0238329199999825"
                            x2="0.1062245449999395"
                            y1="0.24024412519453264"
                            y2="0.5554605001945097"
                            gradientTransform="matrix(0,-8,2.811,0,72.6,11.9)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_415)"
                            d=" M 74.9 11.9 C 74.9 11.9 75.5 11.1 75.4 10.7 C 75.3 10.3 74.5 7.6 74.5 7.6 C 74.6 6.8 74.7 6 75 5.3 C 75.3 5 75.1 4.8 75.1 4.8 L 74.1 4.1 L 72.6 3.9 L 72.7 5 C 72.6 5.5 72.6 6.1 72.8 6.6 C 73 7.1 74.6 11.6 74.9 11.9 Z "
                          />
                          <linearGradient
                            id="_lgradient_416"
                            x1="0.676787365015489"
                            x2="0.39464967153456953"
                            y1="-0.21127859813065264"
                            y2="0.53294413014575"
                            gradientTransform="matrix(0,-9.147,5.109,0,69.7,14)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_416)"
                            d=" M 74.1 14 C 74.1 14 74.9 13 74.8 12.7 C 74.7 12.4 72 5.9 71.9 5.1 C 71.8 4.3 69.7 5.7 69.7 5.7 C 69.7 5.7 70.9 8.4 71.9 8.8 C 72.1 8.9 72.3 9.1 72.5 9.3 C 73.1 10.4 73.6 11.6 74 12.8 C 74 12.8 73.8 13.9 74.1 14 Z "
                          />
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_65"
                cx="0.4478749999998968"
                cy="0.5000000000000142"
                r="1"
                fx="0.4478749999998968"
                fy="0.5000000000000142"
                gradientTransform="matrix(0,-0.8,0.8,0,76.6,16.9)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_65)"
                d=" M 77 16.9 C 76.779 16.9 76.6 16.721 76.6 16.5 C 76.6 16.279 76.779 16.1 77 16.1 C 77.221 16.1 77.4 16.279 77.4 16.5 C 77.4 16.721 77.221 16.9 77 16.9 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_66"
                cx="0.4478749999998968"
                cy="0.5000000000000426"
                r="1"
                fx="0.4478749999998968"
                fy="0.5000000000000426"
                gradientTransform="matrix(0,-0.8,0.8,0,74.2,18.9)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_66)"
                d=" M 74.6 18.9 C 74.379 18.9 74.2 18.721 74.2 18.5 C 74.2 18.279 74.379 18.1 74.6 18.1 C 74.821 18.1 75 18.279 75 18.5 C 75 18.721 74.821 18.9 74.6 18.9 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_67"
                cx="0.4478749999998968"
                cy="0.4999999999999858"
                r="1"
                fx="0.4478749999998968"
                fy="0.4999999999999858"
                gradientTransform="matrix(0,-0.8,0.8,0,71.3,17.4)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_67)"
                d=" M 71.7 17.4 C 71.479 17.4 71.3 17.221 71.3 17 C 71.3 16.779 71.479 16.6 71.7 16.6 C 71.921 16.6 72.1 16.779 72.1 17 C 72.1 17.221 71.921 17.4 71.7 17.4 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_68"
                cx="0.4479999999999791"
                cy="0.5"
                r="1"
                fx="0.4479999999999791"
                fy="0.5"
                gradientTransform="matrix(0,-0.8,0.8,0,72.1,14)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_68)"
                d=" M 72.5 14 C 72.279 14 72.1 13.821 72.1 13.6 C 72.1 13.379 72.279 13.2 72.5 13.2 C 72.721 13.2 72.9 13.379 72.9 13.6 C 72.9 13.821 72.721 14 72.5 14 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_69"
                cx="0.4479999999999791"
                cy="0.5000000000000284"
                r="1"
                fx="0.4479999999999791"
                fy="0.5000000000000284"
                gradientTransform="matrix(0,-0.8,0.8,0,75.4,14)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_69)"
                d=" M 75.8 14 C 75.579 14 75.4 13.821 75.4 13.6 C 75.4 13.379 75.579 13.2 75.8 13.2 C 76.021 13.2 76.2 13.379 76.2 13.6 C 76.2 13.821 76.021 14 75.8 14 Z "
              />
            </g>
          </g>
          <linearGradient
            id="_lgradient_417"
            x1="0.6854579439252593"
            x2="0.31454672897198854"
            y1="0.01045794392523458"
            y2="0.9895467289719612"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(190,209,221)" }} />
            <stop
              offset="50%"
              stopOpacity="1"
              style={{ stopColor: "rgb(247,247,247)" }}
            />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(190,209,221)" }}
            />
          </linearGradient>
          <path
            fill="none"
            stroke="url(#_lgradient_417)"
            strokeLinecap="butt"
            strokeLinejoin="miter"
            strokeMiterlimit="10"
            strokeWidth="1"
            d=" M 74.6 26.3 C 68.695 26.3 63.9 21.505 63.9 15.6 C 63.9 9.695 68.695 4.9 74.6 4.9 C 80.505 4.9 85.3 9.695 85.3 15.6 C 85.3 21.505 80.505 26.3 74.6 26.3 Z "
            vectorEffect="non-scaling-stroke"
          />
        </g>
        <path
          className={"highlight"}
          d=" M 74.6 0 C 65.9 0 58.9 7 58.9 15.7 C 58.9 24.4 65.9 31.4 74.6 31.4 L 76.2 31.4 C 84.9 31.4 91.9 24.4 91.9 15.7 C 91.9 7 84.9 0 76.2 0 L 76.2 0 L 74.6 0 Z "
        />
      </g>
      <g
        id="leftrear_wheel"
        className={
          damagedParts &&
          ((damagedParts[ViGenericPartName.WHEEL] &&
            damagedParts[ViGenericPartName.WHEEL]["N/A"]) ||
            (damagedParts[ViGenericPartName.WHEEL] &&
              damagedParts[ViGenericPartName.WHEEL][ViSide.LEFT]) ||
            (damagedParts[ViGenericPartName.REAR_WHEEL] &&
              damagedParts[ViGenericPartName.REAR_WHEEL][ViSide.LEFT]))
            ? isFaded([
                ...(damagedParts[ViGenericPartName.WHEEL] &&
                damagedParts[ViGenericPartName.WHEEL]["N/A"] &&
                damagedParts[ViGenericPartName.WHEEL]["N/A"].length
                  ? damagedParts[ViGenericPartName.WHEEL]["N/A"]
                  : []),
                ...(damagedParts[ViGenericPartName.WHEEL] &&
                damagedParts[ViGenericPartName.WHEEL][ViSide.LEFT] &&
                damagedParts[ViGenericPartName.WHEEL][ViSide.LEFT].length
                  ? damagedParts[ViGenericPartName.WHEEL][ViSide.LEFT]
                  : []),
                ...(damagedParts[ViGenericPartName.REAR_WHEEL] &&
                damagedParts[ViGenericPartName.REAR_WHEEL][ViSide.LEFT] &&
                damagedParts[ViGenericPartName.REAR_WHEEL][ViSide.LEFT].length
                  ? damagedParts[ViGenericPartName.REAR_WHEEL][ViSide.LEFT]
                  : []),
              ])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={(e) => {
          if (
            (damagedParts && damagedParts[ViGenericPartName.WHEEL]) ||
            (damagedParts &&
              damagedParts[ViGenericPartName.WHEEL] &&
              damagedParts[ViGenericPartName.WHEEL][ViSide.LEFT]) ||
            (damagedParts &&
              damagedParts[ViGenericPartName.REAR_WHEEL] &&
              damagedParts[ViGenericPartName.REAR_WHEEL][ViSide.LEFT]) ||
            (damagedParts && damagedParts[ViGenericPartName.REAR_WHEEL])
          )
            handleHover({
              genericPartName: ViGenericPartName.WHEEL,
            });
        }}
        onMouseLeave={() => handleHover()}
      >
        <path
          fill="rgb(0,0,0)"
          d=" M 276.4 232.1 C 267.7 232.1 260.7 239.1 260.7 247.8 C 260.7 256.5 267.7 263.5 276.4 263.5 L 276.4 263.5 L 278 263.5 C 286.7 263.5 293.7 256.5 293.7 247.8 C 293.7 239.1 286.7 232.1 278 232.1 L 278 232.1 L 276.4 232.1 Z "
        />
        <g opacity="0.5">
          <linearGradient
            id="_lgradient_392"
            x1="0.5"
            x2="0.5000000000000001"
            y1="0.9972789115646279"
            y2="-0.0006802721088412511"
            gradientTransform="matrix(0,-29.4,29.4,0,261.7,262.4)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="0" style={{ stopColor: "rgb(219,220,224)" }} />
            <stop
              offset="100%"
              stopOpacity="0.6"
              style={{ stopColor: "rgb(255,255,255)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_392)"
            d=" M 276.4 262.4 C 268.287 262.4 261.7 255.813 261.7 247.7 C 261.7 239.587 268.287 233 276.4 233 C 284.513 233 291.1 239.587 291.1 247.7 C 291.1 255.813 284.513 262.4 276.4 262.4 Z "
          />
        </g>
        <g>
          <linearGradient
            id="_lgradient_393"
            x1="1.003330097087396"
            x2="-0.0005631067960898406"
            y1="0.5"
            y2="0.4999999999999999"
            gradientTransform="matrix(0,-20.6,20.6,0,266.1,258.1)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_393)"
            d=" M 276.4 258.1 C 270.7 258.1 266.1 253.5 266.1 247.8 C 266.1 242.1 270.7 237.5 276.4 237.5 C 282.1 237.5 286.7 242.1 286.7 247.8 L 286.7 247.8 C 286.7 253.5 282.1 258.1 276.4 258.1 L 276.4 258.1 Z "
          />
          <g>
            <g>
              <g>
                <g>
                  <g>
                    <clipPath id="_clipPath_Tix3jqWNnjNY7akhycIv2DCVujtf6sa8">
                      <path
                        fill="rgb(255,255,255)"
                        d=" M 276.4 258.4 C 270.494 258.4 265.7 253.605 265.7 247.7 C 265.7 241.795 270.494 237 276.4 237 C 282.305 237 287.1 241.795 287.1 247.7 C 287.1 253.605 282.305 258.4 276.4 258.4 Z "
                      />
                    </clipPath>
                    <g clipPath="url(#_clipPath_Tix3jqWNnjNY7akhycIv2DCVujtf6sa8)">
                      <g>
                        <g>
                          <linearGradient
                            id="_lgradient_394"
                            x1="0.8617662337663177"
                            x2="0.0410598526995859"
                            y1="0.9889295774647948"
                            y2="0.004782051981323088"
                            gradientTransform="matrix(0,-5.133,7.1,0,280.2,247.433)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_394)"
                            d=" M 280.2 247.3 C 280.2 247.3 281.2 247.6 281.5 247.3 C 281.8 247 284.1 245.3 284.1 245.3 C 284.9 245.1 285.7 245 286.5 245 C 286.9 245.2 287 245 287 245 L 287.3 243.8 L 286.9 242.3 L 285.9 242.8 C 285.4 242.9 284.9 243.1 284.5 243.5 C 283 244.7 281.5 245.9 280.2 247.3 Z "
                          />
                          <linearGradient
                            id="_lgradient_395"
                            x1="1.00665656008821"
                            x2="0.003376515986872164"
                            y1="0.5000025701126773"
                            y2="0.5000025701126772"
                            gradientTransform="matrix(0,-7.256,7.782,0,277.9,247.556)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_395)"
                            d=" M 277.9 247.3 C 277.9 247.3 279.1 247.7 279.4 247.5 C 279.7 247.3 284.7 242.5 285.5 242.1 C 286.3 241.7 284.2 240.3 284.2 240.3 C 284.2 240.3 282.1 242.4 282.1 243.4 C 282.1 243.7 282 243.9 281.8 244.1 C 281.2 244.8 279 246.7 279 246.7 C 279 246.7 277.9 247 277.9 247.3 Z "
                          />
                          <linearGradient
                            id="_lgradient_396"
                            x1="0.0930845693547937"
                            x2="0.8489244726030885"
                            y1="0.8406376067590031"
                            y2="-0.12500140869152077"
                            gradientTransform="matrix(0,-6.2,6.411,0,277.789,257.5)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_396)"
                            d=" M 277.8 251.3 C 277.8 251.3 277.7 252.3 278.1 252.6 C 278.5 252.9 280.6 254.7 280.6 254.7 C 281 255.4 281.2 256.1 281.4 256.9 C 281.3 257.3 281.5 257.4 281.5 257.4 L 282.8 257.5 L 284.2 256.8 L 283.5 255.9 C 283.3 255.4 282.9 254.9 282.5 254.6 C 282 254.3 278.2 251.4 277.8 251.3 Z "
                          />
                          <linearGradient
                            id="_lgradient_397"
                            x1="0.6495251049793467"
                            x2="0.4588503586308996"
                            y1="0.971328580923057"
                            y2="0.08360144042353146"
                            gradientTransform="matrix(0,-6.407,8.125,0,277.375,255.5)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_397)"
                            d=" M 277.4 249.1 C 277.4 249.1 277.3 250.4 277.5 250.6 C 277.7 250.8 283.6 254.8 284 255.4 C 284.4 256 285.5 253.7 285.5 253.7 C 285.5 253.7 283 252.1 282 252.3 C 281.7 252.4 281.4 252.3 281.2 252.2 C 280.4 251.7 278 250 278 250 C 278 250 277.7 249 277.4 249.1 Z "
                          />
                          <linearGradient
                            id="_lgradient_398"
                            x1="0.0623112316456087"
                            x2="1.009587545569815"
                            y1="0.2386415139535174"
                            y2="0.5023637493965165"
                            gradientTransform="matrix(0,-7.9,4.3,0,269.1,258.8)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_398)"
                            d=" M 273.4 250.9 C 273.4 250.9 272.4 251.2 272.3 251.6 C 272.2 252 271.3 254.7 271.3 254.7 C 270.8 255.3 270.2 255.8 269.5 256.3 C 269.1 256.4 269.1 256.6 269.1 256.6 L 269.5 257.8 L 270.7 258.8 L 271.3 257.9 C 271.7 257.5 272 257 272.1 256.5 C 272.7 254.7 273.1 252.8 273.4 250.9 Z "
                          />
                          <linearGradient
                            id="_lgradient_399"
                            x1="0.17922644361711448"
                            x2="0.8153164436170037"
                            y1="1.1933417800527195"
                            y2="0.7535309800526977"
                            gradientTransform="matrix(0,-9.4,3.275,0,272.057,259.1)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_399)"
                            d=" M 275.3 249.7 C 275.3 249.7 274.1 250.1 274 250.4 C 273.9 250.7 272.5 257.6 272.1 258.3 C 271.7 259 274.2 259.1 274.2 259.1 C 274.2 259.1 274.7 256.2 274.1 255.3 C 274 255.1 273.9 254.8 273.9 254.5 C 274 253.5 274.7 250.7 274.7 250.7 C 274.7 250.7 275.5 249.9 275.3 249.7 Z "
                          />
                          <linearGradient
                            id="_lgradient_400"
                            x1="0.11595916030498188"
                            x2="0.09084613250415588"
                            y1="5.048779882352843"
                            y2="5.963998312931452"
                            gradientTransform="matrix(0,-2.911,8.5,0,264.3,246.9)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_400)"
                            d=" M 272.8 246.7 C 272.8 246.7 272.2 245.8 271.8 245.8 C 271.4 245.8 268.5 245.6 268.5 245.6 C 267.8 245.3 267.1 244.8 266.5 244.3 C 266.3 243.9 266.1 244 266.1 244 L 265 244.7 L 264.3 246.1 L 265.3 246.4 C 265.8 246.7 266.3 246.8 266.8 246.9 C 267.6 246.8 272.4 246.8 272.8 246.7 Z "
                          />
                          <linearGradient
                            id="_lgradient_401"
                            x1="5.613085033275098"
                            x2="7.501886398607073"
                            y1="1.5064125683060325"
                            y2="3.1685577697980696"
                            gradientTransform="matrix(0,-2.212,9.516,0,264.884,249.3)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_401)"
                            d=" M 274.4 248.2 C 274.4 248.2 273.7 247.1 273.4 247.1 C 273.1 247.1 266.1 247.3 265.3 247.1 C 264.5 246.9 265.1 249.3 265.1 249.3 C 265.1 249.3 268 249.1 268.7 248.3 C 268.9 248.1 269.1 248 269.4 247.9 C 270.4 247.8 273.2 247.8 273.2 247.8 C 273.2 247.8 274.3 248.4 274.4 248.2 Z "
                          />
                          <linearGradient
                            id="_lgradient_402"
                            x1="1.0204961274999889"
                            x2="0.10288775249997428"
                            y1="0.24354682130046967"
                            y2="0.5587631963004469"
                            gradientTransform="matrix(0,-8,2.811,0,274.3,244)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_402)"
                            d=" M 276.6 244 C 276.6 244 277.2 243.2 277.1 242.8 C 277 242.4 276.2 239.7 276.2 239.7 C 276.3 238.9 276.4 238.1 276.7 237.4 C 277 237.1 276.8 236.9 276.8 236.9 L 275.8 236.2 L 274.3 236 L 274.4 237.1 C 274.3 237.6 274.3 238.2 274.5 238.7 C 274.7 239.2 276.4 243.7 276.6 244 Z "
                          />
                          <linearGradient
                            id="_lgradient_403"
                            x1="0.6742644364016712"
                            x2="0.3921073647995499"
                            y1="-0.19929049877415395"
                            y2="0.5449833451049386"
                            gradientTransform="matrix(0,-9.147,5.109,0,271.4,246.1)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_403)"
                            d=" M 275.8 246.1 C 275.8 246.1 276.6 245.1 276.5 244.8 C 276.4 244.5 273.7 238 273.6 237.2 C 273.5 236.4 271.4 237.8 271.4 237.8 C 271.4 237.8 272.6 240.5 273.6 240.9 C 273.8 241 274 241.2 274.2 241.4 C 274.8 242.5 275.3 243.7 275.7 244.9 C 275.7 244.9 275.5 246 275.8 246.1 Z "
                          />
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_60"
                cx="0.5"
                cy="0.5"
                r="1"
                fx="0.38537500000040836"
                fy="0.5624999999999432"
                gradientTransform="matrix(0,-0.8,0.8,0,278.3,249)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_60)"
                d=" M 278.7 249 C 278.479 249 278.3 248.821 278.3 248.6 C 278.3 248.379 278.479 248.2 278.7 248.2 C 278.921 248.2 279.1 248.379 279.1 248.6 C 279.1 248.821 278.921 249 278.7 249 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_61"
                cx="0.5"
                cy="0.5"
                r="1"
                fx="0.4478749999998968"
                fy="0.5"
                gradientTransform="matrix(0,-0.8,0.8,0,276,251)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_61)"
                d=" M 276.4 251 C 276.179 251 276 250.821 276 250.6 C 276 250.379 276.179 250.2 276.4 250.2 C 276.621 250.2 276.8 250.379 276.8 250.6 C 276.8 250.821 276.621 251 276.4 251 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_62"
                cx="0.5"
                cy="0.5"
                r="1"
                fx="0.4478749999998968"
                fy="0.49999999999994316"
                gradientTransform="matrix(0,-0.8,0.8,0,273.1,249.5)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_62)"
                d=" M 273.5 249.5 C 273.279 249.5 273.1 249.321 273.1 249.1 C 273.1 248.879 273.279 248.7 273.5 248.7 C 273.721 248.7 273.9 248.879 273.9 249.1 C 273.9 249.321 273.721 249.5 273.5 249.5 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_63"
                cx="0.5"
                cy="0.5"
                r="1"
                fx="0.4479999999999791"
                fy="0.49999999999994316"
                gradientTransform="matrix(0,-0.8,0.8,0,273.9,246.1)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_63)"
                d=" M 274.3 246.1 C 274.079 246.1 273.9 245.921 273.9 245.7 C 273.9 245.479 274.079 245.3 274.3 245.3 C 274.521 245.3 274.7 245.479 274.7 245.7 C 274.7 245.921 274.521 246.1 274.3 246.1 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_64"
                cx="0.5"
                cy="0.5"
                r="1"
                fx="0.4479999999999791"
                fy="0.49999999999994316"
                gradientTransform="matrix(0,-0.8,0.8,0,277.2,246.1)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_64)"
                d=" M 277.6 246.1 C 277.379 246.1 277.2 245.921 277.2 245.7 C 277.2 245.479 277.379 245.3 277.6 245.3 C 277.821 245.3 278 245.479 278 245.7 C 278 245.921 277.821 246.1 277.6 246.1 Z "
              />
            </g>
          </g>
          <linearGradient
            id="_lgradient_404"
            x1="0.685523364486011"
            x2="0.3144766355140298"
            y1="0.010476635514020671"
            y2="0.9895233644859797"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(190,209,221)" }} />
            <stop
              offset="50%"
              stopOpacity="1"
              style={{ stopColor: "rgb(247,247,247)" }}
            />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(190,209,221)" }}
            />
          </linearGradient>
          <path
            fill="none"
            stroke="url(#_lgradient_404)"
            strokeLinecap="butt"
            strokeLinejoin="miter"
            strokeMiterlimit="10"
            strokeWidth="1"
            d=" M 276.4 258.4 C 270.494 258.4 265.7 253.605 265.7 247.7 C 265.7 241.795 270.494 237 276.4 237 C 282.305 237 287.1 241.795 287.1 247.7 C 287.1 253.605 282.305 258.4 276.4 258.4 Z "
            vectorEffect="non-scaling-stroke"
          />
        </g>
        <path
          className={"highlight"}
          d=" M 276.4 232.1 C 267.7 232.1 260.7 239.1 260.7 247.8 C 260.7 256.5 267.7 263.5 276.4 263.5 L 276.4 263.5 L 278 263.5 C 286.7 263.5 293.7 256.5 293.7 247.8 C 293.7 239.1 286.7 232.1 278 232.1 L 278 232.1 L 276.4 232.1 Z "
        />
      </g>
      <g
        id="rightrear_wheel"
        className={
          damagedParts &&
          ((damagedParts[ViGenericPartName.WHEEL] &&
            damagedParts[ViGenericPartName.WHEEL]["N/A"]) ||
            (damagedParts[ViGenericPartName.WHEEL] &&
              damagedParts[ViGenericPartName.WHEEL][ViSide.RIGHT]) ||
            (damagedParts[ViGenericPartName.REAR_WHEEL] &&
              damagedParts[ViGenericPartName.REAR_WHEEL][ViSide.RIGHT]))
            ? isFaded([
                ...(damagedParts[ViGenericPartName.WHEEL] &&
                damagedParts[ViGenericPartName.WHEEL]["N/A"] &&
                damagedParts[ViGenericPartName.WHEEL]["N/A"].length
                  ? damagedParts[ViGenericPartName.WHEEL]["N/A"]
                  : []),
                ...(damagedParts[ViGenericPartName.WHEEL] &&
                damagedParts[ViGenericPartName.WHEEL][ViSide.RIGHT] &&
                damagedParts[ViGenericPartName.WHEEL][ViSide.RIGHT].length
                  ? damagedParts[ViGenericPartName.WHEEL][ViSide.RIGHT]
                  : []),
                ...(damagedParts[ViGenericPartName.REAR_WHEEL] &&
                damagedParts[ViGenericPartName.REAR_WHEEL][ViSide.RIGHT] &&
                damagedParts[ViGenericPartName.REAR_WHEEL][ViSide.RIGHT].length
                  ? damagedParts[ViGenericPartName.REAR_WHEEL][ViSide.RIGHT]
                  : []),
              ])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            (damagedParts && damagedParts[ViGenericPartName.WHEEL]) ||
            (damagedParts &&
              damagedParts[ViGenericPartName.WHEEL] &&
              damagedParts[ViGenericPartName.WHEEL][ViSide.RIGHT]) ||
            (damagedParts &&
              damagedParts[ViGenericPartName.REAR_WHEEL] &&
              damagedParts[ViGenericPartName.REAR_WHEEL][ViSide.RIGHT]) ||
            (damagedParts && damagedParts[ViGenericPartName.REAR_WHEEL])
          )
            handleHover({
              genericPartName: ViGenericPartName.WHEEL,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <path
          fill="rgb(0,0,0)"
          d=" M 276.4 0 C 267.7 0 260.7 7 260.7 15.7 C 260.7 24.4 267.7 31.4 276.4 31.4 L 276.4 31.4 L 278 31.4 C 286.7 31.4 293.7 24.4 293.7 15.7 C 293.7 7 286.7 0 278 0 L 278 0 L 276.4 0 Z "
        />
        <g opacity="0.5">
          <linearGradient
            id="_lgradient_366"
            x1="0.5"
            x2="0.5000000000000001"
            y1="0.9972789115646279"
            y2="-0.0006802721088412511"
            gradientTransform="matrix(0,-29.4,29.4,0,261.7,30.3)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="0" style={{ stopColor: "rgb(219,220,224)" }} />
            <stop
              offset="100%"
              stopOpacity="0.6"
              style={{ stopColor: "rgb(255,255,255)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_366)"
            d=" M 276.4 30.3 C 268.287 30.3 261.7 23.713 261.7 15.6 C 261.7 7.487 268.287 0.9 276.4 0.9 C 284.513 0.9 291.1 7.487 291.1 15.6 C 291.1 23.713 284.513 30.3 276.4 30.3 Z "
          />
        </g>
        <g>
          <linearGradient
            id="_lgradient_367"
            x1="1.003330097087396"
            x2="-0.0005631067960898406"
            y1="0.5000000000000018"
            y2="0.5000000000000017"
            gradientTransform="matrix(0,-20.6,20.6,0,266.1,26)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_367)"
            d=" M 276.4 26 C 270.7 26 266.1 21.4 266.1 15.7 C 266.1 10 270.7 5.4 276.4 5.4 C 282.1 5.4 286.7 10 286.7 15.7 L 286.7 15.7 C 286.7 21.4 282.1 26 276.4 26 L 276.4 26 Z "
          />
          <g>
            <g>
              <g>
                <g>
                  <g>
                    <clipPath id="_clipPath_eGLSEsn37k3CUbeEjFa8WfE0fvKXKFSb">
                      <path
                        fill="rgb(255,255,255)"
                        d=" M 276.4 26.3 C 270.495 26.3 265.7 21.505 265.7 15.6 C 265.7 9.695 270.495 4.9 276.4 4.9 C 282.305 4.9 287.1 9.695 287.1 15.6 C 287.1 21.505 282.305 26.3 276.4 26.3 Z "
                      />
                    </clipPath>
                    <g clipPath="url(#_clipPath_eGLSEsn37k3CUbeEjFa8WfE0fvKXKFSb)">
                      <g>
                        <g>
                          <linearGradient
                            id="_lgradient_368"
                            x1="0.8617662337663177"
                            x2="0.0410598526995859"
                            y1="0.9889295774647948"
                            y2="0.004782051981323088"
                            gradientTransform="matrix(0,-5.133,7.1,0,280.2,15.333)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_368)"
                            d=" M 280.2 15.2 C 280.2 15.2 281.2 15.5 281.5 15.2 C 281.8 14.9 284.1 13.2 284.1 13.2 C 284.9 13 285.7 12.9 286.5 12.9 C 286.9 13.1 287 12.9 287 12.9 L 287.3 11.7 L 286.9 10.2 L 285.9 10.7 C 285.4 10.8 284.9 11 284.5 11.4 C 283 12.6 281.5 13.8 280.2 15.2 Z "
                          />
                          <linearGradient
                            id="_lgradient_369"
                            x1="1.00665656008821"
                            x2="0.003376515986872164"
                            y1="0.5000025701126773"
                            y2="0.5000025701126772"
                            gradientTransform="matrix(0,-7.256,7.782,0,277.9,15.456)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_369)"
                            d=" M 277.9 15.2 C 277.9 15.2 279.1 15.6 279.4 15.4 C 279.7 15.2 284.7 10.4 285.5 10 C 286.3 9.6 284.2 8.2 284.2 8.2 C 284.2 8.2 282.1 10.3 282.1 11.3 C 282.1 11.6 282 11.8 281.8 12 C 281.2 12.7 279 14.6 279 14.6 C 279 14.6 277.9 14.9 277.9 15.2 Z "
                          />
                          <linearGradient
                            id="_lgradient_370"
                            x1="0.0930845693547937"
                            x2="0.8489244726030885"
                            y1="0.8406376067590031"
                            y2="-0.12500140869152077"
                            gradientTransform="matrix(0,-6.2,6.411,0,277.789,25.4)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_370)"
                            d=" M 277.8 19.2 C 277.8 19.2 277.7 20.2 278.1 20.5 C 278.5 20.8 280.6 22.6 280.6 22.6 C 281 23.3 281.2 24 281.4 24.8 C 281.3 25.2 281.5 25.3 281.5 25.3 L 282.8 25.4 L 284.2 24.7 L 283.5 23.8 C 283.3 23.3 282.9 22.8 282.5 22.5 C 282 22.2 278.2 19.3 277.8 19.2 Z "
                          />
                          <linearGradient
                            id="_lgradient_371"
                            x1="0.6495251049793467"
                            x2="0.4588503586308996"
                            y1="0.971328580923057"
                            y2="0.08360144042353146"
                            gradientTransform="matrix(0,-6.407,8.125,0,277.375,23.4)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_371)"
                            d=" M 277.4 17 C 277.4 17 277.3 18.3 277.5 18.5 C 277.7 18.7 283.6 22.7 284 23.3 C 284.4 23.9 285.5 21.6 285.5 21.6 C 285.5 21.6 283 20 282 20.2 C 281.7 20.3 281.4 20.2 281.2 20.1 C 280.4 19.6 278 17.9 278 17.9 C 278 17.9 277.7 16.9 277.4 17 Z "
                          />
                          <linearGradient
                            id="_lgradient_372"
                            x1="0.0623112316456087"
                            x2="1.009587545569815"
                            y1="0.2386415139535174"
                            y2="0.5023637493965165"
                            gradientTransform="matrix(0,-7.9,4.3,0,269.1,26.7)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_372)"
                            d=" M 273.4 18.8 C 273.4 18.8 272.4 19.1 272.3 19.5 C 272.2 19.9 271.3 22.6 271.3 22.6 C 270.8 23.2 270.2 23.7 269.5 24.2 C 269.1 24.3 269.1 24.5 269.1 24.5 L 269.5 25.7 L 270.7 26.7 L 271.3 25.8 C 271.7 25.4 272 24.9 272.1 24.4 C 272.7 22.6 273.1 20.7 273.4 18.8 Z "
                          />
                          <linearGradient
                            id="_lgradient_373"
                            x1="0.17922644361711448"
                            x2="0.8153164436170037"
                            y1="1.1933417800527195"
                            y2="0.7535309800526977"
                            gradientTransform="matrix(0,-9.4,3.275,0,272.057,27)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_373)"
                            d=" M 275.3 17.6 C 275.3 17.6 274.1 18 274 18.3 C 273.9 18.6 272.5 25.5 272.1 26.2 C 271.7 26.9 274.2 27 274.2 27 C 274.2 27 274.7 24.1 274.1 23.2 C 274 23 273.9 22.7 273.9 22.4 C 274 21.4 274.7 18.6 274.7 18.6 C 274.7 18.6 275.5 17.8 275.3 17.6 Z "
                          />
                          <linearGradient
                            id="_lgradient_374"
                            x1="0.11595916030498188"
                            x2="0.09084613250415588"
                            y1="5.048779882352843"
                            y2="5.963998312931452"
                            gradientTransform="matrix(0,-2.911,8.5,0,264.3,14.8)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_374)"
                            d=" M 272.8 14.6 C 272.8 14.6 272.2 13.7 271.8 13.7 C 271.4 13.7 268.5 13.5 268.5 13.5 C 267.8 13.2 267.1 12.7 266.5 12.2 C 266.3 11.8 266.1 11.9 266.1 11.9 L 265 12.6 L 264.3 14 L 265.3 14.3 C 265.8 14.6 266.3 14.7 266.8 14.8 C 267.6 14.7 272.4 14.7 272.8 14.6 Z "
                          />
                          <linearGradient
                            id="_lgradient_375"
                            x1="5.613085033275098"
                            x2="7.501886398607073"
                            y1="1.5064125683060325"
                            y2="3.1685577697980696"
                            gradientTransform="matrix(0,-2.212,9.516,0,264.884,17.2)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_375)"
                            d=" M 274.4 16.1 C 274.4 16.1 273.7 15 273.4 15 C 273.1 15 266.1 15.2 265.3 15 C 264.5 14.8 265.1 17.2 265.1 17.2 C 265.1 17.2 268 17 268.7 16.2 C 268.9 16 269.1 15.9 269.4 15.8 C 270.4 15.7 273.2 15.7 273.2 15.7 C 273.2 15.7 274.3 16.3 274.4 16.1 Z "
                          />
                          <linearGradient
                            id="_lgradient_376"
                            x1="1.0204961274999889"
                            x2="0.10288775249997428"
                            y1="0.24354682130046967"
                            y2="0.5587631963004469"
                            gradientTransform="matrix(0,-8,2.811,0,274.3,11.9)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_376)"
                            d=" M 276.6 11.9 C 276.6 11.9 277.2 11.1 277.1 10.7 C 277 10.3 276.2 7.6 276.2 7.6 C 276.3 6.8 276.4 6 276.7 5.3 C 277 5 276.8 4.8 276.8 4.8 L 275.8 4.1 L 274.3 3.9 L 274.4 5 C 274.3 5.5 274.3 6.1 274.5 6.6 C 274.7 7.1 276.4 11.6 276.6 11.9 Z "
                          />
                          <linearGradient
                            id="_lgradient_377"
                            x1="0.6742644364016712"
                            x2="0.3921073647995499"
                            y1="-0.19929049877415395"
                            y2="0.5449833451049386"
                            gradientTransform="matrix(0,-9.147,5.109,0,271.4,14)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                            <stop
                              offset="50%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(247,247,247)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(190,209,221)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_377)"
                            d=" M 275.8 14 C 275.8 14 276.6 13 276.5 12.7 C 276.4 12.4 273.7 5.9 273.6 5.1 C 273.5 4.3 271.4 5.7 271.4 5.7 C 271.4 5.7 272.6 8.4 273.6 8.8 C 273.8 8.9 274 9.1 274.2 9.3 C 274.8 10.4 275.3 11.6 275.7 12.8 C 275.7 12.8 275.5 13.9 275.8 14 Z "
                          />
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_50"
                cx="0.38537500000040836"
                cy="0.5624999999999432"
                r="1"
                fx="0.38537500000040836"
                fy="0.5624999999999432"
                gradientTransform="matrix(0,-0.8,0.8,0,278.3,16.9)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_50)"
                d=" M 278.7 16.9 C 278.479 16.9 278.3 16.721 278.3 16.5 C 278.3 16.279 278.479 16.1 278.7 16.1 C 278.921 16.1 279.1 16.279 279.1 16.5 C 279.1 16.721 278.921 16.9 278.7 16.9 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_51"
                cx="0.4478749999998968"
                cy="0.5"
                r="1"
                fx="0.4478749999998968"
                fy="0.5"
                gradientTransform="matrix(0,-0.8,0.8,0,276,18.9)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_51)"
                d=" M 276.4 18.9 C 276.179 18.9 276 18.721 276 18.5 C 276 18.279 276.179 18.1 276.4 18.1 C 276.621 18.1 276.8 18.279 276.8 18.5 C 276.8 18.721 276.621 18.9 276.4 18.9 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_52"
                cx="0.4478749999998968"
                cy="0.49999999999994316"
                r="1"
                fx="0.4478749999998968"
                fy="0.49999999999994316"
                gradientTransform="matrix(0,-0.8,0.8,0,273.1,17.4)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_52)"
                d=" M 273.5 17.4 C 273.279 17.4 273.1 17.221 273.1 17 C 273.1 16.779 273.279 16.6 273.5 16.6 C 273.721 16.6 273.9 16.779 273.9 17 C 273.9 17.221 273.721 17.4 273.5 17.4 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_53"
                cx="0.4479999999999791"
                cy="0.49999999999994316"
                r="1"
                fx="0.4479999999999791"
                fy="0.49999999999994316"
                gradientTransform="matrix(0,-0.8,0.8,0,273.9,14)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_53)"
                d=" M 274.3 14 C 274.079 14 273.9 13.821 273.9 13.6 C 273.9 13.379 274.079 13.2 274.3 13.2 C 274.521 13.2 274.7 13.379 274.7 13.6 C 274.7 13.821 274.521 14 274.3 14 Z "
              />
            </g>
            <g opacity="0.4">
              <radialGradient
                id="_rgradient_54"
                cx="0.4479999999999791"
                cy="0.49999999999994316"
                r="1"
                fx="0.4479999999999791"
                fy="0.49999999999994316"
                gradientTransform="matrix(0,-0.8,0.8,0,277.2,14)"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(247,247,247)" }}
                />
                <stop
                  offset="100%"
                  stopOpacity="1"
                  style={{ stopColor: "rgb(190,209,221)" }}
                />
              </radialGradient>
              <path
                fill="url(#_rgradient_54)"
                d=" M 277.6 14 C 277.379 14 277.2 13.821 277.2 13.6 C 277.2 13.379 277.379 13.2 277.6 13.2 C 277.821 13.2 278 13.379 278 13.6 C 278 13.821 277.821 14 277.6 14 Z "
              />
            </g>
          </g>
          <linearGradient
            id="_lgradient_378"
            x1="0.685523364486011"
            x2="0.3144766355140298"
            y1="0.010476635514020671"
            y2="0.9895233644859797"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(190,209,221)" }} />
            <stop
              offset="50%"
              stopOpacity="1"
              style={{ stopColor: "rgb(247,247,247)" }}
            />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(190,209,221)" }}
            />
          </linearGradient>
          <path
            fill="none"
            stroke="url(#_lgradient_378)"
            strokeLinecap="butt"
            strokeLinejoin="miter"
            strokeMiterlimit="10"
            strokeWidth="1"
            d=" M 276.4 26.3 C 270.495 26.3 265.7 21.505 265.7 15.6 C 265.7 9.695 270.495 4.9 276.4 4.9 C 282.305 4.9 287.1 9.695 287.1 15.6 C 287.1 21.505 282.305 26.3 276.4 26.3 Z "
            vectorEffect="non-scaling-stroke"
          />
        </g>
        <path
          className={"highlight"}
          d=" M 276.4 0 C 267.7 0 260.7 7 260.7 15.7 C 260.7 24.4 267.7 31.4 276.4 31.4 L 276.4 31.4 L 278 31.4 C 286.7 31.4 293.7 24.4 293.7 15.7 C 293.7 7 286.7 0 278 0 L 278 0 L 276.4 0 Z "
        />
      </g>
      <g
        id="roof"
        className={
          damagedParts && damagedParts[ViGenericPartName.ROOF]
            ? isFaded(damagedParts[ViGenericPartName.ROOF])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (damagedParts && damagedParts[ViGenericPartName.ROOF])
            handleHover({
              genericPartName: ViGenericPartName.ROOF,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <linearGradient
          id="_lgradient_431"
          x1="0.4999995176920575"
          x2="0.4999995176920576"
          y1="1"
          y2="4.440892098500626e-16"
          gradientTransform="matrix(0,-67.405,86.3,0,190.3,165.403)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
          <stop offset="50%" stopOpacity="1" style={{ stopColor: "rgb(210,211,215)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(191,192,197)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_431)"
          d=" M 272.9 132.1 C 272.9 154.3 276.5 165.2 276.5 165.2 C 262.6 163.5 248.7 162.7 234.7 162.8 C 221.5 162.8 208.4 163.6 195.3 165.4 C 193.2 165.7 190.3 144.8 190.3 132 C 190.3 118.2 194 97.8 196.1 98 C 209.6 99.5 223.1 100.3 236.6 100.4 C 258.5 100.6 276.6 98.9 276.6 98.9 C 276.6 98.9 273 110 272.9 132.1 Z "
        />
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_sRDIgUq4EZ9BRbPunVWc5ViZYY5Vwlif">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 272.9 132.1 C 272.9 154.3 276.5 165.2 276.5 165.2 C 262.6 163.5 248.7 162.7 234.7 162.8 C 221.5 162.8 208.4 163.6 195.3 165.4 C 193.2 165.7 190.3 144.8 190.3 132 C 190.3 118.2 194 97.8 196.1 98 C 209.6 99.5 223.1 100.3 236.6 100.4 C 258.5 100.6 276.6 98.9 276.6 98.9 C 276.6 98.9 273 110 272.9 132.1 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_sRDIgUq4EZ9BRbPunVWc5ViZYY5Vwlif)">
                  <g>
                    <g opacity="0.5">
                      <linearGradient
                        id="_lgradient_432"
                        x1="0.8486736074678589"
                        x2="-0.11943750527921004"
                        y1="0.5000002047779848"
                        y2="0.5000002047779847"
                        gradientTransform="matrix(0,-27.104,94.657,0,186.1,173.204)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="0.85"
                          style={{ stopColor: "rgb(255,255,255)" }}
                        />
                        <stop
                          offset="50%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(210,211,215)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(191,192,197)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_432)"
                        d=" M 279.5 146.1 C 279.5 146.1 267.2 153.1 232.9 153.1 C 210.2 153.1 190.9 150.5 186.1 147.5 L 188.8 166.6 C 188.8 166.6 202.4 173.4 236.2 173.2 C 246.3 173.1 273.1 172.4 277.3 164.8 C 281.5 157.2 281.4 147 279.5 146.1 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 272.9 132.1 C 272.9 154.3 276.5 165.2 276.5 165.2 C 262.6 163.5 248.7 162.7 234.7 162.8 C 221.5 162.8 208.4 163.6 195.3 165.4 C 193.2 165.7 190.3 144.8 190.3 132 C 190.3 118.2 194 97.8 196.1 98 C 209.6 99.5 223.1 100.3 236.6 100.4 C 258.5 100.6 276.6 98.9 276.6 98.9 C 276.6 98.9 273 110 272.9 132.1 Z "
        />
      </g>
      <g
        id="leftwing"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.WING] &&
          damagedParts[ViGenericPartName.WING][ViSide.LEFT]
            ? isFaded(damagedParts[ViGenericPartName.WING][ViSide.LEFT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.WING] &&
            damagedParts[ViGenericPartName.WING][ViSide.LEFT]
          )
            handleHover({
              genericPartName: ViGenericPartName.WING,
              side: ViSide.LEFT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <linearGradient
          id="_lgradient_457"
          x1="0.5"
          x2="0.49999999999999983"
          y1="0"
          y2="1"
          gradientTransform="matrix(0,57.9,44.3,0,243.9,177.5)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
          <stop offset="50%" stopOpacity="1" style={{ stopColor: "rgb(210,211,215)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(191,192,197)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_457)"
          d=" M 246.6 235.4 C 247.5 223 247.3 210.5 246.1 198.1 L 282.8 196.1 C 283.6 196.2 279.7 185.4 277 183.9 C 275.3 183 248.8 179.9 244.2 179.5 L 243.9 177.5 C 269.5 177.5 280.5 181.5 281.1 182 C 286 185.9 288.2 205.6 288.2 205.6 L 286.3 211.5 L 282.8 212 L 282.8 212 C 273.8 205.8 261.5 208.1 255.3 217 C 253 220.3 251.8 224.2 251.8 228.3 C 251.8 230.6 252.2 233 253 235.2 C 248 235.4 246.6 235.4 246.6 235.4 Z "
        />
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_FYbqccO7mWhVEKfbSuawZljkt8DVeoLX">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 246.6 235.5 C 247.5 223.1 247.3 210.6 246.1 198.2 L 282.8 196.2 C 283.6 196.3 279.7 185.5 277 184 C 275.3 183.1 248.8 180 244.2 179.6 L 243.9 177.6 C 269.5 177.6 280.5 181.6 281.1 182.1 C 286 186 288.2 205.7 288.2 205.7 L 286.3 211.6 L 282.8 212.1 L 282.8 212.1 C 273.8 205.9 261.5 208.2 255.3 217.1 C 253 220.4 251.8 224.3 251.8 228.4 C 251.8 230.7 252.2 233.1 253 235.3 C 248 235.5 246.6 235.5 246.6 235.5 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_FYbqccO7mWhVEKfbSuawZljkt8DVeoLX)">
                  <g>
                    <g>
                      <g opacity="0.7">
                        <linearGradient
                          id="_lgradient_458"
                          x1="0.6141693629648337"
                          x2="-0.0005047581816240987"
                          y1="0.49959376853240256"
                          y2="0.49776117133849357"
                          gradientTransform="matrix(0,21.881,78.841,0,241.359,200)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="0"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_458)"
                          d=" M 319.5 200 C 318.6 200 245 203.7 245 203.7 C 245 203.7 237.6 220.7 243.9 221.5 C 243.9 221.5 251.1 222.8 255.3 220.6 C 259.3 215.1 265.8 211.8 272.6 211.8 C 278.6 211.8 284.3 214.3 288.3 218.7 C 288.9 218.7 289.4 218.7 290 218.6 C 295.8 218.2 320.2 217 320.2 217 L 319.5 200 Z "
                        />
                      </g>
                      <g opacity="0.2">
                        <linearGradient
                          id="_lgradient_459"
                          x1="1.4940743801652623"
                          x2="0.04615667536171242"
                          y1="0.5199360000000262"
                          y2="0.4926553131075697"
                          gradientTransform="matrix(0,12.1,12.5,0,244.9,227)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(86,86,89)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(98,98,100)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_459)"
                          d=" M 255.9 227 L 244.9 227 L 245.4 237.8 C 245.4 237.8 254.3 239.1 254.7 239.1 C 255.1 239.1 257.4 236.7 257.4 236.7 L 255.9 227 Z "
                        />
                      </g>
                      <g opacity="0.7">
                        <linearGradient
                          id="_lgradient_460"
                          x1="0.6097692211967018"
                          x2="-0.0101860165036316"
                          y1="0.5000768034389158"
                          y2="0.49780904264853953"
                          gradientTransform="matrix(0,26.502,83.94,0,240.16,175.598)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="0"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_460)"
                          d=" M 243.6 202.1 C 257.1 200.9 324.1 198.3 324.1 198.3 C 324.1 198.3 308.4 177.4 303.9 176.9 C 302.1 176.7 254.3 175.7 250.2 175.6 C 243.9 175.5 239.7 179.7 240.2 182.2 C 241.3 187.4 243.6 202.1 243.6 202.1 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g>
          <g opacity="0.7">
            <linearGradient
              id="_lgradient_461"
              x1="0.971020134228187"
              x2="-0.055821049026136604"
              y1="0.5007343843718113"
              y2="0.4973854056107415"
              gradientTransform="matrix(0,14.9,34.701,0,244.5,181.5)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(10,10,10)" }} />
              <stop
                offset="100%"
                stopOpacity="0.5"
                style={{ stopColor: "rgb(64,64,64)" }}
              />
            </linearGradient>
            <path
              fill="url(#_lgradient_461)"
              d=" M 244.5 181.5 C 246.1 181.6 273.2 184.7 274.6 185.5 C 276.7 186.7 279.8 195.1 279.1 195 L 245.9 196.4 C 245.9 196.4 245.9 195 245.3 189 C 244.9 185 244.6 181.9 244.5 181.5 Z "
            />
          </g>
          <linearGradient
            id="_lgradient_462"
            x1="0.9698279569892474"
            x2="-0.05221113865535387"
            y1="0.5018796566279882"
            y2="0.49865386253337085"
            gradientTransform="matrix(0,18.6,38.706,0,244.2,179.6)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_462)"
            d=" M 245.8 196.4 L 279 195 C 279.7 195.1 276.6 186.7 274.5 185.5 C 273.3 184.8 253.1 182.5 244.4 181.5 L 244.2 179.6 C 246.3 179.7 275.2 183 277 184 C 279.7 185.5 283.6 196.3 282.8 196.2 L 246 198.2 L 245.8 196.4 Z "
          />
        </g>
        <path
          className={"highlight"}
          d=" M 246.6 235.4 C 247.5 223 247.3 210.5 246.1 198.1 L 243.9 177.5 C 269.5 177.5 280.5 181.5 281.1 182 C 286 185.9 288.2 205.6 288.2 205.6 L 286.3 211.5 L 282.8 212 L 282.8 212 C 273.8 205.8 261.5 208.1 255.3 217 C 253 220.3 251.8 224.2 251.8 228.3 C 251.8 230.6 252.2 233 253 235.2 C 248 235.4 246.6 235.4 246.6 235.4 Z "
        />
      </g>
      <g
        id="rightwing"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.WING] &&
          damagedParts[ViGenericPartName.WING][ViSide.RIGHT]
            ? isFaded(damagedParts[ViGenericPartName.WING][ViSide.RIGHT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.WING] &&
            damagedParts[ViGenericPartName.WING][ViSide.RIGHT]
          )
            handleHover({
              genericPartName: ViGenericPartName.WING,
              side: ViSide.RIGHT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <linearGradient
          id="_lgradient_451"
          x1="0.5000000000000004"
          x2="0.5000000000000002"
          y1="0"
          y2="1"
          gradientTransform="matrix(0,-57.9,44.3,0,243.9,86.5)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
          <stop offset="50%" stopOpacity="1" style={{ stopColor: "rgb(210,211,215)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(191,192,197)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_451)"
          d=" M 246.6 28.6 C 247.5 41 247.3 53.5 246.1 65.9 L 282.8 67.9 C 283.6 67.8 279.7 78.6 277 80.1 C 275.3 81 248.8 84.1 244.2 84.5 L 243.9 86.5 C 269.5 86.5 280.5 82.5 281.1 82 C 286 78.1 288.2 58.4 288.2 58.4 L 286.3 52.5 L 282.8 52 L 282.8 52 C 273.8 58.2 261.5 55.9 255.3 47 C 253 43.7 251.8 39.8 251.8 35.7 C 251.8 33.4 252.2 31 253 28.8 C 248 28.6 246.6 28.6 246.6 28.6 Z "
        />
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_yTsG7y7FeGweCgT2Aj5aKeZ7bpFCyyM6">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 246.6 28.5 C 247.5 40.9 247.3 53.4 246.1 65.8 L 282.8 67.8 C 283.6 67.7 279.7 78.5 277 80 C 275.3 80.9 248.8 84 244.2 84.4 L 243.9 86.4 C 269.5 86.4 280.5 82.4 281.1 81.9 C 286 78 288.2 58.3 288.2 58.3 L 286.3 52.4 L 282.8 51.9 L 282.8 51.9 C 273.8 58.1 261.5 55.8 255.3 46.9 C 253 43.6 251.8 39.7 251.8 35.6 C 251.8 33.3 252.2 30.9 253 28.7 C 248 28.5 246.6 28.5 246.6 28.5 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_yTsG7y7FeGweCgT2Aj5aKeZ7bpFCyyM6)">
                  <g>
                    <g>
                      <g opacity="0.7">
                        <linearGradient
                          id="_lgradient_452"
                          x1="0.6141693629648337"
                          x2="-0.0005047581816240987"
                          y1="0.49959376853240256"
                          y2="0.49776117133849357"
                          gradientTransform="matrix(0,-21.881,78.841,0,241.359,64)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="0"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_452)"
                          d=" M 319.5 64 C 318.6 64 245 60.3 245 60.3 C 245 60.3 237.6 43.3 243.9 42.5 C 243.9 42.5 251.1 41.2 255.3 43.4 C 259.3 48.9 265.8 52.2 272.6 52.2 C 278.6 52.2 284.3 49.7 288.3 45.3 C 288.9 45.3 289.4 45.3 290 45.4 C 295.8 45.8 320.2 47 320.2 47 L 319.5 64 Z "
                        />
                      </g>
                      <g opacity="0.2">
                        <linearGradient
                          id="_lgradient_453"
                          x1="1.4940743801652623"
                          x2="0.04615667536171242"
                          y1="0.5199360000000262"
                          y2="0.4926553131075697"
                          gradientTransform="matrix(0,-12.1,12.5,0,244.9,37)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(86,86,89)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(98,98,100)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_453)"
                          d=" M 255.9 37 L 244.9 37 L 245.4 26.2 C 245.4 26.2 254.3 24.9 254.7 24.9 C 255.1 24.9 257.4 27.3 257.4 27.3 L 255.9 37 Z "
                        />
                      </g>
                      <g opacity="0.7">
                        <linearGradient
                          id="_lgradient_454"
                          x1="0.6097692211967018"
                          x2="-0.0101860165036316"
                          y1="0.5000768034389158"
                          y2="0.49780904264853953"
                          gradientTransform="matrix(0,-26.502,83.94,0,240.16,88.402)"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0%"
                            stopOpacity="0"
                            style={{ stopColor: "rgb(218,218,220)" }}
                          />
                          <stop
                            offset="100%"
                            stopOpacity="1"
                            style={{ stopColor: "rgb(255,255,255)" }}
                          />
                        </linearGradient>
                        <path
                          fill="url(#_lgradient_454)"
                          d=" M 243.6 61.9 C 257.1 63.1 324.1 65.7 324.1 65.7 C 324.1 65.7 308.4 86.6 303.9 87.1 C 302.1 87.3 254.3 88.3 250.2 88.4 C 243.9 88.5 239.7 84.3 240.2 81.8 C 241.3 76.6 243.6 61.9 243.6 61.9 Z "
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g>
          <g opacity="0.7">
            <linearGradient
              id="_lgradient_455"
              x1="0.971020134228187"
              x2="-0.055821049026136604"
              y1="0.5007343843718113"
              y2="0.4973854056107415"
              gradientTransform="matrix(0,-14.9,34.701,0,244.5,82.5)"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(10,10,10)" }} />
              <stop
                offset="100%"
                stopOpacity="0.5"
                style={{ stopColor: "rgb(64,64,64)" }}
              />
            </linearGradient>
            <path
              fill="url(#_lgradient_455)"
              d=" M 244.5 82.5 C 246.1 82.4 273.2 79.3 274.6 78.5 C 276.7 77.3 279.8 68.9 279.1 69 L 245.9 67.6 C 245.9 67.6 245.9 69 245.3 75 C 244.9 79 244.6 82.1 244.5 82.5 Z "
            />
          </g>
          <linearGradient
            id="_lgradient_456"
            x1="0.9698279569892474"
            x2="-0.05221113865535387"
            y1="0.5018796566279882"
            y2="0.49865386253337085"
            gradientTransform="matrix(0,-18.6,38.706,0,244.2,84.4)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(32,32,32)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(36,36,36)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_456)"
            d=" M 245.8 67.6 L 279 69 C 279.7 68.9 276.6 77.3 274.5 78.5 C 273.3 79.2 253.1 81.5 244.4 82.5 L 244.2 84.4 C 246.3 84.3 275.2 81 277 80 C 279.7 78.5 283.6 67.7 282.8 67.8 L 246 65.8 L 245.8 67.6 Z "
          />
        </g>
        <path
          className={"highlight"}
          d=" M 246.6 28.6 C 247.5 41 247.3 53.5 246.1 65.9 L 243.9 86.5 C 269.5 86.5 280.5 82.5 281.1 82 C 286 78.1 288.2 58.4 288.2 58.4 L 286.3 52.5 L 282.8 52 L 282.8 52 C 273.8 58.2 261.5 55.9 255.3 47 C 253 43.7 251.8 39.8 251.8 35.7 C 251.8 33.4 252.2 31 253 28.8 C 248 28.6 246.6 28.6 246.6 28.6 Z "
        ></path>
      </g>
      <g
        id="tailgate"
        className={
          damagedParts && damagedParts[ViGenericPartName.TAILGATE]
            ? isFaded(damagedParts[ViGenericPartName.TAILGATE])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (damagedParts && damagedParts[ViGenericPartName.TAILGATE])
            handleHover({
              genericPartName: ViGenericPartName.TAILGATE,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <linearGradient
          id="_lgradient_463"
          x1="0.5000000000000002"
          x2="0.5"
          y1="0"
          y2="1"
          gradientTransform="matrix(0,-90.6,68.2,0,282.9,177)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
          <stop offset="50%" stopOpacity="1" style={{ stopColor: "rgb(210,211,215)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(191,192,197)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_463)"
          d=" M 314.5 87.1 C 314.6 89 314.4 90.8 313.9 92.6 C 313.9 92.6 288.4 95.2 285.9 99.3 C 281.9 103.6 281.9 159.8 285.9 164 C 288.3 168.1 313.9 170.7 313.9 170.7 C 314.4 172.5 314.6 174.3 314.5 176.2 C 317 176.5 351.1 177 351.1 177 L 351.1 86.4 C 351.1 86.4 316.9 86.8 314.5 87.1 Z  M 310.4 100.3 C 311.3 100.3 312.1 101 312.1 101.9 L 312.1 161.5 C 312.1 161.5 312 163.3 310.4 163.3 C 307.9 163.6 295.5 161.6 290.8 159.7 C 289.7 159.2 288.5 158.5 288.2 154.7 C 287.7 148.8 286.9 141.1 286.9 130.9 C 286.9 123 287.1 114.2 288.2 108.1 C 288.7 104.8 289.6 104.2 290.8 103.8 C 295.5 102.1 307.9 100.1 310.4 100.3 Z "
        />
        <g>
          <g>
            <g>
              <clipPath id="_clipPath_Lpl0LspXXrQRyMxzRiRRvP14aVw39FKs">
                <path
                  fill="rgb(255,255,255)"
                  d=" M 314.5 87.1 C 314.6 89 314.4 90.8 313.9 92.6 C 313.9 92.6 288.4 95.2 285.9 99.3 C 281.9 103.6 281.9 159.8 285.9 164 C 288.3 168.1 313.9 170.7 313.9 170.7 C 314.4 172.5 314.6 174.3 314.5 176.2 C 317 176.5 351.1 177 351.1 177 L 351.1 86.4 C 351.1 86.4 316.9 86.8 314.5 87.1 Z "
                />
              </clipPath>
              <g clipPath="url(#_clipPath_Lpl0LspXXrQRyMxzRiRRvP14aVw39FKs)">
                <g>
                  <g>
                    <g opacity="0.9">
                      <linearGradient
                        id="_lgradient_464"
                        x1="0.49999904397705885"
                        x2="0.4999990439770589"
                        y1="1.0013059591812095"
                        y2="0.0781739207778962"
                        gradientTransform="matrix(0,-104.6,14.711,0,339.9,183.5)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="32%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(155,155,158)" }}
                        />
                        <stop
                          offset="83%"
                          stopOpacity="0.4"
                          style={{ stopColor: "rgb(139,139,141)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(139,139,141)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_464)"
                        d=" M 340.4 78.9 C 340.4 78.9 352.1 82.4 353 88.8 C 354.1 96.9 354.6 131.1 354.6 131.2 C 354.7 138.7 354.1 167.2 353 173.4 C 351.7 180.7 340.2 183.5 340.2 183.5 L 339.9 183.3 C 341.3 172.9 341.7 156.3 341.6 131.3 C 341.5 106.3 341.3 83.6 340.4 78.9 Z "
                      />
                    </g>
                    <linearGradient
                      id="_lgradient_465"
                      x1="0.9999982238010636"
                      x2="0.0000017761989361542163"
                      y1="0.5000039370078966"
                      y2="0.5000039370078965"
                      gradientTransform="matrix(0,-112.6,25.4,0,316.9,179.4)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(255,255,255)" }}
                      />
                      <stop
                        offset="70%"
                        stopOpacity="0"
                        style={{ stopColor: "rgb(219,220,224)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="0"
                        style={{ stopColor: "rgb(219,220,224)" }}
                      />
                    </linearGradient>
                    <path
                      fill="url(#_lgradient_465)"
                      d=" M 316.9 178.2 C 317.2 178.2 340.7 177.6 342.3 179.4 C 341.6 149 341.7 73.8 341.7 73.8 L 321.5 66.8 L 317.8 79 L 316.9 178.2 Z "
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <linearGradient
          id="_lgradient_466"
          x1="-0.0028412698412694226"
          x2="0.9971587301587306"
          y1="0.5000000000000071"
          y2="0.5000000000000071"
          gradientTransform="matrix(0,-25.2,6.3,0,326.6,142.6)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(145,147,152)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(191,192,197)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_466)"
          d=" M 332.9 136.1 C 332.9 141.9 326.6 142.6 326.6 142.6 L 326.6 117.4 C 326.6 117.4 332.9 117.7 332.9 123.3 L 332.9 136.1 Z "
        />
        <linearGradient
          id="_lgradient_467"
          x1="0.5000016141119534"
          x2="0.5000016141119532"
          y1="0.005714285714276457"
          y2="1.0142857142856911"
          gradientTransform="matrix(0,-27.105,3.5,0,323.1,143.605)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(191,192,197)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_467)"
          d=" M 323.1 130.1 L 323.1 121 C 323.1 116.5 326.6 116.5 326.6 116.5 L 326.6 143.6 C 326.6 143.6 323.1 143.9 323.1 139.7 L 323.1 130.1 Z "
        />
        <path
          className={"highlight"}
          d=" M 314.5 87.1 C 314.6 89 314.4 90.8 313.9 92.6 L 313.9 170.7 C 314.4 172.5 314.6 174.3 314.5 176.2 C 317 176.5 351.1 177 351.1 177 L 351.1 86.4 C 351.1 86.4 316.9 86.8 314.5 87.1 Z "
        ></path>
      </g>
      <g
        id="rear_windscreen"
        className={
          damagedParts && damagedParts[ViGenericPartName.REAR_WINDSCREEN]
            ? isFaded(damagedParts[ViGenericPartName.REAR_WINDSCREEN])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (damagedParts && damagedParts[ViGenericPartName.REAR_WINDSCREEN])
            handleHover({
              genericPartName: ViGenericPartName.REAR_WINDSCREEN,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g opacity="0.7">
          <linearGradient
            id="_lgradient_468"
            x1="-7.922430855789031e-7"
            x2="1.000000305419008"
            y1="0.5"
            y2="0.5"
            gradientTransform="matrix(0,-63.044,25.2,0,286.9,163.23)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(10,10,10)" }} />
            <stop
              offset="100%"
              stopOpacity="0.5"
              style={{ stopColor: "rgb(64,64,64)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_468)"
            d=" M 290.8 103.7 C 289.6 104.1 288.7 104.7 288.2 108 C 287.1 114.1 286.9 122.9 286.9 130.8 C 286.9 141 287.7 148.7 288.2 154.6 C 288.5 158.4 289.7 159.1 290.8 159.6 C 295.5 161.5 307.9 163.5 310.4 163.2 C 312 163.2 312.1 161.4 312.1 161.4 L 312.1 101.8 C 312.1 100.9 311.3 100.2 310.4 100.2 C 307.9 100 295.5 102 290.8 103.7 Z "
          />
        </g>
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_lXgOjkwFVA6DiXhU2aol0U3Kosu8nApZ">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 289.4 99.6 C 288 100.1 287 100.8 286.4 104.5 C 285.1 111.5 284.9 121.6 284.9 130.6 C 284.9 142.3 285.8 151.1 286.4 158 C 286.8 162.3 288.1 163.2 289.4 163.7 C 294.8 165.9 309 168.2 311.9 167.8 C 313.7 167.8 313.9 165.7 313.9 165.7 L 313.9 97.4 C 313.8 96.4 312.9 95.6 311.9 95.6 C 309 95.3 294.8 97.7 289.4 99.6 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_lXgOjkwFVA6DiXhU2aol0U3Kosu8nApZ)">
                  <g>
                    <g opacity="0.7">
                      <linearGradient
                        id="_lgradient_469"
                        x1="1.0000008930002573"
                        x2="-0.0000021726432506419258"
                        y1="0.5"
                        y2="0.4999999999999999"
                        gradientTransform="matrix(0,-82.19,71.4,0,242.5,218.772)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(226,226,218)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(79,85,86)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_469)"
                        d=" M 242.5 147 C 242.5 147 243.5 194 253 212.4 C 262.5 230.8 313.9 203.5 313.9 203.5 L 313.9 143.8 C 313.9 143.8 282.3 125.6 242.5 147 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_RGvlqYEzf0dqmZRaG4p1XQSDoBMSzHrL">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 289.4 99.6 C 288 100.1 287 100.8 286.4 104.5 C 285.1 111.5 284.9 121.6 284.9 130.6 C 284.9 142.3 285.8 151.1 286.4 158 C 286.8 162.3 288.1 163.2 289.4 163.7 C 294.8 165.9 309 168.2 311.9 167.8 C 313.7 167.8 313.9 165.7 313.9 165.7 L 313.9 97.4 C 313.8 96.4 312.9 95.6 311.9 95.6 C 309 95.3 294.8 97.7 289.4 99.6 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_RGvlqYEzf0dqmZRaG4p1XQSDoBMSzHrL)">
                  <g>
                    <linearGradient
                      id="_lgradient_470"
                      x1="-6.422168510322734e-7"
                      x2="1.000000308187529"
                      y1="0.5"
                      y2="0.5"
                      gradientTransform="matrix(0,-72.271,29,0,285,167.846)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(32,32,32)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(36,36,36)" }}
                      />
                    </linearGradient>
                    <path
                      fill="url(#_lgradient_470)"
                      d=" M 289.5 99.6 C 288.1 100.1 287.1 100.8 286.5 104.5 C 285.2 111.5 285 121.6 285 130.6 C 285 142.3 285.9 151.1 286.5 158 C 286.9 162.3 288.2 163.2 289.5 163.7 C 294.9 165.9 309.1 168.2 312 167.8 C 313.8 167.8 314 165.7 314 165.7 L 314 97.4 C 313.9 96.4 313 95.6 312 95.6 C 309.1 95.3 295 97.7 289.5 99.6 Z  M 310.4 100.3 C 311.3 100.3 312.1 101 312.1 101.9 L 312.1 161.5 C 312.1 161.5 312 163.4 310.4 163.3 C 307.9 163.6 295.5 161.6 290.8 159.7 C 289.7 159.2 288.5 158.4 288.2 154.7 C 287.7 148.8 286.9 141.1 286.9 130.9 C 286.9 123 287.1 114.2 288.2 108.1 C 288.8 104.8 289.7 104.2 290.8 103.8 C 295.5 102 307.9 100 310.4 100.3 L 310.4 100.3 Z "
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 289.5 99.6 C 288.1 100.1 287.1 100.8 286.5 104.5 C 285.2 111.5 285 121.6 285 130.6 C 285 142.3 285.9 151.1 286.5 158 C 286.9 162.3 288.2 163.2 289.5 163.7 C 294.9 165.9 309.1 168.2 312 167.8 C 313.8 167.8 314 165.7 314 165.7 L 314 97.4 C 313.9 96.4 313 95.6 312 95.6 C 309.1 95.3 295 97.7 289.5 99.6 Z "
        />
      </g>
      <g
        id="grille"
        className={
          damagedParts && damagedParts[ViGenericPartName.GRILLE]
            ? isFaded(damagedParts[ViGenericPartName.GRILLE])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (damagedParts && damagedParts[ViGenericPartName.GRILLE])
            handleHover({
              genericPartName: ViGenericPartName.GRILLE,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g opacity="0.7">
          <linearGradient
            id="_lgradient_471"
            x1="-0.0029876200819838328"
            x2="0.9999931426968436"
            y1="0.5000000000000009"
            y2="0.5000000000000009"
            gradientTransform="matrix(0,-72.953,16.3,0,30.2,168.164)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(10,10,10)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(64,64,64)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_471)"
            d=" M 42.1 95.3 C 40.2 95.9 37 96.8 35 97.3 C 33 97.8 31.3 101.3 31.2 104.9 L 30.3 125.1 C 30.2 126.9 30.2 129.3 30.2 131.7 C 30.2 134.1 30.2 136.5 30.3 138.3 L 31.2 158.5 C 31.4 162.1 33.1 165.6 35 166.1 C 36.9 166.6 40.1 167.6 42.1 168.1 C 44.1 168.6 45.7 166.1 45.8 162.5 L 46.4 138.3 C 46.4 136.5 46.5 134.1 46.5 131.7 C 46.5 129.3 46.5 126.9 46.4 125.1 L 45.8 100.9 C 45.7 97.2 44 94.7 42.1 95.3 Z "
          />
        </g>
        <linearGradient
          id="_lgradient_472"
          x1="-0.0029876200819838328"
          x2="0.9999931426968436"
          y1="0.5000000000000009"
          y2="0.5000000000000009"
          gradientTransform="matrix(0,-72.953,16.3,0,30.2,168.164)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(191,192,197)" }} />
          <stop offset="50%" stopOpacity="1" style={{ stopColor: "rgb(210,211,215)" }} />
          <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_472)"
          d=" M 42.1 95.3 C 40.2 95.9 37 96.8 35 97.3 C 33 97.8 31.3 101.3 31.2 104.9 L 30.3 125.1 C 30.2 126.9 30.2 129.3 30.2 131.7 C 30.2 134.1 30.2 136.5 30.3 138.3 L 31.2 158.5 C 31.4 162.1 33.1 165.6 35 166.1 C 36.9 166.6 40.1 167.6 42.1 168.1 C 44.1 168.6 45.7 166.1 45.8 162.5 L 46.4 138.3 C 46.4 136.5 46.5 134.1 46.5 131.7 C 46.5 129.3 46.5 126.9 46.4 125.1 L 45.8 100.9 C 45.7 97.2 44 94.7 42.1 95.3 Z  M 43.7 133.6 C 43.7 135.4 44.7 137.1 44.7 138.4 L 44.2 160.4 C 44.1 164.1 43.1 166.7 41.6 166.2 C 41.4 166.2 41.3 166.1 41.1 166.1 L 41.4 133.5 L 43.7 133.6 Z  M 39.9 133.4 L 40 165.7 C 39 165.4 37.9 165.2 36.9 164.9 L 36.7 133.3 L 39.9 133.4 Z  M 35.2 133.2 L 35.7 164.5 L 35.6 164.5 C 34.1 164 32.7 160.2 32.6 156.6 L 31.9 138.4 C 31.8 136.8 31.8 135.2 31.8 133.1 L 35.2 133.2 Z  M 31.9 130.5 C 31.9 128.4 31.9 126.8 32 125.2 L 32.7 107 C 32.8 103.4 34.2 99.6 35.7 99.1 L 35.8 99.1 L 35.3 130.4 L 31.9 130.5 Z  M 36.8 130.3 L 37 98.7 C 38 98.4 39.1 98.2 40.1 97.9 L 40 130.2 L 36.8 130.3 Z  M 44.2 103.1 L 44.7 125.1 C 44.7 126.5 43.7 128.2 43.7 129.9 L 41.4 130 L 41.1 97.4 C 41.3 97.4 41.4 97.3 41.6 97.3 C 43.2 96.8 44.1 99.5 44.2 103.1 L 44.2 103.1 Z "
        />
        <linearGradient
          id="_lgradient_473"
          x1="0.9988591376583704"
          x2="-0.00460608061590051"
          y1="0.5"
          y2="0.4999999999999999"
          gradientTransform="matrix(0,-36.464,16.3,0,30.2,168.264)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(255,255,255)" }} />
          <stop offset="100%" stopOpacity="0" style={{ stopColor: "rgb(255,255,255)" }} />
        </linearGradient>
        <path
          fill="url(#_lgradient_473)"
          d=" M 46.5 131.8 L 30.2 131.8 C 30.2 134.2 30.2 136.6 30.3 138.4 L 31.2 158.6 C 31.4 162.2 33.1 165.7 35 166.2 C 36.9 166.7 40.1 167.7 42.1 168.2 C 44.1 168.7 45.7 166.2 45.8 162.6 L 46.4 138.4 C 46.5 136.6 46.5 134.2 46.5 131.8 Z  M 41.6 166.2 C 41.4 166.2 41.3 166.1 41.1 166.1 L 41.4 133.5 L 43.7 133.6 C 43.7 135.4 44.7 137.1 44.7 138.4 L 44.2 160.4 C 44.1 164.1 43.2 166.7 41.6 166.2 Z  M 36.9 164.9 L 36.7 133.3 L 39.9 133.4 L 40 165.7 C 39 165.4 37.9 165.2 36.9 164.9 Z  M 31.9 133.1 L 35.3 133.2 L 35.8 164.5 L 35.7 164.5 C 34.2 164 32.8 160.2 32.7 156.6 L 32 138.4 C 31.9 136.8 31.9 135.2 31.9 133.1 Z "
        />
        <path
          className={"highlight"}
          d=" M 42.1 95.3 C 40.2 95.9 37 96.8 35 97.3 C 33 97.8 31.3 101.3 31.2 104.9 L 30.3 125.1 C 30.2 126.9 30.2 129.3 30.2 131.7 C 30.2 134.1 30.2 136.5 30.3 138.3 L 31.2 158.5 C 31.4 162.1 33.1 165.6 35 166.1 C 36.9 166.6 40.1 167.6 42.1 168.1 C 44.1 168.6 45.7 166.1 45.8 162.5 L 46.4 138.3 C 46.4 136.5 46.5 134.1 46.5 131.7 C 46.5 129.3 46.5 126.9 46.4 125.1 L 45.8 100.9 C 45.7 97.2 44 94.7 42.1 95.3 Z "
        />
      </g>
      <g
        id="leftfront_fender"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.FRONT_FENDER] &&
          damagedParts[ViGenericPartName.FRONT_FENDER][ViSide.LEFT]
            ? isFaded(damagedParts[ViGenericPartName.FRONT_FENDER][ViSide.LEFT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.FRONT_FENDER] &&
            damagedParts[ViGenericPartName.FRONT_FENDER][ViSide.LEFT]
          )
            handleHover({
              genericPartName: ViGenericPartName.FRONT_FENDER,
              side: ViSide.LEFT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g>
          <linearGradient
            id="_lgradient_477"
            x1="0.9989025349415901"
            x2="-0.008338232617093988"
            y1="0.5000026787126748"
            y2="0.5000026787126747"
            gradientTransform="matrix(0,-37.906,48.002,0,50.9,230.906)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
            <stop
              offset="50%"
              stopOpacity="1"
              style={{ stopColor: "rgb(210,211,215)" }}
            />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(191,192,197)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_477)"
            d=" M 95.8 196.3 L 91.9 193 C 88.3 194.7 87.9 194.5 86 194.2 C 83.3 196.6 70.5 197.5 66.5 197.7 C 61.2 198.1 55.9 199.2 50.9 201.1 C 51.3 201.2 52.3 202.2 53 202 C 64.6 199.2 66.7 200.8 66.6 201.2 C 66.2 202.8 61.8 205 60.6 205.6 C 60.6 205.6 55.4 207.1 53.3 208.6 C 51.9 209.6 52.7 210.8 52.1 211.7 L 57.6 225.1 L 57.6 225.1 C 58 216.6 65.7 209.9 75.2 209.9 C 84.7 209.9 93.5 216.9 93.9 225.3 L 93.9 230.2 C 93.9 230.6 94.3 230.9 94.7 230.9 L 98.9 230.9 C 99 231.2 94.2 219.3 95.8 196.3 Z "
          />
          <g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_BKJmRhmcMO6TsE2p98B6XCyYGdr0R27a">
                    <path
                      fill="rgb(255,255,255)"
                      d=" M 95.8 196.3 L 91.9 193 C 88.3 194.7 87.9 194.5 86 194.2 C 83.3 196.6 70.5 197.5 66.5 197.7 C 61.2 198.1 55.9 199.2 50.9 201.1 C 51.3 201.2 52.3 202.2 53 202 C 64.6 199.2 66.7 200.8 66.6 201.2 C 66.2 202.8 61.8 205 60.6 205.6 C 60.6 205.6 55.4 207.1 53.3 208.6 C 51.9 209.6 52.7 210.8 52.1 211.7 L 57.6 225.1 L 57.6 225.1 C 58 216.6 65.7 209.9 75.2 209.9 C 84.7 209.9 93.5 216.9 93.9 225.3 L 93.9 230.2 C 93.9 230.6 94.3 230.9 94.7 230.9 L 98.9 230.9 C 99 231.2 94.3 219.6 95.8 196.3 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_BKJmRhmcMO6TsE2p98B6XCyYGdr0R27a)">
                    <g>
                      <g>
                        <g opacity="0.5">
                          <linearGradient
                            id="_lgradient_478"
                            x1="0.6960126582278665"
                            x2="-0.6009524184687569"
                            y1="0.4973561643835588"
                            y2="0.5656174842096935"
                            gradientTransform="matrix(0,-7.9,65.7,0,38.8,232.1)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="0"
                              style={{ stopColor: "rgb(98,98,100)" }}
                            />
                            <stop
                              offset="24%"
                              stopOpacity="0.5"
                              style={{ stopColor: "rgb(86,86,89)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(86,86,89)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_478)"
                            d=" M 38.8 224.2 C 42.8 229.4 99.6 226.5 99.6 226.5 L 104.5 231.7 L 49.7 232.1 L 38.8 228.1 L 38.8 224.2 Z "
                          />
                        </g>
                        <g opacity="0.8">
                          <linearGradient
                            id="_lgradient_479"
                            x1="0.4453821138211618"
                            x2="0.9477621945636778"
                            y1="0.4999737260101491"
                            y2="0.49104010397954057"
                            gradientTransform="matrix(0,-12.3,53.359,0,49.2,211.4)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="0"
                              style={{ stopColor: "rgb(218,218,220)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(255,255,255)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_479)"
                            d=" M 51 211.4 C 51 211.4 58.8 204.7 86.8 204.5 C 109.5 203.5 101 204.7 101 204.7 L 101 199.1 C 101 199.1 70.2 200.5 64.3 201.4 C 58.4 202.3 49.2 207.7 49.2 207.7 L 51 211.4 Z "
                          />
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 95.8 196.3 L 91.9 193 C 88.3 194.7 87.9 194.5 86 194.2 C 83.3 196.6 70.5 197.5 66.5 197.7 C 61.2 198.1 55.9 199.2 50.9 201.1 C 51.3 201.2 52.3 202.2 53 202 C 64.6 199.2 66.7 200.8 66.6 201.2 C 66.2 202.8 61.8 205 60.6 205.6 C 60.6 205.6 55.4 207.1 53.3 208.6 C 51.9 209.6 52.7 210.8 52.1 211.7 L 57.6 225.1 L 57.6 225.1 C 58 216.6 65.7 209.9 75.2 209.9 C 84.7 209.9 93.5 216.9 93.9 225.3 L 93.9 230.2 C 93.9 230.6 94.3 230.9 94.7 230.9 L 98.9 230.9 C 99 231.2 94.2 219.3 95.8 196.3 Z "
        />
      </g>
      <g
        id="rightfront_fender"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.FRONT_FENDER] &&
          damagedParts[ViGenericPartName.FRONT_FENDER][ViSide.RIGHT]
            ? isFaded(damagedParts[ViGenericPartName.FRONT_FENDER][ViSide.RIGHT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.FRONT_FENDER] &&
            damagedParts[ViGenericPartName.FRONT_FENDER][ViSide.RIGHT]
          )
            handleHover({
              genericPartName: ViGenericPartName.FRONT_FENDER,
              side: ViSide.RIGHT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g>
          <linearGradient
            id="_lgradient_474"
            x1="-0.003474704792315819"
            x2="1.0035881415521963"
            y1="0.500002678712675"
            y2="0.500002678712675"
            gradientTransform="matrix(0,-37.903,48.002,0,50.9,70.4)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(220,227,233)" }} />
            <stop
              offset="50%"
              stopOpacity="1"
              style={{ stopColor: "rgb(210,211,215)" }}
            />
            <stop
              offset="100%"
              stopOpacity="1"
              style={{ stopColor: "rgb(191,192,197)" }}
            />
          </linearGradient>
          <path
            fill="url(#_lgradient_474)"
            d=" M 95.8 67.1 L 91.9 70.4 C 88.3 68.7 87.9 68.9 86 69.2 C 83.3 66.8 70.5 65.9 66.5 65.7 C 61.2 65.3 55.9 64.2 50.9 62.3 C 51.3 62.2 52.3 61.2 53 61.4 C 64.6 64.2 66.7 62.6 66.6 62.2 C 66.2 60.6 61.8 58.4 60.6 57.8 C 60.6 57.8 55.4 56.3 53.3 54.8 C 51.9 53.8 52.7 52.6 52.1 51.7 L 57.6 38.3 L 57.6 38.3 C 58 46.8 65.7 53.5 75.2 53.5 C 84.7 53.5 93.5 46.5 93.9 38.1 L 93.9 33.2 C 93.9 32.8 94.3 32.5 94.7 32.5 L 98.9 32.5 C 99 32.3 94.2 44.1 95.8 67.1 Z "
          />
          <g>
            <g>
              <g>
                <g>
                  <clipPath id="_clipPath_ljnm3gZmJNWAzt0bC0Bzqg9oVOk2XCOc">
                    <path
                      fill="rgb(255,255,255)"
                      d=" M 95.8 67.1 L 91.9 70.4 C 88.3 68.7 87.9 68.9 86 69.2 C 83.3 66.8 70.5 65.9 66.5 65.7 C 61.2 65.3 55.9 64.2 50.9 62.3 C 51.3 62.2 52.3 61.2 53 61.4 C 64.6 64.2 66.7 62.6 66.6 62.2 C 66.2 60.6 61.8 58.4 60.6 57.8 C 60.6 57.8 55.4 56.3 53.3 54.8 C 51.9 53.8 52.7 52.6 52.1 51.7 L 57.6 38.3 L 57.6 38.3 C 58 46.8 65.7 53.5 75.2 53.5 C 84.7 53.5 93.5 46.5 93.9 38.1 L 93.9 33.2 C 93.9 32.8 94.3 32.5 94.7 32.5 L 98.9 32.5 C 99 32.3 94.3 43.8 95.8 67.1 Z "
                    />
                  </clipPath>
                  <g clipPath="url(#_clipPath_ljnm3gZmJNWAzt0bC0Bzqg9oVOk2XCOc)">
                    <g>
                      <g>
                        <g opacity="0.5">
                          <linearGradient
                            id="_lgradient_475"
                            x1="0.2884050632911226"
                            x2="1.5853034879712147"
                            y1="0.49720700152206987"
                            y2="0.5667414872351709"
                            gradientTransform="matrix(0,-7.9,65.7,0,38.8,39.3)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="0"
                              style={{ stopColor: "rgb(98,98,100)" }}
                            />
                            <stop
                              offset="24%"
                              stopOpacity="0.5"
                              style={{ stopColor: "rgb(86,86,89)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(86,86,89)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_475)"
                            d=" M 38.8 39.3 C 42.8 34.1 99.6 37 99.6 37 L 104.5 31.8 L 49.7 31.4 L 38.8 35.4 L 38.8 39.3 Z "
                          />
                        </g>
                        <g opacity="0.8">
                          <linearGradient
                            id="_lgradient_476"
                            x1="0.5453821138211268"
                            x2="0.04302642697149528"
                            y1="0.499932495546088"
                            y2="0.49099887364529027"
                            gradientTransform="matrix(0,-12.3,53.359,0,49.2,64.4)"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop
                              offset="0%"
                              stopOpacity="0"
                              style={{ stopColor: "rgb(218,218,220)" }}
                            />
                            <stop
                              offset="100%"
                              stopOpacity="1"
                              style={{ stopColor: "rgb(255,255,255)" }}
                            />
                          </linearGradient>
                          <path
                            fill="url(#_lgradient_476)"
                            d=" M 51 52.1 C 51 52.1 58.8 58.8 86.8 59 C 109.5 60 101 58.8 101 58.8 L 101 64.4 C 101 64.4 70.2 63 64.3 62.1 C 58.4 61.2 49.2 55.8 49.2 55.8 L 51 52.1 Z "
                          />
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 95.8 67.1 L 91.9 70.4 C 88.3 68.7 87.9 68.9 86 69.2 C 83.3 66.8 70.5 65.9 66.5 65.7 C 61.2 65.3 55.9 64.2 50.9 62.3 C 51.3 62.2 52.3 61.2 53 61.4 C 64.6 64.2 66.7 62.6 66.6 62.2 C 66.2 60.6 61.8 58.4 60.6 57.8 C 60.6 57.8 55.4 56.3 53.3 54.8 C 51.9 53.8 52.7 52.6 52.1 51.7 L 57.6 38.3 L 57.6 38.3 C 58 46.8 65.7 53.5 75.2 53.5 C 84.7 53.5 93.5 46.5 93.9 38.1 L 93.9 33.2 C 93.9 32.8 94.3 32.5 94.7 32.5 L 98.9 32.5 C 99 32.3 94.2 44.1 95.8 67.1 Z "
        />
      </g>
      <g
        id="lefttaillight"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.TAILLIGHT] &&
          damagedParts[ViGenericPartName.TAILLIGHT][ViSide.LEFT]
            ? isFaded(damagedParts[ViGenericPartName.TAILLIGHT][ViSide.LEFT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.TAILLIGHT] &&
            damagedParts[ViGenericPartName.TAILLIGHT][ViSide.LEFT]
          )
            handleHover({
              genericPartName: ViGenericPartName.TAILLIGHT,
              side: ViSide.LEFT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_V4JaQobCOt2iOdKoYTZP5zpCyeeVvjmf">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 293.4 177.5 C 293.4 177.5 303 179.7 308.9 179.7 C 309 182 308.9 185.3 308.9 185.3 C 308.9 185.3 297.8 184.1 293.4 182.1 L 293.4 177.5 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_V4JaQobCOt2iOdKoYTZP5zpCyeeVvjmf)">
                  <g>
                    <linearGradient
                      id="_lgradient_480"
                      x1="-0.007409416913292333"
                      x2="0.9939943100141191"
                      y1="0.5"
                      y2="0.5"
                      gradientTransform="matrix(0,-12.363,23.326,0,290.687,187.9)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="0.8"
                        style={{ stopColor: "rgb(255,143,156)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(198,119,123)" }}
                      />
                    </linearGradient>
                    <path
                      fill="url(#_lgradient_480)"
                      d=" M 294.1 176.2 C 292.8 175.7 291.5 175.4 291.2 175.6 C 290.5 175.9 290.5 182.6 291.3 185.9 C 291.7 187.4 292.9 187.1 294.3 186.4 L 294.3 186.5 C 295.1 189.9 301.1 184 301.1 184 L 301.1 178.9 C 301 178.7 300.9 178.6 300.7 178.5 C 299.2 177.3 294.9 175.8 294.1 176.2 L 294.1 176.2 Z  M 313.5 187.9 C 314.2 187.6 314.2 180.9 313.4 177.6 C 313 176.1 311.8 176.4 310.4 177.1 L 310.4 177 C 309.6 173.6 303.6 179 303.6 179 L 303.6 184.6 C 303.7 184.8 303.8 184.9 304 185 C 305.4 186 309.7 187.5 310.4 187.2 L 310.5 187.1 C 311.4 187.6 312.5 187.9 313.5 187.9 Z "
                    />
                    <linearGradient
                      id="_lgradient_481"
                      x1="-0.003761904761924839"
                      x2="0.995047619047611"
                      y1="0.4999999999999858"
                      y2="0.4999999999999858"
                      gradientTransform="matrix(8.4,0,0,2.8,298,180.1)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(220,227,233)" }}
                      />
                      <stop
                        offset="50%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(210,211,215)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(191,192,197)" }}
                      />
                    </linearGradient>
                    <rect
                      width="8.4"
                      height="2.8"
                      x="298"
                      y="180.1"
                      fill="url(#_lgradient_481)"
                      transform="matrix(0,-1,1,0,120.7,483.7)"
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g>
          <g>
            <g>
              <clipPath id="_clipPath_71UOlHjAEKkhF5gsVuCN5zSrXz7BkJnQ">
                <path
                  fill="rgb(255,255,255)"
                  d=" M 293.4 177.5 C 293.4 177.5 303 179.7 308.9 179.7 C 309 182 308.9 185.3 308.9 185.3 C 308.9 185.3 297.8 184.1 293.4 182.1 L 293.4 177.5 Z "
                />
              </clipPath>
              <g clipPath="url(#_clipPath_71UOlHjAEKkhF5gsVuCN5zSrXz7BkJnQ)">
                <g>
                  <g opacity="0.5">
                    <linearGradient
                      id="_lgradient_482"
                      x1="1.0946681521977997"
                      x2="-0.04965578659468939"
                      y1="0.47313165119367895"
                      y2="0.5200672792286528"
                      gradientTransform="matrix(0,-18.2,19.766,0,291.2,195.5)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(255,255,255)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="0"
                        style={{ stopColor: "rgb(219,220,224)" }}
                      />
                    </linearGradient>
                    <path
                      fill="url(#_lgradient_482)"
                      d=" M 291.6 177.3 C 291.6 177.3 295.7 182.6 296.7 182.1 C 297.2 181.6 297.2 180.3 299.2 180.8 C 299.2 180.8 305.7 181.7 306 181.8 C 306.3 181.9 310.4 185.5 310.9 185.5 C 311.4 185.5 308.9 192.7 308.9 192.7 L 297 195.5 L 291.2 191.4 L 291.6 177.3 Z "
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 293.4 177.5 C 293.4 177.5 303 179.7 308.9 179.7 C 309 182 308.9 185.3 308.9 185.3 C 308.9 185.3 297.8 184.1 293.4 182.1 L 293.4 177.5 Z "
        />
      </g>
      <g
        id="righttaillight"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.TAILLIGHT] &&
          damagedParts[ViGenericPartName.TAILLIGHT][ViSide.RIGHT]
            ? isFaded(damagedParts[ViGenericPartName.TAILLIGHT][ViSide.RIGHT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.TAILLIGHT] &&
            damagedParts[ViGenericPartName.TAILLIGHT][ViSide.RIGHT]
          )
            handleHover({
              genericPartName: ViGenericPartName.TAILLIGHT,
              side: ViSide.RIGHT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_DCj8VfDVeutOdeZVwtw9xkMEs4rbQO6b">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 293.4 81.3 C 297.8 79.3 308.9 78.1 308.9 78.1 C 308.9 78.1 309 81.4 308.9 83.7 C 303 83.7 293.4 85.9 293.4 85.9 L 293.4 81.3 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_DCj8VfDVeutOdeZVwtw9xkMEs4rbQO6b)">
                  <g>
                    <linearGradient
                      id="_lgradient_483"
                      x1="0.9966350246332727"
                      x2="-0.0047687022941467205"
                      y1="0.5"
                      y2="0.4999999999999999"
                      gradientTransform="matrix(0,-12.363,23.326,0,290.687,87.863)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="0.8"
                        style={{ stopColor: "rgb(255,143,156)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(198,119,123)" }}
                      />
                    </linearGradient>
                    <path
                      fill="url(#_lgradient_483)"
                      d=" M 294.1 87.2 C 294.9 87.6 299.2 86.1 300.7 84.9 C 300.9 84.8 301 84.7 301.1 84.5 L 301.1 79.4 C 301.1 79.4 295.1 73.5 294.3 76.9 L 294.3 77 C 292.9 76.3 291.7 76 291.3 77.5 C 290.5 80.8 290.5 87.5 291.2 87.8 C 291.5 88 292.8 87.7 294.1 87.2 Z  M 310.5 76.3 L 310.4 76.2 C 309.7 75.9 305.4 77.4 304 78.4 C 303.8 78.5 303.7 78.6 303.6 78.8 L 303.6 84.4 C 303.6 84.4 309.6 89.8 310.4 86.4 L 310.4 86.3 C 311.8 87 313 87.3 313.4 85.8 C 314.2 82.5 314.2 75.8 313.5 75.5 C 312.5 75.6 311.4 75.9 310.5 76.3 Z "
                    />
                    <linearGradient
                      id="_lgradient_484"
                      x1="0.985523809523805"
                      x2="-0.013297619047619946"
                      y1="0.4999999999999858"
                      y2="0.4999999999999857"
                      gradientTransform="matrix(8.4,0,0,2.8,298,80.5)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(220,227,233)" }}
                      />
                      <stop
                        offset="50%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(210,211,215)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(191,192,197)" }}
                      />
                    </linearGradient>
                    <rect
                      width="8.4"
                      height="2.8"
                      x="298"
                      y="80.5"
                      fill="url(#_lgradient_484)"
                      transform="matrix(0,-1,1,0,220.3,384.1)"
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <g>
          <g>
            <g>
              <clipPath id="_clipPath_ZndxDjs0AqkdYl4uzfCHO4yErCLxXDX8">
                <path
                  fill="rgb(255,255,255)"
                  d=" M 293.4 81.3 C 297.8 79.3 308.9 78.1 308.9 78.1 C 308.9 78.1 309 81.4 308.9 83.7 C 303 83.7 293.4 85.9 293.4 85.9 L 293.4 81.3 Z "
                />
              </clipPath>
              <g clipPath="url(#_clipPath_ZndxDjs0AqkdYl4uzfCHO4yErCLxXDX8)">
                <g>
                  <g opacity="0.5">
                    <linearGradient
                      id="_lgradient_485"
                      x1="0.693383626373592"
                      x2="1.836789801943793"
                      y1="0.5032389342308967"
                      y2="0.5556713293812087"
                      gradientTransform="matrix(0,-18.2,19.766,0,291.2,86.1)"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0%"
                        stopOpacity="1"
                        style={{ stopColor: "rgb(255,255,255)" }}
                      />
                      <stop
                        offset="100%"
                        stopOpacity="0"
                        style={{ stopColor: "rgb(219,220,224)" }}
                      />
                    </linearGradient>
                    <path
                      fill="url(#_lgradient_485)"
                      d=" M 291.2 72 L 297 67.9 L 308.9 70.7 C 308.9 70.7 311.4 77.9 310.9 77.9 C 310.4 77.9 306.2 81.5 306 81.6 C 305.8 81.7 299.2 82.6 299.2 82.6 C 297.2 83.1 297.2 81.8 296.7 81.3 C 295.7 80.8 291.6 86.1 291.6 86.1 L 291.2 72 Z "
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 293.4 81.3 C 297.8 79.3 308.9 78.1 308.9 78.1 C 308.9 78.1 309 81.4 308.9 83.7 C 303 83.7 293.4 85.9 293.4 85.9 L 293.4 81.3 Z "
        />
      </g>
      <g
        id="leftheadlight"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.HEADLIGHT] &&
          damagedParts[ViGenericPartName.HEADLIGHT][ViSide.LEFT]
            ? isFaded(damagedParts[ViGenericPartName.HEADLIGHT][ViSide.LEFT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.HEADLIGHT] &&
            damagedParts[ViGenericPartName.HEADLIGHT][ViSide.LEFT]
          )
            handleHover({
              genericPartName: ViGenericPartName.HEADLIGHT,
              side: ViSide.LEFT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g opacity="0.7">
          <linearGradient
            id="_lgradient_489"
            x1="0.996801562347271"
            x2="-0.000753118159072308"
            y1="0.5000001840822401"
            y2="0.50000018408224"
            gradientTransform="matrix(-11.177,-19.36,11.994,-6.925,55.29,196.84)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(10,10,10)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(64,64,64)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_489)"
            d=" M 58.76 179.07 C 64.412 183.659 65.742 187.163 66.696 189.615 C 65.956 190.734 60.927 195.023 55.073 192.284 C 51.618 190.699 46.771 183.105 46.124 179.783 C 45.667 177.391 45.923 176.435 46.443 176.135 C 46.962 175.835 53.395 174.777 58.76 179.07 Z "
          />
        </g>
        <g>
          <path
            fill="rgb(255,255,255)"
            d=" M 56.05 183.175 C 56.05 183.175 55.513 182.445 55.08 182.695 C 54.647 182.945 54.054 183.519 54.154 183.692 C 54.391 183.902 54.577 184.025 54.901 184.185 C 54.901 184.185 55.457 183.748 56.05 183.175 Z "
          />
          <g>
            <path
              fill="rgb(226,221,228)"
              d=" M 56.55 184.041 C 55.251 184.791 54.861 186.517 55.648 187.679 C 56.398 188.978 58.124 189.367 59.286 188.581 C 60.585 187.831 60.938 186.242 60.188 184.943 C 59.488 183.73 57.849 183.291 56.55 184.041 L 56.55 184.041 Z  M 56.75 184.387 C 57.875 183.737 59.378 184.14 60.028 185.266 C 60.678 186.392 60.275 187.894 59.15 188.544 C 58.024 189.194 56.521 188.792 55.871 187.666 C 55.221 186.54 55.624 185.037 56.75 184.387 L 56.75 184.387 Z "
            />
          </g>
          <g style={{ opacity: "0.5" }}>
            <g opacity="0.5">
              <path
                fill="rgb(255,255,255)"
                d=" M 56.05 183.175 C 54.318 184.175 53.695 186.497 54.695 188.229 C 55.695 189.961 58.018 190.584 59.75 189.584 C 61.482 188.584 62.104 186.261 61.104 184.529 L 61.104 184.529 C 60.104 182.797 57.782 182.175 56.05 183.175 Z  M 56.55 184.041 C 57.849 183.291 59.488 183.73 60.238 185.029 C 60.988 186.328 60.549 187.967 59.25 188.717 C 57.951 189.467 56.311 189.028 55.561 187.729 C 54.811 186.43 55.251 184.791 56.55 184.041 L 56.55 184.041 Z "
              />
            </g>
          </g>
          <g style={{ opacity: "0.5" }}>
            <g opacity="0.5">
              <path
                fill="rgb(255,255,255)"
                d=" M 47.836 179.949 C 47.85 180.173 47.95 180.346 48.05 180.519 C 48.45 181.212 48.886 181.768 49.41 182.274 L 50.433 183.646 L 50.583 183.906 L 50.583 183.906 C 51.406 184.932 52.006 185.971 52.743 187.047 L 53.193 187.827 L 53.593 188.519 C 54.706 190.648 56.685 192.276 58.971 193.035 C 59.194 193.022 59.554 193.045 60.001 193.018 C 57.965 192.692 56.199 191.634 54.889 189.965 C 56.782 191.643 59.624 191.966 61.875 190.666 C 62.741 190.166 63.544 189.356 64.073 188.473 L 63.577 188.413 C 63.084 189.159 62.455 189.869 61.675 190.319 C 58.991 191.869 55.576 190.954 54.026 188.269 C 52.776 186.104 53.261 183.745 54.753 181.729 L 54.603 181.47 C 54.48 181.656 54.27 181.893 54.147 182.079 L 54.147 182.079 L 51.266 184.089 L 50.979 183.793 L 54.503 181.296 L 54.353 181.037 L 50.829 183.533 L 50.493 183.15 L 54.017 180.654 L 53.817 180.307 L 50.293 182.804 L 50.006 182.507 L 53.617 179.961 L 53.467 179.701 L 49.769 182.297 L 49.669 182.124 L 49.569 181.951 L 53.267 179.355 L 53.117 179.095 L 49.419 181.691 L 49.133 181.395 L 52.917 178.748 L 52.767 178.489 L 48.983 181.135 L 48.783 180.789 L 52.603 178.006 L 52.403 177.659 L 48.41 180.542 C 48.31 180.369 48.16 180.109 48.146 179.886 C 47.943 178.733 50.957 175.954 50.957 175.954 L 50.547 175.844 C 50.51 175.981 47.533 178.624 47.836 179.949 Z  M 53.152 187.157 C 52.702 186.378 52.302 185.685 51.816 185.042 L 53.101 184.069 C 52.895 185.112 52.962 186.228 53.152 187.157 L 53.152 187.157 Z  M 53.298 183.609 L 53.298 183.609 L 51.666 184.782 L 51.416 184.349 L 53.691 182.689 C 53.581 183.099 53.508 183.372 53.298 183.609 L 53.298 183.609 Z "
              />
            </g>
          </g>
        </g>
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_toGKxj5bniW8RylTxdgcIFtrxC08aORQ">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 58.673 179.12 C 64.325 183.709 65.656 187.213 66.609 189.665 C 65.87 190.784 60.841 195.073 54.986 192.334 C 51.531 190.749 46.685 183.155 46.037 179.833 C 45.58 177.441 45.836 176.485 46.356 176.185 C 46.876 175.885 53.395 174.777 58.673 179.12 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_toGKxj5bniW8RylTxdgcIFtrxC08aORQ)">
                  <g>
                    <g opacity="0.7">
                      <linearGradient
                        id="_lgradient_490"
                        x1="0.0010406909300821887"
                        x2="0.9985953714364171"
                        y1="0.5000001840822428"
                        y2="0.5000001840822428"
                        gradientTransform="matrix(-11.177,-19.36,11.994,-6.925,58.244,194.557)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(255,255,255)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(219,220,224)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_490)"
                        d=" M 61.714 176.787 C 67.366 181.376 68.696 184.88 69.65 187.332 C 68.911 188.451 63.882 192.74 58.027 190 C 54.572 188.416 49.726 180.822 49.078 177.5 C 48.621 175.108 48.877 174.152 49.397 173.852 C 49.917 173.552 56.349 172.494 61.714 176.787 Z "
                      />
                    </g>
                    <g opacity="0.7">
                      <linearGradient
                        id="_lgradient_491"
                        x1="-0.001862506323947688"
                        x2="0.9956921741823868"
                        y1="0.5000001840822428"
                        y2="0.5000001840822428"
                        gradientTransform="matrix(-11.177,-19.36,11.994,-6.925,60.615,192.265)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(255,255,255)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(219,220,224)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_491)"
                        d=" M 64.086 174.494 C 69.737 179.083 71.068 182.587 72.021 185.039 C 71.282 186.158 66.253 190.448 60.399 187.708 C 56.943 186.123 52.097 178.529 51.45 175.207 C 50.992 172.816 51.249 171.859 51.768 171.559 C 52.288 171.259 58.721 170.201 64.086 174.494 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 58.76 179.07 C 64.412 183.659 65.742 187.163 66.696 189.615 C 65.956 190.734 60.927 195.023 55.073 192.284 C 51.618 190.699 46.771 183.105 46.124 179.783 C 45.667 177.391 45.923 176.435 46.443 176.135 C 46.962 175.835 53.395 174.777 58.76 179.07 Z "
        />
      </g>
      <g
        id="rightheadlight"
        className={
          damagedParts &&
          damagedParts[ViGenericPartName.HEADLIGHT] &&
          damagedParts[ViGenericPartName.HEADLIGHT][ViSide.RIGHT]
            ? isFaded(damagedParts[ViGenericPartName.HEADLIGHT][ViSide.RIGHT])
              ? classes.fadedDamagedPart
              : classes.damagedPart
            : classes.part
        }
        onMouseEnter={() => {
          if (
            damagedParts &&
            damagedParts[ViGenericPartName.HEADLIGHT] &&
            damagedParts[ViGenericPartName.HEADLIGHT][ViSide.RIGHT]
          )
            handleHover({
              genericPartName: ViGenericPartName.HEADLIGHT,
              side: ViSide.RIGHT,
            });
        }}
        onMouseLeave={() => handleHover({})}
      >
        <g opacity="0.7">
          <linearGradient
            id="_lgradient_486"
            x1="0"
            x2="0.9975546805063331"
            y1="0.5000034663418647"
            y2="0.5000034663418647"
            gradientTransform="matrix(11.177,-19.36,11.956,6.903,43.451,84.874)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopOpacity="1" style={{ stopColor: "rgb(10,10,10)" }} />
            <stop offset="100%" stopOpacity="1" style={{ stopColor: "rgb(64,64,64)" }} />
          </linearGradient>
          <path
            fill="url(#_lgradient_486)"
            d=" M 58.06 83.263 C 63.712 78.674 65.042 75.17 65.996 72.718 C 65.256 71.599 60.227 67.31 54.373 70.049 C 50.918 71.634 46.158 79.278 45.424 82.55 C 44.967 84.942 45.223 85.898 45.743 86.198 C 46.262 86.498 52.695 87.556 58.06 83.263 Z "
          />
        </g>
        <g>
          <path
            fill="rgb(255,255,255)"
            d=" M 55.4 79.071 C 55.4 79.071 54.863 79.801 54.43 79.551 C 53.997 79.301 53.404 78.728 53.504 78.554 C 53.741 78.345 53.977 78.135 54.251 78.062 C 54.634 78.398 55.017 78.735 55.4 79.071 Z "
          />
          <path
            fill="rgb(226,221,228)"
            d=" M 59.588 77.217 C 60.338 75.918 59.899 74.279 58.6 73.529 C 57.301 72.779 55.661 73.218 54.911 74.517 C 54.161 75.816 54.601 77.455 55.9 78.205 L 55.9 78.205 C 57.199 78.955 58.838 78.516 59.588 77.217 L 59.588 77.217 Z  M 55.171 74.667 C 55.821 73.541 57.324 73.139 58.45 73.789 C 59.575 74.439 59.978 75.941 59.328 77.067 C 58.678 78.193 57.175 78.596 56.05 77.946 L 56.05 77.946 C 54.924 77.296 54.521 75.793 55.171 74.667 Z "
          />
          <g opacity="0.5">
            <path
              fill="rgb(255,255,255)"
              d=" M 60.454 77.717 C 61.454 75.985 60.832 73.663 59.1 72.663 C 57.368 71.663 55.045 72.285 54.045 74.017 C 53.045 75.749 53.668 78.071 55.4 79.071 L 55.4 79.071 C 57.132 80.071 59.404 79.536 60.454 77.717 L 60.454 77.717 Z  M 54.911 74.517 C 55.661 73.218 57.301 72.779 58.6 73.529 C 59.899 74.279 60.338 75.918 59.588 77.217 C 58.838 78.516 57.199 78.955 55.9 78.205 L 55.9 78.205 C 54.601 77.455 54.161 75.816 54.911 74.517 L 54.911 74.517 Z "
            />
          </g>
          <g opacity="0.5">
            <path
              fill="rgb(255,255,255)"
              d=" M 49.897 86.402 L 50.257 86.379 C 50.257 86.379 47.156 83.55 47.446 82.447 C 47.46 82.224 47.61 81.964 47.71 81.791 L 51.617 84.624 L 51.767 84.364 L 47.946 81.581 L 48.146 81.235 L 51.93 83.881 L 52.08 83.621 L 48.296 80.975 C 48.346 80.888 48.446 80.715 48.583 80.678 L 52.28 83.275 L 52.43 83.015 L 48.733 80.419 L 48.933 80.072 L 52.543 82.619 L 52.693 82.359 L 49.083 79.812 C 49.133 79.726 49.233 79.553 49.369 79.516 L 52.893 82.012 L 53.043 81.753 L 49.519 79.256 C 49.619 79.083 49.669 78.996 49.856 78.873 L 53.38 81.37 L 53.53 81.11 L 50.093 78.663 C 50.143 78.577 50.243 78.403 50.379 78.367 L 53.26 80.377 L 53.26 80.377 C 53.47 80.613 53.507 80.75 53.717 80.987 L 53.867 80.727 C 52.425 78.624 51.889 76.352 53.139 74.187 C 54.689 71.502 58.191 70.637 60.875 72.187 C 61.655 72.637 62.248 73.21 62.74 73.957 L 63.237 73.897 C 62.707 73.014 61.991 72.254 61.039 71.704 C 58.787 70.404 55.995 70.64 54.052 72.405 C 55.312 70.823 57.078 69.764 59.114 69.438 C 58.668 69.411 58.358 69.348 58.134 69.334 C 55.762 70.043 53.869 71.722 52.756 73.85 L 51.906 75.322 C 51.256 76.448 50.569 77.437 49.746 78.463 L 49.596 78.723 C 49.21 79.193 48.873 79.576 48.573 80.095 C 48.036 80.825 47.463 81.418 47.213 81.851 C 47.063 82.11 46.963 82.284 47 82.42 C 46.883 83.623 49.81 86.352 49.897 86.402 Z  M 52.501 78.091 L 51.166 77.204 C 51.652 76.562 52.102 75.782 52.502 75.089 C 52.262 76.105 52.245 77.135 52.501 78.091 Z  M 53.004 79.42 L 50.729 77.761 L 50.929 77.414 L 52.561 78.587 L 52.561 78.587 C 52.808 78.961 52.881 79.234 53.004 79.42 Z "
            />
          </g>
        </g>
        <g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_DI2xHfraqJdQWXe2w43OPwgUsrQ0nh5F">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 57.973 83.213 C 63.625 78.624 64.956 75.12 65.909 72.668 C 65.17 71.549 60.141 67.26 54.286 69.999 C 50.831 71.584 46.071 79.228 45.337 82.5 C 44.88 84.892 45.136 85.848 45.656 86.148 C 49.993 87.035 54.538 85.964 57.973 83.213 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_DI2xHfraqJdQWXe2w43OPwgUsrQ0nh5F)">
                  <g>
                    <g opacity="0.7">
                      <linearGradient
                        id="_lgradient_487"
                        x1="0.9970491931569967"
                        x2="-0.00005815341188863954"
                        y1="0.5000034663418678"
                        y2="0.5000034663418677"
                        gradientTransform="matrix(11.177,-19.36,11.956,6.903,46.405,87.157)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(255,255,255)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(219,220,224)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_487)"
                        d=" M 61.014 85.546 C 66.666 80.957 67.996 77.453 68.95 75.001 C 68.211 73.882 63.182 69.593 57.327 72.332 C 53.872 73.917 49.112 81.561 48.378 84.833 C 47.921 87.225 48.177 88.181 48.697 88.481 C 49.217 88.781 55.649 89.839 61.014 85.546 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
          <g>
            <g>
              <g>
                <clipPath id="_clipPath_15m1Vgbon6xnZiW5TbNlqlJHoyGE4t4K">
                  <path
                    fill="rgb(255,255,255)"
                    d=" M 57.973 83.213 C 63.625 78.624 64.956 75.12 65.909 72.668 C 65.17 71.549 60.141 67.26 54.286 69.999 C 50.831 71.584 46.071 79.228 45.337 82.5 C 44.88 84.892 45.136 85.848 45.656 86.148 C 49.993 87.035 54.538 85.964 57.973 83.213 Z "
                  />
                </clipPath>
                <g clipPath="url(#_clipPath_15m1Vgbon6xnZiW5TbNlqlJHoyGE4t4K)">
                  <g>
                    <g opacity="0.7">
                      <linearGradient
                        id="_lgradient_488"
                        x1="0.9981272679462609"
                        x2="0.0005591674218106135"
                        y1="0.5000034663418664"
                        y2="0.5000034663418663"
                        gradientTransform="matrix(11.177,-19.36,11.956,6.903,48.776,89.45)"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop
                          offset="0%"
                          stopOpacity="1"
                          style={{ stopColor: "rgb(255,255,255)" }}
                        />
                        <stop
                          offset="100%"
                          stopOpacity="0"
                          style={{ stopColor: "rgb(219,220,224)" }}
                        />
                      </linearGradient>
                      <path
                        fill="url(#_lgradient_488)"
                        d=" M 63.386 87.839 C 69.037 83.25 70.368 79.746 71.321 77.294 C 70.582 76.175 65.553 71.885 59.699 74.625 C 56.243 76.21 51.484 83.854 50.75 87.125 C 50.292 89.517 50.549 90.474 51.068 90.774 C 51.588 91.074 58.071 92.045 63.386 87.839 Z "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <path
          className={"highlight"}
          d=" M 58.06 83.263 C 63.712 78.674 65.042 75.17 65.996 72.718 C 65.256 71.599 60.227 67.31 54.373 70.049 C 50.918 71.634 46.158 79.278 45.424 82.55 C 44.967 84.942 45.223 85.898 45.743 86.198 C 46.262 86.498 52.695 87.556 58.06 83.263 Z "
        />
      </g>
    </svg>
  );
}

export default React.memo(CarMap);
